import {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { AuthContext } from "../context/AuthContext";
import { Plus, Trash2, Download, Copy, Check, Edit, RotateCcw, Barcode, Zap, RefreshCw, Eye, EyeOff } from "lucide-react";
import { getIngredients } from "../api/ingredients";
import { getBomEstimatedCost } from "../api/bomApi";
import toast, { Toaster } from "react-hot-toast";
import { formatIngredients } from "../api/ingredientApi";
import {
  createRecipeSku,
  saveRecipeToDatabase,
  syncRecipeToErpag,
  generateUniqueUpc,
  getCostingDraft,
  saveCostingDraft,
  deleteCostingDraft,
  getCostingBatch,
  upsertCostingBatchItem,
  clearCostingBatch,
  getSkuRawSnapshot,
} from "../api/sku";
import { useSearchParams } from "react-router-dom";
import {
  getAllPackagingVariants,
  getCostingSettings,
  getChannelRules,
} from "../api/costingSettingsApi";
import { getRMRows } from "../api/Rmdatabase";
import FormatBuilder from "./FormatBuilder";
import ItemCategorySection from "../sections/ItemCategorySection";
import SellingPriceSection from "../sections/SellingPriceSection";
import AdditionalInfoSection from "../sections/AdditionalInfoSection";
import FulfillmentSection from "../sections/FulfillmentSection";
import CustomFieldsSection from "../sections/CustomFieldsSection";
import BarcodeSecondUomSection from "../sections/BarcodeSecondUomSection";
import BaseProductNameSection from "../sections/BaseProductNameSection";
import {
  exportRecipeToExcel,
  exportRecipeBatchToExcel,
  buildFrDescription,
} from "../utils/exportToExcel";
import { getMandatoryFields } from "../api/ValidationApi";
import { getSuppliers, createSupplier } from "../api/suppliers";
import SaveBeforeVariantModal from "../modals/SaveBeforeVariantModal";
import { useNavigate } from "react-router-dom";
import { generateId } from "../utils/generateId";
import { ITEM_CATEGORIES } from "../constants/itemCategories";
import { buildInvalidSkuMessage, parseSkuInput } from "../utils/skuValidation";
import PrivateLabel from "./PrivateLabel";
import RecipeHistoryTab from "./RecipeHistoryTab";

const PACKAGING_VARIANTS_STALE_MS = 5 * 60 * 1000;
const PACKAGING_VARIANTS_TIMEOUT_MS = 45000;
const PACKAGING_VARIANTS_MAX_RETRIES = 1;

const logPackagingCostComparison = (label, variant) => {
  if (!variant?.items?.length) return;

  console.groupCollapsed(`[CostingTool] ${label}: ${variant.name || ""}`);
  console.table(
    variant.items.map((item) => ({
      sku: item.sku,
      name: item.name,
      qty: Number(item.qty || 0),
      erpagCost: item.erpagUnitCost ?? null,
      sheetCost: item.sheetUnitCost ?? null,
      selectedCost: Number(item.unitCost || 0),
      source: item.unitCostSource || "unknown",
    })),
  );
  console.groupEnd();
};

export default function CostingTool() {
  const ENABLE_SERVER_DRAFTS = true;
  const [activeTab, setActiveTab] = useState("recipe");
  const [rmDatabaseRows, setRmDatabaseRows] = useState([]);
  const [focusedIngredientId, setFocusedIngredientId] = useState(null);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [globalUnit, setGlobalUnit] = useState("kg");
  const [categoryRules, setCategoryRules] = useState([]);
  const [royaltyRules, setRoyaltyRules] = useState([]);
  const [bulkPricingRules, setBulkPricingRules] = useState([]);
  const [fxRates, setFxRates] = useState([]);
  const [channelRules, setChannelRules] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("CAD");
  const [exportCurrencies, setExportCurrencies] = useState(["CAD"]);
  const [globalAssumptions, setGlobalAssumptions] = useState({
    lossBufferPercent: 2,
    earlyPaymentDeductionPercent: 2,
    cogsIncreaseBufferPercent: 5,
    confidentialCostInflationPercent: 0,
    hideDetailedCostsByDefault: false,
    workingDaysPerWeek: 5,
    weeklyOverheadCostCad: 30200,
    weeklyLabourCostCad: 38000,
  });
  const [throughputOverhead, setThroughputOverhead] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState({});
  const [hasGeneratedButUnsavedBase, setHasGeneratedButUnsavedBase] =
    useState(false);
  const [showSaveBeforeVariantModal, setShowSaveBeforeVariantModal] =
    useState(false);
  const [barcodeSecondUomRows, setBarcodeSecondUomRows] = useState([]);
  const [showUnsavedPromptModal, setShowUnsavedPromptModal] = useState(false);
  const [showDraftResumeModal, setShowDraftResumeModal] = useState(false);
  const [pendingDraftSnapshot, setPendingDraftSnapshot] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSavingFromUnsavedPrompt, setIsSavingFromUnsavedPrompt] =
    useState(false);
  const [isApplyingDraftChoice, setIsApplyingDraftChoice] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingNavigationRef = useRef(null);
  const pendingInternalActionRef = useRef(null);
  const baselineRef = useRef(null);
  const formatDebounceTimerRef = useRef(null);
  const lastFormattedPayloadSignatureRef = useRef(null);
  const latestFormatRequestIdRef = useRef(0);
  const latestIngredientsRef = useRef([]);
  const [formattedIngredients, setFormattedIngredients] = useState([]);
  const [ingredientNameDrafts, setIngredientNameDrafts] = useState({});
  const [ingredientRatioDrafts, setIngredientRatioDrafts] = useState({});
  const [copiedRecipeColumn, setCopiedRecipeColumn] = useState(null);
  const [savedExportBatch, setSavedExportBatch] = useState([]);
  const [isSyncingErpag, setIsSyncingErpag] = useState(false);
  const ingredientNameTimersRef = useRef({});
  const bomCostRequestIdByIngredientRef = useRef({});
  const hasHydratedDraftRef = useRef(false);
  const hasHydratedBatchRef = useRef(false);
  const getSupplierName = (supplier) =>
    String(supplier?.supplier?.supplier || "").trim();
  const normalizeBaseDescription = (value) =>
    String(value || "")
      .replace(/\(NEW\)/gi, "")
      .replace(/\s+/g, " ")
      .trim();

  const descriptionForSingle = (productData) => {
    const baseName = String(productData.nameEn || "")
      .trim()
      .toUpperCase();
    const isIngredientType =
      String(productData.type || "")
        .trim()
        .toLowerCase() === "ingredient";
    const trademark = String(productData.trademark || "")
      .trim()
      .toUpperCase();
    const hasTrademark = trademark && baseName.includes(trademark);
    const includeTrademark = !isIngredientType && trademark && !hasTrademark;
    const desc = normalizeBaseDescription(
      [baseName, includeTrademark ? trademark : ""].filter(Boolean).join(" "),
    ).toUpperCase();
    if (!desc) return "BASE PRODUCT VRAC";
    return desc.includes("VRAC") ? desc : `${desc} VRAC`;
  };

  const descriptionForSingleFr = (productData) => {
    const baseName = String(productData.nameFr || productData.nameEn || "")
      .trim()
      .toUpperCase();
    const isIngredientType =
      String(productData.type || "")
        .trim()
        .toLowerCase() === "ingredient";
    const trademark = String(productData.trademark || "")
      .trim()
      .toUpperCase();
    const hasTrademark = trademark && baseName.includes(trademark);
    const includeTrademark = !isIngredientType && trademark && !hasTrademark;
    const desc = normalizeBaseDescription(
      [baseName, includeTrademark ? trademark : ""].filter(Boolean).join(" "),
    ).toUpperCase();
    if (!desc) return "PRODUIT DE BASE VRAC";
    return desc.includes("VRAC") ? desc : `${desc} VRAC`;
  };

  const descriptionForMaster = (productData, qty = 25) => {
    const baseName = String(productData.nameEn || "")
      .trim()
      .toUpperCase();
    const isIngredientType =
      String(productData.type || "")
        .trim()
        .toLowerCase() === "ingredient";
    const trademark = String(productData.trademark || "")
      .trim()
      .toUpperCase();
    const hasTrademark = trademark && baseName.includes(trademark);
    const includeTrademark = !isIngredientType && trademark && !hasTrademark;
    const desc = normalizeBaseDescription(
      [baseName, includeTrademark ? trademark : ""].filter(Boolean).join(" "),
    )
      .replace(/\bNUTRIVILLA\b/gi, "")
      .replace(/\bVRAC\b/gi, "")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();

    return `${desc || "BASE PRODUCT"} 1 MASTER CS x ${qty} kg`;
  };

  const refreshLockedBarcodeRows = (rows, productData) => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;

    return rows.map((row) => {
      const secondUom = String(row?.secondUom || "")
        .trim()
        .toUpperCase();
      const qty = Number(row?.quantityDefault) || 25;
      const nextDescription =
        secondUom === "CS"
          ? descriptionForMaster(productData, qty)
          : descriptionForSingle(productData);

      return {
        ...row,
        description: nextDescription,
      };
    });
  };

  const createBarcodeRowsForBase = async (productData) => {
    const baseUom = productData.uom || "kg";
    const baseUpc =
      String(productData.upc || "").trim() ||
      (await generateUniqueUpc()).data?.fullUPC ||
      "";
    const isIngredientType =
      String(productData.type || "")
        .trim()
        .toLowerCase() === "ingredient";
    const orderStepQty = Number(productData.orderStepQuantity) || 25;
    const supplierSku = String(productData.supplierSku || "").trim();
    const caseUpc = isIngredientType
      ? supplierSku
      : (await generateUniqueUpc()).data?.fullUPC || "";

    const baseRow = [
      {
        id: generateId(),
        eanUpc: baseUpc,
        secondUom: baseUom,
        quantityDefault: 1,
        description: descriptionForSingle(productData),
        type: "UPCA",
        labelPrinting: "Per quantity",
      },
    ];

    if (isIngredientType) {
      return [
        ...baseRow,
        {
          id: generateId(),
          eanUpc: caseUpc,
          secondUom: "CS",
          quantityDefault: orderStepQty,
          description: descriptionForMaster(productData, orderStepQty),
          type: "GS128",
          labelPrinting: "Single label",
        },
      ];
    }

    return [
      ...baseRow,
      {
        id: generateId(),
        eanUpc: caseUpc,
        secondUom: "CS",
        quantityDefault: 25,
        description: descriptionForMaster(productData, 25),
        type: "UPCA",
        labelPrinting: "Single label",
      },
    ];
  };

  const autoGenerateBarcodeRows = async (productData) => {
    if (productData.isVariant) return;

    const nextRows = await createBarcodeRowsForBase(productData);
    setBarcodeSecondUomRows(nextRows);
  };



  //-------------------Recipe-Sku----------------
  const submitRecipeSku = async () => {
    if (!canWriteCostingTool) {
      toast.error("You do not have permission to generate recipe SKUs.");
      return;
    }
    try {
      const productNameEN = String(product.nameEn || "")
        .trim()
        .toUpperCase();
      const normalizedKeyword = String(product.skuKeyword || "")
        .trim()
        .toUpperCase();
      const lastGeneratedKeyword = String(product.generatedForKeyword || "")
        .trim()
        .toUpperCase();
      const isBaseProduct = !String(product.baseProductSku || "").trim();
      const alreadyGenerated =
        String(product.generatedForNameEn || "")
          .trim()
          .toUpperCase() === productNameEN &&
        lastGeneratedKeyword === normalizedKeyword &&
        String(product.sku || "").trim() !== "" &&
        String(product.upc || "").trim() !== "";

      if (product.isEdit || alreadyGenerated) {
        const lockedProduct = {
          ...product,
          descriptionEn: descriptionForSingle(product),
          descriptionFr: descriptionForSingleFr(product),
        };

        setProduct((prev) => ({
          ...prev,
          descriptionEn: lockedProduct.descriptionEn,
          descriptionFr: lockedProduct.descriptionFr,
        }));
        setBarcodeSecondUomRows((prev) =>
          refreshLockedBarcodeRows(prev, lockedProduct),
        );

        if (product.isEdit) {
          toast.success(
            `Edit Mode: SKU ${product.sku} and Reserved UPC ${product.upc} are preserved. Descriptions refreshed.`,
          );
        } else {
          toast.success(
            "SKU/UPC already generated. Codes are locked, descriptions refreshed.",
          );
        }
        return;
      }

      const productNameFR = String(product.nameFr || "")
        .trim()
        .toUpperCase();
      if (isBaseProduct && product.type !== "ingredient") {
        if (!ingredients || ingredients.length < 2) {
          toast.error("Minimum 2 ingredients are required to build a recipe");
          return;
        }

        const invalidIngredient = ingredients.find(
          (ing) =>
            !ing.name?.trim() ||
            !ing.sku?.trim() ||
            !ing.unit ||
            !ing.weight ||
            Number(ing.weight) <= 0,
        );

        if (invalidIngredient) {
          toast.error("All ingredient fields must be filled correctly");
          return;
        }

        if (!isRatioTotalValid) {
          toast.error("Ingredient ratio must total exactly 100%");
          return;
        }
      }
      if (!productNameEN) {
        toast.error("Product name is required");
        return;
      }

      if (isBaseProduct && product.hsCodeOverride !== false) {
        if (!product.harmonizedTariff || !product.harmonizedTariff.trim()) {
          toast.error("Harmonized Tariff (HS Code) is required");
          return;
        }
      }

      // FDA Code
      if (mandatoryFields?.fdaCode) {
        if (!product.fdaCode || !product.fdaCode.trim()) {
          toast.error("FDA Code is required");
          return;
        }
      }

      // Qty/SKU
      if (mandatoryFields?.qtySku) {
        if (!product.qtySku || Number(product.qtySku) <= 0) {
          toast.error("Qty/SKU is required");
          return;
        }
      }

      // Net Weight (g/kg)
      if (mandatoryFields?.netWeightGkKg) {
        if (
          !product.netWeightGkKg ||
          Number(product.netWeightGkKg) <= 0 ||
          !product.gkKgUnit
        ) {
          toast.error("Net Weight (g/kg) is required");
          return;
        }
      }

      const isIngredientType = product.type === "ingredient";
      if (isIngredientType) {
        if (!product.supplier || !product.supplier.trim()) {
          toast.error("Supplier is required");
          return;
        }

        if (!product.supplierSku || !product.supplierSku.trim()) {
          toast.error("Supplier SKU is required");
          return;
        }

        if (!product.purchasePrice || Number(product.purchasePrice) <= 0) {
          toast.error("Purchase price is required");
          return;
        }

        if (product.landingCost !== "" && Number(product.landingCost) < 0) {
          toast.error("Landing cost cannot be negative");
          return;
        }

        if (
          !product.orderStepQuantity ||
          Number(product.orderStepQuantity) <= 0
        ) {
          toast.error("Order step quantity is required");
          return;
        }
      }
      const shouldRequireIngredientFields =
        !isIngredientType || product.containsIngredients === "yes";
      const shouldRequireAllergenFields = isIngredientType
        ? product.containsAllergen === "yes"
        : product.containsAllergen === "yes" || product.allergensOverride;

      // Ingredients EN
      if (mandatoryFields?.ingredientsEn) {
        if (
          shouldRequireIngredientFields &&
          (!product.ingredientsEn || !product.ingredientsEn.trim())
        ) {
          toast.error("Ingredients (EN) is required");
          return;
        }
      }

      // Ingredients FR
      if (mandatoryFields?.ingredientsFr) {
        if (
          shouldRequireIngredientFields &&
          (!product.ingredientsFr || !product.ingredientsFr.trim())
        ) {
          toast.error("Ingredients (FR) is required");
          return;
        }
      }

      // Allergens EN
      if (mandatoryFields?.allergensEn) {
        if (
          shouldRequireAllergenFields &&
          (!product.allergensEn ||
            !product.allergensEn.trim() ||
            (product.type === "ingredient" &&
              (!Array.isArray(product.selectedAllergens) ||
                product.selectedAllergens.length === 0)))
        ) {
          toast.error("Allergens (EN) is required");
          return;
        }
      }

      // Allergens FR
      if (mandatoryFields?.allergensFr) {
        if (
          shouldRequireAllergenFields &&
          (!product.allergensFr ||
            !product.allergensFr.trim() ||
            (product.type === "ingredient" &&
              (!Array.isArray(product.selectedAllergens) ||
                product.selectedAllergens.length === 0)))
        ) {
          toast.error("Allergens (FR) is required");
          return;
        }
      }

      // MSRP CAD
      if (mandatoryFields?.msrpCad) {
        if (!product.msrpCad || Number(product.msrpCad) <= 0) {
          toast.error("MSRP CAD is required");
          return;
        }
      }

      // MSRP USD
      if (mandatoryFields?.msrpUsd) {
        if (!product.msrpUsd || Number(product.msrpUsd) <= 0) {
          toast.error("MSRP USD is required");
          return;
        }
      }

      const hasPackagingFieldMandatory =
        mandatoryFields?.["L(mm)"] ||
        mandatoryFields?.["W(mm)"] ||
        mandatoryFields?.["H(mm)"] ||
        mandatoryFields?.["L('')"] ||
        mandatoryFields?.["W('')"] ||
        mandatoryFields?.["H('')"];

      if (!isIngredientType && hasPackagingFieldMandatory && !addedPackaging) {
        toast.error("Please select the packaging");
        return;
      }

      if (!isIngredientType) {
        if (mandatoryFields?.["L(mm)"]) {
          if (!product.lengthMm || Number(product.lengthMm) <= 0) {
            toast.error("Length (mm) is required");
            return;
          }
        }

        // Width (mm)
        if (mandatoryFields?.["W(mm)"]) {
          if (!product.widthMm || Number(product.widthMm) <= 0) {
            toast.error("Width (mm) is required");
            return;
          }
        }

        // Height (mm)
        if (mandatoryFields?.["H(mm)"]) {
          if (!product.heightMm || Number(product.heightMm) <= 0) {
            toast.error("Height (mm) is required");
            return;
          }
        }

        // Length (inch)
        if (mandatoryFields?.[" L('')"]) {
          if (!product.lengthInch || Number(product.lengthInch) <= 0) {
            toast.error("Length (inch) is required");
            return;
          }
        }

        // Width (inch)
        if (mandatoryFields?.[" W('')"]) {
          if (!product.widthInch || Number(product.widthInch) <= 0) {
            toast.error("Width (inch) is required");
            return;
          }
        }

        // Height (inch)
        if (mandatoryFields?.[" H('')"]) {
          if (!product.heightInch || Number(product.heightInch) <= 0) {
            toast.error("Height (inch) is required");
            return;
          }
        }
      }

      const payload = {
        productNameEN,
        productNameFR,
        type: String(product.type || "Regular"),
        isBaseProduct,
        trademark: String(product.trademark || "NutriVilla").trim(),
        customerName: String(product.customerName || "").trim(),
        customPrefix: String(product.customPrefix || "").trim(),
        skuGenerated: Boolean(product.skuGenerated),
        sku: String(product.sku || "").trim(),
        keyword: normalizedKeyword,

        ingredients: isBaseProduct ? ingredients : [],

        bulkFormats: (bulkFormats || []).map((format) => ({
          ...format,
          netWeightKg:
            format.netWeightKg !== null && format.netWeightKg !== ""
              ? Number(format.netWeightKg)
              : 0,
        })),

        baseSku: isBaseProduct
          ? null
          : String(product.baseProductSku || "").trim(),
      };

      const response = await createRecipeSku(payload);

      if (!response?.success) {
        toast.error("Failed to generate SKU");
        return;
      }

      // Handle Base Product
      if (response.baseProduct) {
        const base = response.baseProduct;

        const nextProduct = {
          ...product,
          sku: base.sku,
          upc: base.upc,
          descriptionEn: base.descriptionEn,
          descriptionFr: base.descriptionFr,
          uom: base.uom,
          customPrefix: base.customPrefix || (base.sku && /^15\d{8}$/.test(base.sku) ? base.sku.substring(0, 4) : product.customPrefix),
        };

        const isIngredientType = String(product.type || "").trim().toLowerCase() === "ingredient";

        setProduct((prev) => ({
          ...prev,
          sku: base.sku,
          upc: base.upc,
          customPrefix: base.customPrefix || (base.sku && /^15\d{8}$/.test(base.sku) ? base.sku.substring(0, 4) : prev.customPrefix),
          // ✅ Ingredient ke liye trademark remove karo server description se
          descriptionEn: isIngredientType
            ? String(base.descriptionEn || "")
              .replace(/\bA\s+SPICE\s+AFFAIR\b/gi, "")
              .replace(/\bNUTRIVILLA\b/gi, "")
              .replace(/\s+/g, " ")
              .trim()
            : base.descriptionEn,
          descriptionFr: isIngredientType
            ? (() => {
              const cleaned = String(base.descriptionFr || "")
                .replace(/\bA\s+SPICE\s+AFFAIR\b/gi, "")
                .replace(/\bNUTRIVILLA\b/gi, "")
                .replace(/\s+/g, " ")
                .trim();
              return cleaned.toUpperCase().includes("VRAC") ? cleaned : `${cleaned} VRAC`;
            })()
            : base.descriptionFr,
          // uom: base.uom,
          uom: isIngredientType ? prev.uom : base.uom,
          skuGenerated: true,
          generatedForNameEn: productNameEN,
          generatedForKeyword: normalizedKeyword,
        }));

        if (
          Array.isArray(base.barcodeSecondUom) &&
          base.barcodeSecondUom.length
        ) {
          setBarcodeSecondUomRows(
            base.barcodeSecondUom.map((row) => ({
              id: generateId(),
              eanUpc: String(row.eanUpc || "").trim(),
              secondUom: String(row.secondUom || "").trim() || "kg",
              quantityDefault: Number(row.quantityDefault) || 0,
              description: String(row.description || "").trim(),
              type: String(row.type || "UPCA"),
              labelPrinting: String(row.labelPrinting || "Per quantity"),
            })),
          );
        } else {
          await autoGenerateBarcodeRows(nextProduct);
        }
      }

      // Handle Variant Product
      if (response.variants?.length) {
        const variant = response.variants[0];

        setProduct((prev) => ({
          ...prev,
          sku: variant.sku,
          upc: variant.upc,
          descriptionEn: variant.descriptionEn,
          descriptionFr: variant.descriptionFr,
          uom: variant.uom,
          skuGenerated: true,
          generatedForNameEn: productNameEN,
          generatedForKeyword: normalizedKeyword,
        }));
        setBarcodeSecondUomRows([]);
      }

      toast.success("SKU + UPC generated successfully");

      if (!product.isVariant) {
        setHasGeneratedButUnsavedBase(true);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate SKU",
      );
    }
  };

  const { user, canWrite, canRead, getAccess } = useContext(AuthContext);
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const canWriteCostingTool = canWrite("CostingToolPage");

  const detailedAccess = getAccess ? getAccess("CostingDetailedCosts") : (canRead("CostingDetailedCosts") ? "read" : "no_access");
  const hasDetailedCostsPermission =
    detailedAccess === "no_access"
      ? false
      : detailedAccess === "read" || detailedAccess === "write"
      ? true
      : isAdmin;

  const [detailedCostsVisible, setDetailedCostsVisible] = useState(true);
  const canSeeDetailedCosts = hasDetailedCostsPermission && detailedCostsVisible;

  const historyAccess = getAccess ? getAccess("CostingRecipeHistory") : (canRead("CostingRecipeHistory") ? "read" : "no_access");
  const canSeeRecipeHistory =
    historyAccess === "no_access"
      ? false
      : historyAccess === "read" || historyAccess === "write"
      ? true
      : isAdmin;

  useEffect(() => {
    if (activeTab === "history" && !canSeeRecipeHistory) {
      setActiveTab("recipe");
    }
  }, [activeTab, canSeeRecipeHistory]);

  const ensureCostingWriteAccess = (actionLabel = "perform this action") => {
    if (canWriteCostingTool) return true;
    toast.error(`You do not have permission to ${actionLabel}.`);
    return false;
  };

  const handleCreateSupplier = useCallback(
    async (payload) => {
      const isObjectPayload =
        payload && typeof payload === "object" && !Array.isArray(payload);
      const suppliedName = isObjectPayload ? payload.supplier : payload;
      const trimmedName = String(suppliedName || "").trim();
      if (!trimmedName) return null;
      if (!ensureCostingWriteAccess("add suppliers")) return null;

      try {
        const res = await createSupplier(
          isObjectPayload
            ? { ...payload, supplier: trimmedName }
            : { supplier: trimmedName },
        );
        const created = res?.supplier || res?.data || res || {};
        const createdName = getSupplierName(created) || trimmedName;

        setSuppliers((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const exists = list.some(
            (supplier) =>
              getSupplierName(supplier).toLowerCase() ===
              createdName.toLowerCase(),
          );
          if (!exists) {
            list.push(created);
          }
          return list.sort((a, b) =>
            getSupplierName(a)
              .toLowerCase()
              .localeCompare(getSupplierName(b).toLowerCase()),
          );
        });

        toast.success("Supplier saved.");
        return createdName;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create supplier",
        );
        return null;
      }
    },
    [ensureCostingWriteAccess, getSupplierName],
  );

  useEffect(() => {
    const loadIngredients = async () => {
      try {
        const data = await getIngredients();
        setIngredientsList(data);
      } catch (err) {
        console.error("Failed to load ingredients");
      }
    };

    loadIngredients();
  }, []);

  useEffect(() => {
    const loadCostingSettings = async () => {
      try {
        const [settingsRes, channelRulesRes] = await Promise.allSettled([
          getCostingSettings(),
          getChannelRules(),
        ]);

        if (
          settingsRes.status === "fulfilled" &&
          settingsRes.value?.success &&
          settingsRes.value?.data
        ) {
          const { data } = settingsRes.value;
          setCategoryRules(
            Array.isArray(data.categoryRules) ? data.categoryRules : [],
          );
          setRoyaltyRules(
            Array.isArray(data.royaltyRules) ? data.royaltyRules : [],
          );
          setBulkPricingRules(
            Array.isArray(data.bulkPricingRules)
              ? data.bulkPricingRules
              : [],
          );
          setFxRates(Array.isArray(data.fxRates) ? data.fxRates : []);
          setGlobalAssumptions((prev) => ({
            ...prev,
            ...(data.globalAssumptions || {}),
          }));
          setThroughputOverhead(
            Array.isArray(data.throughputOverhead)
              ? data.throughputOverhead
              : [],
          );
        }
        if (channelRulesRes.status === "fulfilled" && channelRulesRes.value) {
          const payload = channelRulesRes.value;
          const rows = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
          setChannelRules(rows);
        }
      } catch (err) {
        console.error("Failed to load costing settings", err);
        toast.error("Using default costing settings");
      }
    };

    loadCostingSettings();
  }, []);

  useEffect(() => {
    const loadRMDatabaseRows = async () => {
      try {
        const data = await getRMRows();
        setRmDatabaseRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load RM database rows", err);
      }
    };

    loadRMDatabaseRows();
  }, []);


  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const res = await getSuppliers();
        const list = res?.suppliers || res?.data || res || [];
        const normalized = Array.isArray(list) ? list : [];
        normalized.sort((a, b) =>
          getSupplierName(a)
            .toLowerCase()
            .localeCompare(getSupplierName(b).toLowerCase()),
        );
        setSuppliers(normalized);
      } catch (err) {
        console.error("Failed to load suppliers", err);
      }
    };

    loadSuppliers();
  }, []);

  /* -------------------- PRODUCT -------------------- */

  const getInitialProduct = () => ({
    nameEn: "",
    nameFr: "",
    descriptionEn: "",
    descriptionFr: "",
    type: "regular",
    sku: "",
    upc: "",
    scc14: "",
    containsAllergen: "no",
    allergensOverride: false,
    masterWeightKg: 25,
    uom: "kg",
    taxCategory: "Tax exemption",
    itemCategory: "",
    trademark: "A Spice Affair",
    defaultPrice: "",
    backOrder: "",
    supplier: "",
    supplierSku: "",
    purchasePrice: "",
    landingCost: "",
    shippingDays: "",
    minimumOrderQuantity: "",
    orderStepQuantity: "",
    baseQuantity: "",
    countryOfOrigin: "Canada",
    harmonizedTariff: "",
    expiryDays: "1095",
    fdaCode: "",
    fr: "",
    ingredientsEn: "",
    ingredientsFr: "",
    allergensEn: "",
    allergensFr: "",
    selectedAllergens: [],
    nameFrCustom: "",
    nameEnCustom: "",
    rawMaterialSku: "",
    baseProductSku: "",
    isVariant: false,
    customFieldsSku: "",
    qtySku: "1",
    skuGenerated: false,
    generatedForNameEn: "",
    generatedForKeyword: "",
    qtySkuUnit: "kg",
    netWeightGkKg: "1",
    gkKgUnit: "kg",
    netWeightOzLbs: "2.20",
    ozLbsUnit: "oz",
    skuKeyword: "",
    categorie: "",
    msrpCad: "",
    msrpUsd: "",
    lengthMm: "",
    widthMm: "",
    heightMm: "",
    lengthInch: "",
    widthInch: "",
    heightInch: "",
  });

  const resetEntireForm = ({ showToast = true } = {}) => {
    setProduct(getInitialProduct());

    setIngredients([createEmptyIngredient(), createEmptyIngredient()]);
    setIngredientCount(1);

    setAddedPackaging(null);
    setPackagingCost("");
    setShowPackagingModal(false);
    setSelectedVariant(null);
    setVariants([]);
    setMasterCase({
      type: "Standard Master case",
      weight: 25,
    });
    setBarcodeSecondUomRows([]);
    setFocusedIngredientId(null);
    baselineRef.current = null;
    setHasUnsavedChanges(false);
    void clearSavedDraft();
    if (showToast) {
      toast.success("Form completely reset! Ready for a new recipe.", {
        duration: 4000,
      });
    }
  };

  const [product, setProduct] = useState(getInitialProduct);

  const productTypeKey =
    String(product?.type || "")
      .trim()
      .toLowerCase() === "ingredient"
      ? "ingredient"
      : "blend";

  useEffect(() => {
    const fetchMandatoryFields = async () => {
      try {
        const data = await getMandatoryFields(productTypeKey);
        setMandatoryFields(data || {});
      } catch (err) {
        console.error("Error loading mandatory fields:", err);
      }
    };

    fetchMandatoryFields();
  }, [productTypeKey]);

  useEffect(() => {
    if (product.type === "ingredient" && product.backOrder !== "Supplier") {
      setProduct((prev) => ({ ...prev, backOrder: "Supplier" }));
    }
  }, [product.type, product.backOrder]);

  useEffect(() => {
    if (
      product.type !== "ingredient" &&
      product.backOrder !== "Bill of Materials"
    ) {
      setProduct((prev) => ({ ...prev, backOrder: "Bill of Materials" }));
    }
  }, [product.type, product.backOrder]);

  useEffect(() => {
    if (product.type !== "ingredient") return;

    setProduct((prev) => ({
      ...prev,
      lengthMm:
        prev.lengthMm && Number(prev.lengthMm) > 0 ? prev.lengthMm : "0",
      widthMm: prev.widthMm && Number(prev.widthMm) > 0 ? prev.widthMm : "0",
      heightMm:
        prev.heightMm && Number(prev.heightMm) > 0 ? prev.heightMm : "0",
      lengthInch:
        prev.lengthInch && Number(prev.lengthInch) > 0 ? prev.lengthInch : "0",
      widthInch:
        prev.widthInch && Number(prev.widthInch) > 0 ? prev.widthInch : "0",
      heightInch:
        prev.heightInch && Number(prev.heightInch) > 0 ? prev.heightInch : "0",
      msrpCad:
        prev.msrpCad === "" || prev.msrpCad === null || prev.msrpCad === undefined
          ? "0"
          : prev.msrpCad,
      msrpUsd:
        prev.msrpUsd === "" || prev.msrpUsd === null || prev.msrpUsd === undefined
          ? "0"
          : prev.msrpUsd,
    }));
  }, [product.type]);

  useEffect(() => {
    if (product.itemCategory === "" && product.type === "regular") {
      const defaultCat = ITEM_CATEGORIES.find(
        (cat) => cat.en === "Spice Blends VRAC",
      );
      if (defaultCat) {
        setProduct((prev) => ({
          ...prev,
          itemCategory: defaultCat.en,
          categorie: defaultCat.fr,
        }));
      }
    }
  }, []);

  /* -------------------- INGREDIENTS -------------------- */
  const createEmptyIngredient = () => ({
    id: generateId(),
    name: "",
    sku: "",
    nameFr: "",
    weight: "",
    unit: globalUnit || "kg",
    ratio: "",
    unitCost: "",
    totalCost: 0,
  });

  const [bulkFormats, setBulkFormats] = useState([]);
  const [packagingModalForFormatIndex, setPackagingModalForFormatIndex] =
    useState(null);

  const [ingredients, setIngredients] = useState([
    createEmptyIngredient(),
    createEmptyIngredient(),
  ]);
  const [ratioRoundingStep, setRatioRoundingStep] = useState("0.1");
  const [showPackagingModal, setShowPackagingModal] = useState(false);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addedPackaging, setAddedPackaging] = useState(null);
  const [loadingPackaging, setLoadingPackaging] = useState(false);
  const [packagingVariantsError, setPackagingVariantsError] = useState("");
  const [packagingSearchTerm, setPackagingSearchTerm] = useState("");
  const [packagingFormatFilter, setPackagingFormatFilter] = useState("All");
  const [packagingCost, setPackagingCost] = useState("");
  const packagingVariantsRef = useRef([]);
  const packagingVariantsLastFetchedAtRef = useRef(0);
  const packagingVariantsRequestRef = useRef(null);

  useEffect(() => {
    packagingVariantsRef.current = variants;
  }, [variants]);

  const fetchPackagingVariants = useCallback(
    async ({ force = false, showLoader = true, notifyOnError = true } = {}) => {
      const now = Date.now();
      const hasCached = packagingVariantsRef.current.length > 0;
      const cacheIsFresh =
        hasCached &&
        now - packagingVariantsLastFetchedAtRef.current <
        PACKAGING_VARIANTS_STALE_MS;

      if (!force && cacheIsFresh) {
        setPackagingVariantsError("");
        return packagingVariantsRef.current;
      }

      if (packagingVariantsRequestRef.current) {
        return packagingVariantsRequestRef.current;
      }

      if (showLoader) setLoadingPackaging(true);
      setPackagingVariantsError("");

      const requestPromise = (async () => {
        let lastError;

        for (
          let attempt = 0;
          attempt <= PACKAGING_VARIANTS_MAX_RETRIES;
          attempt += 1
        ) {
          try {
            const res = await getAllPackagingVariants({
              timeoutMs: PACKAGING_VARIANTS_TIMEOUT_MS,
            });
            const nextVariants = Array.isArray(res?.data) ? res.data : [];
            nextVariants.forEach((variant) =>
              logPackagingCostComparison("loaded packaging cost comparison", variant),
            );
            setVariants(nextVariants);
            packagingVariantsLastFetchedAtRef.current = Date.now();
            setPackagingVariantsError("");
            return nextVariants;
          } catch (err) {
            lastError = err;
            if (attempt < PACKAGING_VARIANTS_MAX_RETRIES) {
              await new Promise((resolve) => {
                setTimeout(resolve, 500 * (attempt + 1));
              });
            }
          }
        }

        const isTimeout =
          lastError?.code === "ECONNABORTED" ||
          /timeout/i.test(lastError?.message || "");

        if (packagingVariantsRef.current.length > 0) {
          const fallbackMessage = isTimeout
            ? "Refresh timed out. Showing cached packaging variants."
            : "Unable to refresh packaging variants. Showing cached list.";
          setPackagingVariantsError(fallbackMessage);
          if (notifyOnError) toast.error(fallbackMessage);
          return packagingVariantsRef.current;
        }

        const errorMessage = isTimeout
          ? "Packaging variants request timed out. Please retry."
          : "Failed to load packaging variants";
        setPackagingVariantsError(errorMessage);
        setVariants([]);
        if (notifyOnError) toast.error(errorMessage);
        return [];
      })();

      packagingVariantsRequestRef.current = requestPromise;

      try {
        return await requestPromise;
      } finally {
        packagingVariantsRequestRef.current = null;
        if (showLoader) setLoadingPackaging(false);
      }
    },
    [],
  );

  const packagingFormatOptions = useMemo(() => {
    const defaults = ["All", "Bulk", "Pouch", "Glass"];
    const dynamicFormats = Array.from(
      new Set(
        (variants || [])
          .map((variant) => String(variant?.format || "").trim())
          .filter(Boolean),
      ),
    );
    return Array.from(new Set([...defaults, ...dynamicFormats]));
  }, [variants]);

  const filteredPackagingVariants = useMemo(() => {
    const search = packagingSearchTerm.trim().toLowerCase();
    const allowedPackagingNames = [
      "Bulk Master Case",
      "Bulk Master XL Case (light products)",
    ];
    return (variants || []).filter((variant) => {
      const format = String(variant?.format || "");
      const formatLower = format.toLowerCase();
      const size = String(variant?.size || "");
      const name = String(variant?.name || "");

      const matchesName = allowedPackagingNames.includes(name);
      const matchesSearch =
        !search ||
        name.toLowerCase().includes(search) ||
        formatLower.includes(search) ||
        size.toLowerCase().includes(search);

      return matchesName && matchesSearch;
    });
  }, [variants, packagingSearchTerm]);

  const handleOpenPackagingModal = () => {
    if (!ensureCostingWriteAccess("edit packaging")) return;
    setShowPackagingModal(true);
    setSelectedVariant(null);
    setPackagingVariantsError("");
    setPackagingSearchTerm("");
  };

  /* -------------------- MASTER CASE & PACKAGING -------------------- */
  const [masterCase, setMasterCase] = useState({
    type: "Standard Master case",
    weight: product.masterWeightKg,
  });

  useEffect(() => {
    setMasterCase((prev) => ({
      ...prev,
      weight: product.masterWeightKg,
    }));
  }, [product.masterWeightKg]);

  useEffect(() => {
    if (!product.sku || bulkFormats.length === 0) {
      setBulkFormats((prev) =>
        prev.map((f) => ({ ...f, generatedSku: "", displayWeight: "" })),
      );
      return;
    }

    const basePrefix = product.sku.slice(0, -3);

    const updated = bulkFormats.map((format) => {
      // Normalize weight (string OR number)
      const weightNum =
        format.netWeightKg !== null &&
          format.netWeightKg !== undefined &&
          format.netWeightKg !== ""
          ? Number(format.netWeightKg)
          : 0;

      // Empty or invalid
      if (!weightNum || isNaN(weightNum) || weightNum <= 0) {
        return {
          ...format,
          generatedSku: "",
          displayWeight: "",
        };
      }

      // SKU size code logic
      const codeNum = Math.round(weightNum * 10);
      const sizeCode = String(codeNum).padStart(3, "0");

      return {
        ...format,
        displayWeight: `${weightNum} kg`,
        generatedSku: `${basePrefix}${sizeCode}`,
      };
    });

    setBulkFormats(updated);
  }, [product.sku, bulkFormats.map((f) => f.netWeightKg).join("|")]);

  const actualIngredientCount = ingredients.length;

  const totalWeight = ingredients.reduce(
    (sum, ing) => sum + convertToKg(ing.weight, ing.unit),
    0,
  );

  const ratioTotal = ingredients.reduce(
    (sum, ing) => sum + (Number(ing.ratio) || 0),
    0,
  );
  const MAX_RATIO_TOTAL = 100;
  const MAX_RATIO_UNITS = MAX_RATIO_TOTAL * 100;
  const RATIO_TOTAL_EPSILON = 0.001;
  const RATIO_PRECISION = 2;
  const normalizedRatioTotal = Number(ratioTotal.toFixed(RATIO_PRECISION));
  const isRatioTotalValid =
    Math.abs(normalizedRatioTotal - MAX_RATIO_TOTAL) < RATIO_TOTAL_EPSILON;
  const ratioTotalDisplay = normalizedRatioTotal.toFixed(RATIO_PRECISION);
  const toRatioNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  const getRatioStepUnits = () => (ratioRoundingStep === "0.05" ? 5 : 10);
  const toRatioUnits = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.round(num * 100);
  };
  const fromRatioUnits = (units) =>
    Number((Math.max(0, units) / 100).toFixed(RATIO_PRECISION));
  const roundRatioValue = (value) => {
    const rawUnits = toRatioUnits(value);
    const stepUnits = getRatioStepUnits();
    const roundedUnits = Math.round(rawUnits / stepUnits) * stepUnits;
    return fromRatioUnits(roundedUnits);
  };
  const formatRatioDisplay = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(RATIO_PRECISION) : "";
  };
  const capRatiosToMax = (list, preferredId = null) => {
    const next = list.map((ing) => ({
      ...ing,
      ratio: roundRatioValue(ing.ratio),
    }));
    const getRatioUnits = (ing) => toRatioUnits(ing.ratio);
    let totalUnits = next.reduce((sum, ing) => sum + getRatioUnits(ing), 0);
    if (totalUnits <= MAX_RATIO_UNITS) return next;

    const preferredIndex = preferredId
      ? next.findIndex((ing) => ing.id === preferredId)
      : -1;
    const ratioOrder = next
      .map((ing, idx) => ({ idx, ratioUnits: getRatioUnits(ing) }))
      .filter((item) => item.ratioUnits > 0)
      .sort((a, b) => b.ratioUnits - a.ratioUnits)
      .map((item) => item.idx);

    const reductionOrder = [];
    ratioOrder.forEach((idx) => {
      if (idx !== preferredIndex && !reductionOrder.includes(idx)) {
        reductionOrder.push(idx);
      }
    });
    if (preferredIndex >= 0) reductionOrder.push(preferredIndex);

    let overflowUnits = totalUnits - MAX_RATIO_UNITS;
    for (const idx of reductionOrder) {
      if (overflowUnits <= 0) break;
      const currentUnits = getRatioUnits(next[idx]);
      if (currentUnits <= 0) continue;
      const reduceByUnits = Math.min(currentUnits, overflowUnits);
      next[idx].ratio = fromRatioUnits(currentUnits - reduceByUnits);
      overflowUnits -= reduceByUnits;
    }

    totalUnits = next.reduce((sum, ing) => sum + getRatioUnits(ing), 0);
    if (totalUnits > MAX_RATIO_UNITS) {
      const overflowFinalUnits = totalUnits - MAX_RATIO_UNITS;
      const maxIdx = next.reduce(
        (maxIndex, ing, idx) =>
          getRatioUnits(ing) > getRatioUnits(next[maxIndex]) ? idx : maxIndex,
        0,
      );
      const currentUnits = getRatioUnits(next[maxIdx]);
      next[maxIdx].ratio = fromRatioUnits(
        Math.max(0, currentUnits - overflowFinalUnits),
      );
    }

    return next;
  };
  const rebalanceRatiosToHundred = (list, preferredId = null) => {
    const next = list.map((ing) => ({
      ...ing,
      ratio: roundRatioValue(ing.ratio),
    }));
    if (next.length === 0) return next;

    const getRatioUnits = (ing) => toRatioUnits(ing.ratio);
    let totalUnits = next.reduce((sum, ing) => sum + getRatioUnits(ing), 0);
    if (totalUnits === MAX_RATIO_UNITS) return next;

    const preferredIndex = preferredId
      ? next.findIndex((ing) => ing.id === preferredId)
      : -1;
    const ratioOrder = next
      .map((ing, idx) => ({ idx, ratioUnits: getRatioUnits(ing) }))
      .sort((a, b) => b.ratioUnits - a.ratioUnits)
      .map((item) => item.idx);
    const adjustOrder = [];
    if (preferredIndex >= 0) adjustOrder.push(preferredIndex);
    ratioOrder.forEach((idx) => {
      if (!adjustOrder.includes(idx)) adjustOrder.push(idx);
    });

    let deltaUnits = MAX_RATIO_UNITS - totalUnits;
    if (deltaUnits > 0) {
      const targetIdx = adjustOrder[0] ?? 0;
      const currentUnits = getRatioUnits(next[targetIdx]);
      next[targetIdx].ratio = fromRatioUnits(currentUnits + deltaUnits);
    } else if (deltaUnits < 0) {
      let deficitUnits = Math.abs(deltaUnits);
      for (const idx of adjustOrder) {
        if (deficitUnits <= 0) break;
        const currentUnits = getRatioUnits(next[idx]);
        if (currentUnits <= 0) continue;
        const reduceByUnits = Math.min(currentUnits, deficitUnits);
        next[idx].ratio = fromRatioUnits(currentUnits - reduceByUnits);
        deficitUnits -= reduceByUnits;
      }
    }

    totalUnits = next.reduce((sum, ing) => sum + getRatioUnits(ing), 0);
    if (totalUnits > MAX_RATIO_UNITS) {
      return capRatiosToMax(next, preferredId);
    }

    return next;
  };
  const masterCaseWeightKg =
    Number(product.baseQuantity) || Number(product.masterWeightKg) || 0;

  const getRatioWeightKg = (ing) => {
    const ratio = Number(ing.ratio) || 0;
    if (ratio <= 0 || masterCaseWeightKg <= 0) return 0;
    return (ratio / 100) * masterCaseWeightKg;
  };
  const getIngredientQtyKg = (ing) => {
    const ratioWeightKg = getRatioWeightKg(ing);
    return ratioWeightKg > 0 ? ratioWeightKg : convertToKg(ing.weight, ing.unit);
  };
  const rmDatabaseMap = useMemo(() => {
    const map = new Map();

    rmDatabaseRows.forEach((row) => {
      map.set(String(row.RMCode || "").trim(), row);
    });

    return map;
  }, [rmDatabaseRows]);

  const getIngredientCostParts = (ing) => {
    const rmRow = rmDatabaseMap.get(String(ing.sku || "").trim());
    const baseFromIng = Number(ing.unitCost) || 0;
    const baseFromRm = Number(rmRow?.baseCostKgCad) || Number(rmRow?.TotalRMCost) || 0;
    const unitCostKgCad = baseFromIng > 0 ? baseFromIng : baseFromRm;
    const categoryPremiumKgCad = Number(rmRow?.categoryPremiumKgCad) || 0;
    const allergenPremiumKgCad = Number(rmRow?.allergenPremiumKgCad) || 0;
    const totalCostKgCad =
      unitCostKgCad + categoryPremiumKgCad + allergenPremiumKgCad;

    return {
      rmRow,
      unitCostKgCad,
      categoryPremiumKgCad,
      allergenPremiumKgCad,
      totalCostKgCad,
    };
  };

  // const convertToKg = (weight, unit) => {
  //   const value = Number(weight) || 0;
  //   const normalizedUnit = String(unit || "kg").trim().toLowerCase();

  //   if (normalizedUnit === "kg") return value;
  //   if (normalizedUnit === "g") return value / 1000;
  //   if (normalizedUnit === "lb" || normalizedUnit === "lbs")
  //     return value * 0.453592;
  //   if (normalizedUnit === "oz")
  //     return value * 0.0283495;

  //   return value;
  // };



  // const getRatioWeightKg = (ing) => {
  //   const ratio = Number(ing.ratio) || 0;
  //   if (ratio <= 0 || masterCaseWeightKg <= 0) return 0;
  //   return (ratio / 100) * masterCaseWeightKg;
  // };

  /* -------------------- COST CALCULATIONS -------------------- */
  useEffect(() => {
    const updated = ingredients.map((ing) => {
      const qtyKg = getIngredientQtyKg(ing);
      const {
        categoryPremiumKgCad,
        allergenPremiumKgCad,
        totalCostKgCad,
      } = getIngredientCostParts(ing);
      const ingredientTotalCost = qtyKg * totalCostKgCad;

      return {
        ...ing,
        categoryPremiumKgCad,
        allergenPremiumKgCad,
        totalCostKgCad,
        totalCost: ingredientTotalCost,
      };
    });
    setIngredients(updated);
  }, [
    ingredients
      .map((i) => `${i.weight}-${i.unit}-${i.unitCost}-${i.ratio}-${i.sku}`)
      .join(),
    masterCaseWeightKg,
    rmDatabaseMap,
  ]);

  useEffect(() => {
    if (!showPackagingModal) return;

    const hasCached = packagingVariantsRef.current.length > 0;
    const cacheIsStale =
      Date.now() - packagingVariantsLastFetchedAtRef.current >=
      PACKAGING_VARIANTS_STALE_MS;

    if (!hasCached) {
      void fetchPackagingVariants({ showLoader: true });
      return;
    }

    if (cacheIsStale) {
      void fetchPackagingVariants({ showLoader: false });
    }
  }, [showPackagingModal, fetchPackagingVariants]);

  useEffect(() => {
    void fetchPackagingVariants({ showLoader: false, notifyOnError: false });
  }, [fetchPackagingVariants]);

  const isBaseIngredient = product.type === "ingredient";
  const purchasePriceNum = Number(product.purchasePrice) || 0;
  const landingCostNum = Number(product.landingCost) || 0;
  const orderStepQtyNum = Number(product.orderStepQuantity) || 0;
  const baseIngredientCostPerKg = purchasePriceNum + landingCostNum;
  const baseIngredientWeightKg =
    Number(product.netWeightGkKg) || Number(product.qtySku) || Number(product.netWeight1Kg) || Number(product.qtyUn) || (orderStepQtyNum > 0 ? orderStepQtyNum : 1);
  const baseIngredientMasterCaseCost =
    baseIngredientCostPerKg * baseIngredientWeightKg;
  const baseIngredientCostPerLbs = baseIngredientCostPerKg / 2.205;

  // ─── AUTO-FILL WEIGHT FROM SKU (Base Ingredients Only) ───────────────────
  useEffect(() => {
    if (!isBaseIngredient) return;

    // Use supplierSku first, fallback to product sku
    const rawSku = String(product.supplierSku || product.sku || "").trim();
    if (!rawSku) return;

    // Parse weight from last 3 digits of SKU: 010→1, 025→2.5, 050→5, 100→10, 250→25
    const suffix = rawSku.slice(-3);
    const weightMap = {
      "010": 1,
      "025": 2.5,
      "050": 5,
      "100": 10,
      "250": 25,
    };
    const detectedWeight = weightMap[suffix];
    if (!detectedWeight) return;

    const detectedWeightStr = String(detectedWeight);
    const lbsWeight = (detectedWeight * 2.20462).toFixed(2);

    // Only update if values differ to avoid infinite loop
    const needsUpdate =
      String(product.qtySku) !== detectedWeightStr ||
      String(product.netWeightGkKg) !== detectedWeightStr ||
      String(product.netWeight1Kg) !== detectedWeightStr ||
      product.qtySkuUnit !== "kg" ||
      product.gkKgUnit !== "kg";

    if (!needsUpdate) return;

    setProduct((prev) => ({
      ...prev,
      qtySku: detectedWeightStr,
      qtySkuUnit: "kg",
      netWeightGkKg: detectedWeightStr,
      gkKgUnit: "kg",
      netWeightOzLbs: lbsWeight,
      ozLbsUnit: "lbs",
      netWeight1Kg: detectedWeightStr,
      qtyUn: detectedWeightStr,
    }));
  }, [isBaseIngredient, product.supplierSku, product.sku]); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────

  const ingredientCostTotal = ingredients.reduce((sum, ing) => {
    const qtyKg = getIngredientQtyKg(ing);
    const { totalCostKgCad } = getIngredientCostParts(ing);
    return sum + qtyKg * totalCostKgCad;
  }, 0);
  const ALLERGEN_MASTER = [
    { key: "mustard", en: "Mustard", fr: "Moutarde" },
    { key: "sesame", en: "Sesame", fr: "Sésame" },
    { key: "celery", en: "Celery", fr: "Céleri" },
    { key: "dairy", en: "Dairy", fr: "Lait" },
  ];

  const generateAllergensFromIngredients = (ingredientsList) => {
    const detected = new Map();

    ingredientsList.forEach((ing) => {
      const name = (ing.name || "").toLowerCase();

      ALLERGEN_MASTER.forEach((allergen) => {
        if (name.includes(allergen.key)) {
          detected.set(allergen.key, allergen);
        }
      });
    });

    return {
      en: Array.from(detected.values())
        .map((a) => a.en)
        .join(", "),
      fr: Array.from(detected.values())
        .map((a) => a.fr)
        .join(", "),
    };
  };

  //  THEN useEffect
  useEffect(() => {
    if (product.type === "ingredient") return;

    const allergens = generateAllergensFromIngredients(ingredients);

    setProduct((prev) => ({
      ...prev,
      allergensEn: allergens.en,
      allergensFr: allergens.fr,
      containsAllergen: allergens.en.trim() !== "" ? "yes" : "no",
    }));
  }, [ingredients, product.type]);

  const scheduleFormattedIngredients = useCallback((sourceIngredients = []) => {
    const payload = sourceIngredients
      .filter(
        (ing) => ing.name && Number(ing.weight) > 0 && Number(ing.ratio) > 0,
      )
      .map((ing) => ({
        name: ing.name,
        nameFr: ing.nameFr || "",
        percent: Number(ing.ratio),
        sku: ing.sku || "",
      }));
    const payloadSignature = payload.length
      ? payload
        .map((ing) => `${ing.sku}|${ing.name}|${ing.nameFr}|${ing.percent}`)
        .join("||")
      : "__EMPTY__";

    if (payloadSignature === lastFormattedPayloadSignatureRef.current) return;

    if (formatDebounceTimerRef.current) {
      clearTimeout(formatDebounceTimerRef.current);
    }

    formatDebounceTimerRef.current = setTimeout(async () => {
      try {
        if (!payload.length) {
          lastFormattedPayloadSignatureRef.current = "__EMPTY__";
          setProduct((prev) => ({
            ...prev,
            ingredientsEn: "",
            ingredientsFr: "",
          }));
          return;
        }

        const requestId = ++latestFormatRequestIdRef.current;
        const res = await formatIngredients(payload);

        // Ignore stale responses if user changed weight again.
        if (requestId !== latestFormatRequestIdRef.current) return;

        lastFormattedPayloadSignatureRef.current = payloadSignature;
        setProduct((prev) => ({
          ...prev,
          ingredientsEn: res.ingredientsEn || "",
          ingredientsFr: res.ingredientsFr || "",
        }));
      } catch (err) {
        console.error("Ingredient formatting failed", err);
        toast.error("Failed to format ingredients");
      }
    }, 300);
  }, []);

  const weightSignature = useMemo(
    () =>
      ingredients
        .map((ing) => `${ing.id}:${String(ing.weight ?? "")}`)
        .join("|"),
    [ingredients],
  );

  useEffect(() => {
    latestIngredientsRef.current = ingredients;
  }, [ingredients]);

  useEffect(() => {
    scheduleFormattedIngredients(latestIngredientsRef.current);
  }, [weightSignature, scheduleFormattedIngredients]);

  useEffect(() => {
    return () => {
      if (formatDebounceTimerRef.current) {
        clearTimeout(formatDebounceTimerRef.current);
      }
    };
  }, []);

  const packagingCostNum = isBaseIngredient ? 0 : Number(packagingCost || 0);

  const totalBatchCost = isBaseIngredient
    ? baseIngredientCostPerKg
    : ingredientCostTotal + packagingCostNum;

  const acctCostPerKg = isBaseIngredient
    ? baseIngredientCostPerKg.toFixed(3)
    : totalWeight > 0
      ? totalBatchCost.toFixed(3)
      : "0.000";
  const masterCaseCost = isBaseIngredient
    ? baseIngredientMasterCaseCost
    : totalBatchCost;
  const costPerKg = isBaseIngredient
    ? baseIngredientCostPerKg.toFixed(3)
    : masterCaseWeightKg > 0
      ? (masterCaseCost / masterCaseWeightKg).toFixed(3)
      : "0.000";
  const costPerLbs = isBaseIngredient
    ? baseIngredientCostPerLbs.toFixed(3)
    : masterCaseWeightKg > 0
      ? (masterCaseCost / (masterCaseWeightKg * 2.20462)).toFixed(3)
      : "0.000";

  const selectedCategory = String(
    product.itemCategory || product.categorie || "",
  ).trim();
  const selectedCategoryRule =
    (Array.isArray(categoryRules) ? categoryRules : []).find(
      (row) => String(row?.category || "").trim() === selectedCategory,
    ) || {};
  const hasAllergenPremium =
    product.containsAllergen === "yes" ||
    Boolean(product.allergensOverride) ||
    (Array.isArray(product.selectedAllergens) &&
      product.selectedAllergens.length > 0);
  const allergenPremiumPerUnit = hasAllergenPremium
    ? Math.max(0, Number(selectedCategoryRule.allergenPremiumPerUnit) || 0)
    : 0;
  const signaturePremiumPerUnit = isBaseIngredient
    ? 0
    : Math.max(0, Number(selectedCategoryRule.signaturePremiumPerUnit) || 0);
  const selectedPackagingName = String(addedPackaging?.name || "")
    .trim()
    .toLowerCase();
  const matchedThroughputRow =
    (Array.isArray(throughputOverhead) ? throughputOverhead : []).find(
      (row) =>
        String(row?.packagingType || "")
          .trim()
          .toLowerCase() === selectedPackagingName,
    ) || {};
  const workingDaysPerWeek = Math.max(
    1,
    Number(globalAssumptions.workingDaysPerWeek) || 1,
  );
  const weeklyOverheadCostCad = Math.max(
    0,
    Number(globalAssumptions.weeklyOverheadCostCad) || 0,
  );
  const weeklyLabourCostCad = Math.max(
    0,
    Number(globalAssumptions.weeklyLabourCostCad) || 0,
  );
  const throughputUnitsPerDay = Math.max(
    0,
    Number(matchedThroughputRow.throughputUnitsPerDay) || 0,
  );
  const throughputUnitsPerWeek = throughputUnitsPerDay * workingDaysPerWeek;
  const throughputCostPerUnit =
    throughputUnitsPerWeek > 0
      ? ((Number(matchedThroughputRow.overheadAllocationPercent) || 0) *
        weeklyOverheadCostCad +
        (Number(matchedThroughputRow.labourAllocationPercent) || 0) *
        weeklyLabourCostCad) /
      throughputUnitsPerWeek
      : 0;

  const rmCategoryPremiumTotalCad = ingredients.reduce((sum, ing) => {
    const rmRow = rmDatabaseMap.get(String(ing.sku || "").trim());

    if (!rmRow) return sum;

    const qtyKg = getIngredientQtyKg(ing);

    return (
      sum +
      qtyKg * Number(rmRow.categoryPremiumKgCad || 0)
    );
  }, 0);

  const rmAllergenPremiumTotalCad = ingredients.reduce((sum, ing) => {
    const rmRow = rmDatabaseMap.get(String(ing.sku || "").trim());

    if (!rmRow) return sum;

    const qtyKg = getIngredientQtyKg(ing);

    return (
      sum +
      qtyKg * Number(rmRow.allergenPremiumKgCad || 0)
    );
  }, 0);
  // const finalCogsBaseCad =
  //   ingredientCostTotal +
  //   packagingCostNum +
  //   throughputCostPerUnit +
  //   signaturePremiumPerUnit +
  //   allergenPremiumPerUnit;


  const finalCogsBaseCad =
    (isBaseIngredient ? masterCaseCost : ingredientCostTotal) +
    packagingCostNum +
    throughputCostPerUnit +
    signaturePremiumPerUnit +
    allergenPremiumPerUnit;

  const finalCogsMultiplier =
    1 +
    Number(globalAssumptions.lossBufferPercent || 0) / 100 +
    Number(globalAssumptions.earlyPaymentDeductionPercent || 0) / 100 +
    Number(globalAssumptions.cogsIncreaseBufferPercent || 0) / 100;

  const finalCogsPerCsCad =
    finalCogsBaseCad > 0
      ? finalCogsBaseCad * finalCogsMultiplier
      : 0;
  const resolveBulkCategoryName = () => {
    const rawCat = String(product.itemCategory || product.categorie || "").trim();
    const rules = Array.isArray(bulkPricingRules) ? bulkPricingRules : [];
    if (rawCat && rules.some((r) => String(r?.category || "").trim() === rawCat)) {
      return rawCat;
    }
    const w = Number(product.netWeightGkKg) || Number(product.qtySku) || Number(product.netWeight1Kg) || Number(product.qtyUn) || 1;
    if (w === 2.5) return "Spices Bulk 2.5 KG";
    if (w === 5) return "Spices Bulk 5 KG";
    if (w === 10) return "Spices Bulk 10 KG";
    if (w === 25) return "Spices Bulk 25 KG (MASTER)";
    if (rawCat.toLowerCase().includes("blend") || rawCat.toLowerCase().includes("mélange")) {
      return "Spice Blends VRAC";
    }
    return "Spices Bulk 1 KG";
  };

  const resolvedBulkCategory = resolveBulkCategoryName();

  const selectedBulkRule =
    (Array.isArray(bulkPricingRules) ? bulkPricingRules : []).find(
      (row) => String(row?.category || "").trim() === resolvedBulkCategory,
    ) || { grossMarginPercent: 50, webPercent: 55 };
  const grossMarginPercent = Math.max(
    0,
    Number(selectedBulkRule.grossMarginPercent) ?? 50,
  );
  const webPercent = Math.max(0, Number(selectedBulkRule.webPercent) ?? 55);
  const paysRoyalty =
    product.paysRoyalty === true ||
    String(product.paysRoyalty || "")
      .trim()
      .toLowerCase() === "yes";
  const categoryRoyaltyRuleName = String(
    selectedCategoryRule.royaltyRule ||
    selectedCategoryRule.royaltyRuleWeb ||
    selectedCategoryRule.webRoyaltyRule ||
    "",
  ).trim();
  const royaltyPercent = paysRoyalty
    ? Math.max(
      0,
      Number(
        (
          (Array.isArray(royaltyRules) ? royaltyRules : []).find(
            (row) =>
              String(row?.royaltyRule || "").trim() ===
              categoryRoyaltyRuleName,
          ) || {}
        ).royaltyPercent,
      ) || 0,
    )
    : 0;
  const grossMarginRate = grossMarginPercent / 100;
  const webRate = webPercent / 100;
  const royaltyRate = royaltyPercent / 100;
  const wholesale3CadDenominator = 1 - grossMarginRate - royaltyRate;
  const wholesale3Cad =
    wholesale3CadDenominator > 0
      ? finalCogsPerCsCad / wholesale3CadDenominator
      : 0;
  const standardSrpCadRaw =
    1 - webRate > 0 ? wholesale3Cad / (1 - webRate) : 0;
  const roundStandardSrpCad = (value) => {
    const num = Number(value) || 0;
    if (num <= 0) return 0;
    return Math.max(0, Math.ceil(num / 0.5) * 0.5 - 0.05);
  };
  const roundMsrpEnding99 = (value) => {
    const num = Number(value) || 0;
    if (num <= 0) return 0;
    return Math.max(0, Math.ceil(num) - 0.01);
  };
  const standardSrpCad = roundStandardSrpCad(standardSrpCadRaw);

  const standardMarginPercent =
    Number(standardSrpCad) > 0
      ? ((Number(standardSrpCad) - Number(finalCogsPerCsCad || 0)) /
        Number(standardSrpCad)) *
      100
      : null;

  const wholesale3CadRounded = roundStandardSrpCad(wholesale3Cad);
  const wholesale1Rate = Math.max(
    0,
    Number(selectedBulkRule.wholesale1Percent) || 0,
  ) / 100;
  const wholesale2Rate = Math.max(
    0,
    Number(selectedBulkRule.wholesale2Percent) || 0,
  ) / 100;
  const distributorRate = Math.max(
    0,
    Number(selectedBulkRule.distributorPercent) || 0,
  ) / 100;
  const wholesale1Cad = roundStandardSrpCad(standardSrpCad * (1 - wholesale1Rate));
  const wholesale2Cad = roundStandardSrpCad(standardSrpCad * (1 - wholesale2Rate));
  const distributorCad = roundStandardSrpCad(
    wholesale3CadRounded * (1 - distributorRate),
  );
  const webRetailChannelRule =
    (Array.isArray(channelRules) ? channelRules : []).find(
      (row) =>
        String(row?.priceLevel || "")
          .trim()
          .toLowerCase() === "web retail",
    ) || {};
  const usdFxFromChannelRule = Math.max(
    0,
    Number(webRetailChannelRule.fixedUsdFx) || 0,
  );
  const usdFxFromRates = Math.max(
    0,
    Number(
      (
        (Array.isArray(fxRates) ? fxRates : []).find(
          (row) =>
            String(row?.currency || "")
              .trim()
              .toUpperCase() === "USD",
        ) || {}
      ).bufferedFxVsCad,
    ) || 0,
  );
  const usdFxVsCad = usdFxFromChannelRule > 0 ? usdFxFromChannelRule : usdFxFromRates;
  const standardSrpUsd = roundStandardSrpCad(standardSrpCad * usdFxVsCad);
  const msrpCadPerKgRounded99 =
    masterCaseWeightKg > 0
      ? roundMsrpEnding99(standardSrpCadRaw / masterCaseWeightKg)
      : 0;
  const msrpCadMasterRounded99 =
    masterCaseWeightKg > 0 ? msrpCadPerKgRounded99 * masterCaseWeightKg : 0;
  const msrpUsdPerKgRounded99 =
    masterCaseWeightKg > 0
      ? roundMsrpEnding99((standardSrpCadRaw / masterCaseWeightKg) * usdFxVsCad)
      : 0;
  const msrpUsdMasterRounded99 =
    masterCaseWeightKg > 0 ? msrpUsdPerKgRounded99 * masterCaseWeightKg : 0;
  const exportPercent = Math.max(0, Number(selectedBulkRule.exportPercent) || 0) / 100;
  const exportMenaPercent =
    Math.max(0, Number(selectedBulkRule.exportMenaPercent) || 0) / 100;
  const findRoyaltyPercentByRule = (ruleName) => {
    const key = String(ruleName || "").trim();
    if (!key || !paysRoyalty) return 0;
    return Math.max(
      0,
      Number(
        (
          (Array.isArray(royaltyRules) ? royaltyRules : []).find(
            (row) => String(row?.royaltyRule || "").trim() === key,
          ) || {}
        ).royaltyPercent,
      ) || 0,
    );
  };
  const exportUsdRoyaltyRuleName = String(
    selectedCategoryRule.royaltyRuleExportUsd ||
    selectedCategoryRule.royaltyRuleExport ||
    selectedCategoryRule.exportRoyaltyRule ||
    selectedCategoryRule.royaltyRuleUsd ||
    selectedCategoryRule.royaltyRule ||
    "",
  ).trim();
  const exportMenaRoyaltyRuleName = String(
    selectedCategoryRule.royaltyRuleExportMena ||
    selectedCategoryRule.royaltyRuleMena ||
    selectedCategoryRule.menaRoyaltyRule ||
    selectedCategoryRule.royaltyRule ||
    "",
  ).trim();
  const exportUsdRoyaltyRate = findRoyaltyPercentByRule(exportUsdRoyaltyRuleName) / 100;
  const exportMenaRoyaltyRate =
    findRoyaltyPercentByRule(exportMenaRoyaltyRuleName) / 100;
  const exportUsdBaseCad =
    1 - exportUsdRoyaltyRate > 0
      ? (finalCogsPerCsCad * (1 + exportPercent)) / (1 - exportUsdRoyaltyRate)
      : 0;
  const exportMenaBaseCad =
    1 - exportMenaRoyaltyRate > 0
      ? (finalCogsPerCsCad * (1 + exportMenaPercent)) /
      (1 - exportMenaRoyaltyRate)
      : 0;
  const aedFxFromRates = Math.max(
    0,
    Number(
      (
        (Array.isArray(fxRates) ? fxRates : []).find(
          (row) =>
            String(row?.currency || "")
              .trim()
              .toUpperCase() === "AED",
        ) || {}
      ).bufferedFxVsCad,
    ) || 0,
  );
  const exportUsd = roundStandardSrpCad(exportUsdBaseCad * usdFxFromRates);
  // const exportMena = roundStandardSrpCad(exportMenaBaseCad * aedFxFromRates);
  const exportMena = roundStandardSrpCad(exportMenaBaseCad);

  // Calculate all background price levels in CAD to find the lowest price
  const candidatePricesCad = [
    Number(standardSrpCad) || 0,
    Number(wholesale1Cad) || 0,
    Number(wholesale2Cad) || 0,
    Number(wholesale3CadRounded) || 0,
    Number(distributorCad) || 0,
    usdFxFromRates > 0 && Number(exportUsd) > 0 ? Number(exportUsd) / usdFxFromRates : 0,
    Number(exportMena) || 0,
  ].filter((p) => p > 0);

  const lowestCalculatedPriceCad =
    candidatePricesCad.length > 0 ? Math.min(...candidatePricesCad) : 0;

  // Confidential price factor from global assumptions (e.g. 90% -> 0.90)
  const confidentialPriceFactor =
    Number(globalAssumptions.confidentialCostInflationPercent || 90) / 100;

  // When user cannot see detailed costs: we take the lowest price x factor (e.g. 90%)
  const confidentialFloorCostPerCsCad =
    lowestCalculatedPriceCad > 0 ? lowestCalculatedPriceCad * confidentialPriceFactor : finalCogsPerCsCad;

  const displayFinalCogsPerCsCad = canSeeDetailedCosts
    ? finalCogsPerCsCad
    : confidentialFloorCostPerCsCad;

  const summaryCostPerKg =
    !isBaseIngredient && masterCaseWeightKg > 0
      ? (Number(displayFinalCogsPerCsCad || 0) / masterCaseWeightKg).toFixed(3)
      : costPerKg;
  const summaryCostPerLbs =
    !isBaseIngredient && masterCaseWeightKg > 0
      ? (Number(displayFinalCogsPerCsCad || 0) / (masterCaseWeightKg * 2.20462)).toFixed(
        3,
      )
      : costPerLbs;
  const mfgCostPerKg =
    !isBaseIngredient && totalWeight > 0
      ? (Number(acctCostPerKg) + throughputCostPerUnit).toFixed(3)
      : "0.000";
  const marginPercentFromPrice = (price, fxRate = 1) => {
    const numericPrice = Number(price) || 0;
    const rate = Math.max(0, Number(fxRate) || 0);
    if (numericPrice <= 0) return null;
    const cadPrice = rate > 0 ? numericPrice / rate : 0;
    if (cadPrice <= 0) return null;
    return ((cadPrice - Number(finalCogsPerCsCad || 0)) / cadPrice) * 100;
  };
  const formatMarginPercent = (value) =>
    Number.isFinite(value) ? `${value.toFixed(2)}%` : "-";
  const wholesale1MarginPercent = marginPercentFromPrice(wholesale1Cad, 1);
  const wholesale2MarginPercent = marginPercentFromPrice(wholesale2Cad, 1);
  const wholesale3MarginPercent = marginPercentFromPrice(wholesale3CadRounded, 1);
  const webRetailMarginPercent = marginPercentFromPrice(standardSrpCad, 1);
  const distributorMarginPercent = marginPercentFromPrice(distributorCad, 1);
  const msrpCadMarginPercent = marginPercentFromPrice(product.msrpCad, 1);
  const msrpUsdMarginPercent = marginPercentFromPrice(product.msrpUsd, usdFxVsCad);
  const exportUsdMarginPercent = marginPercentFromPrice(exportUsd, usdFxFromRates);
  // const exportMenaMarginPercent = marginPercentFromPrice(exportMena, aedFxFromRates);
  const exportMenaMarginPercent = marginPercentFromPrice(exportMena, 1);

  useEffect(() => {
    if (product.isVariant || isBaseIngredient) return;

    const n = (value, digits = 6) => Number(Number(value || 0).toFixed(digits));
    let ingredientBaseRunning = 0;
    let categoryPremiumRunning = 0;
    let allergenPremiumRunning = 0;

    const recipeBuilderRows = ingredients.map((ing, index) => {
      const inputWeight = Number(ing.weight) || 0;
      const convertedWeightKg = convertToKg(ing.weight, ing.unit);
      const ratio = Number(ing.ratio) || 0;
      const ratioWeightKg = getRatioWeightKg(ing);
      const finalQtyKg = ratioWeightKg > 0 ? ratioWeightKg : convertedWeightKg;

      return {
        row: index + 1,
        id: ing.id,
        sku: ing.sku || "",
        name: ing.name || "",
        inputWeight: n(inputWeight),
        unit: ing.unit || "",
        convertedWeightKg: n(convertedWeightKg),
        ratioPercent: n(ratio),
        masterCaseWeightKg: n(masterCaseWeightKg),
        ratioWeightKg: n(ratioWeightKg),
        finalQtyKg: n(finalQtyKg),
        qtyUsedForCost:
          ratioWeightKg > 0
            ? "ratioWeightKg = ratioPercent / 100 * masterCaseWeightKg"
            : "convertedWeightKg = convertToKg(inputWeight, unit)",
        unitCost: n(ing.unitCost),
        currentLineTotalCost: n(ing.totalCost),
      };
    });

    const ingredientCalculationRows = ingredients
      .filter((ing) => ing?.sku || ing?.name || Number(ing?.weight) > 0)
      .map((ing, index) => {
        const skuKey = String(ing.sku || "").trim();
        const rmRow = rmDatabaseMap.get(skuKey);
        const ratioWeightKg = getRatioWeightKg(ing);
        const convertedWeightKg = convertToKg(ing.weight, ing.unit);
        const qtyKg = ratioWeightKg > 0 ? ratioWeightKg : convertedWeightKg;
        const unitCostKgCad = Number(ing.unitCost) || 0;
        const categoryPremiumKgCad = Number(rmRow?.categoryPremiumKgCad) || 0;
        const allergenPremiumKgCad = Number(rmRow?.allergenPremiumKgCad) || 0;
        const baseLineCad = qtyKg * unitCostKgCad;
        const categoryPremiumLineCad = qtyKg * categoryPremiumKgCad;
        const allergenPremiumLineCad = qtyKg * allergenPremiumKgCad;
        const lineTotalCad =
          baseLineCad + categoryPremiumLineCad + allergenPremiumLineCad;

        ingredientBaseRunning += baseLineCad;
        categoryPremiumRunning += categoryPremiumLineCad;
        allergenPremiumRunning += allergenPremiumLineCad;

        return {
          row: index + 1,
          sku: skuKey,
          name: ing.name || "",
          qtyKg: n(qtyKg),
          qtySource:
            ratioWeightKg > 0
              ? "ratio * masterCaseWeightKg"
              : "convertToKg(weight, unit)",
          unitCostKgCad: n(unitCostKgCad),
          unitCostSource: "ingredients API / BOM fallback -> ing.unitCost",
          rmMatched: Boolean(rmRow),
          rmModel: rmRow ? "Rmdatabase" : "-",
          rmMatch: rmRow ? "RMCode == ingredient.sku" : "no match",
          rmId: rmRow?._id || "-",
          rmName: rmRow?.RMName || "-",
          rmBaseCostKgCad: n(rmRow?.baseCostKgCad),
          rmTotalRMCost: n(rmRow?.TotalRMCost),
          categoryPremiumKgCad: n(categoryPremiumKgCad),
          categoryPremiumSource: rmRow
            ? "Rmdatabase.categoryPremiumKgCad"
            : "0 no RM match",
          allergenPremiumKgCad: n(allergenPremiumKgCad),
          allergenPremiumSource: rmRow
            ? "Rmdatabase.allergenPremiumKgCad"
            : "0 no RM match",
          ingredientCostFormula: `${n(qtyKg)} * ${n(unitCostKgCad)} = ${n(
            baseLineCad,
          )}`,
          categoryPremiumFormula: `${n(qtyKg)} * ${n(
            categoryPremiumKgCad,
          )} = ${n(categoryPremiumLineCad)}`,
          allergenPremiumFormula: `${n(qtyKg)} * ${n(
            allergenPremiumKgCad,
          )} = ${n(allergenPremiumLineCad)}`,
          fullLineFormula: `${n(baseLineCad)} + ${n(
            categoryPremiumLineCad,
          )} + ${n(allergenPremiumLineCad)} = ${n(lineTotalCad)}`,
          baseLineCad: n(baseLineCad),
          categoryPremiumLineCad: n(categoryPremiumLineCad),
          allergenPremiumLineCad: n(allergenPremiumLineCad),
          lineTotalCad: n(lineTotalCad),
          ingredientBaseRunning: n(ingredientBaseRunning),
          categoryPremiumRunning: n(categoryPremiumRunning),
          allergenPremiumRunning: n(allergenPremiumRunning),
        };
      });
    const rmPremiumPerRecipeRows = ingredientCalculationRows.map((row) => ({
      row: row.row,
      sku: row.sku,
      name: row.name,
      rmMatched: row.rmMatched,
      rmSource: row.rmMatched
        ? "Rmdatabase by RMCode == recipe SKU"
        : "No RM match, premiums use 0",
      qtyKg: row.qtyKg,
      baseCostKgCad: row.unitCostKgCad,
      categoryPremiumKgCad: row.categoryPremiumKgCad,
      allergenPremiumKgCad: row.allergenPremiumKgCad,
      totalRmCostKgCad: n(
        Number(row.unitCostKgCad || 0) +
        Number(row.categoryPremiumKgCad || 0) +
        Number(row.allergenPremiumKgCad || 0),
      ),
      totalRmCostKgFormula: `${row.unitCostKgCad} + ${row.categoryPremiumKgCad} + ${row.allergenPremiumKgCad}`,
      lineCostCad: row.lineTotalCad,
      lineCostFormula: `${row.qtyKg} * (${row.unitCostKgCad} + ${row.categoryPremiumKgCad} + ${row.allergenPremiumKgCad}) = ${row.lineTotalCad}`,
    }));
    const ingredientCost302Breakdown = ingredientCalculationRows.map((row) => {
      const sourceIngredient = ingredients.find(
        (ing) => String(ing.sku || "").trim() === row.sku,
      );
      const sourceRmRow = rmDatabaseMap.get(row.sku);
      return {
        row: row.row,
        sku: row.sku,
        skuSource:
          "CostingTool.ingredients[].sku, filled from getIngredients() selection or pasted SKU",
        name: row.name,
        nameSource:
          "CostingTool.ingredients[].name, filled from getIngredients() selection",
        qtyKg: row.qtyKg,
        qtyKgSource: row.qtySource,
        masterCaseWeightKg: n(masterCaseWeightKg),
        masterCaseWeightSource:
          Number(product.baseQuantity) > 0
            ? "product.baseQuantity"
            : "product.masterWeightKg fallback",
        inputWeight: n(sourceIngredient?.weight),
        inputUnit: sourceIngredient?.unit || "",
        ratioPercent: n(sourceIngredient?.ratio),
        unitCostKgCad: row.unitCostKgCad,
        unitCostSource:
          "CostingTool.ingredients[].unitCost from getIngredients() API",
        formula: `${row.qtyKg} * ${row.unitCostKgCad} = ${row.baseLineCad}`,
        lineIngredientCostCad: row.baseLineCad,
        runningIngredientCostTotal: row.ingredientBaseRunning,
        rmMatched: Boolean(sourceRmRow),
        rmSource:
          "Rmdatabase premiums are included in ingredientCostTotal via total RM cost/kg",
      };
    });

    console.groupCollapsed(
      `[ALL COSTING CALC] SKU=${product.sku || "-"} FinalCOGS=${n(
        finalCogsPerCsCad,
      )}`,
    );
    console.log("00 product state", {
      source: "CostingTool product state",
      product,
    });
    console.log("01 master inputs", {
      masterCaseWeightKg: n(masterCaseWeightKg),
      masterCaseFormula:
        "Number(product.baseQuantity) || Number(product.masterWeightKg) || 0",
      productBaseQuantity: n(product.baseQuantity),
      productMasterWeightKg: n(product.masterWeightKg),
      recipeBuilderTotalWeight: n(totalWeight),
      autoBaseQuantityFormula:
        "baseQuantity auto-set to converted KG totalWeight without rounding when product.type !== ingredient",
      ratioTotal: n(ratioTotal),
      actualIngredientCount,
      selectedCategory,
      selectedPackagingName,
      containsAllergen: product.containsAllergen,
      hasAllergenPremium,
    });
    console.log("01A recipe builder + master quantity", {
      source: "Recipe Builder rows in CostingTool ingredients state",
      masterQuantitySource:
        Number(product.baseQuantity) > 0
          ? "product.baseQuantity"
          : "product.masterWeightKg fallback",
      masterCaseWeightKg: n(masterCaseWeightKg),
      totalWeightFromRecipeBuilder: n(totalWeight),
      roundedTotalWeightUsedForBaseQuantity: Math.round(totalWeight || 0),
      differenceBetweenMasterAndRecipeWeight: n(masterCaseWeightKg - totalWeight),
      ratioTotal: n(ratioTotal),
      note:
        "If ratioWeightKg is greater than 0, costing uses ratio-based master quantity. Otherwise it uses converted input weight.",
    });
    console.table(recipeBuilderRows);
    console.log("01B RM premiums added per recipe ingredient", {
      formula:
        "lineCostCad = qtyKg * (baseCostKgCad + categoryPremiumKgCad + allergenPremiumKgCad)",
      source:
        "categoryPremiumKgCad/allergenPremiumKgCad come from Rmdatabase matched by RMCode == recipe ingredient SKU",
    });
    console.table(rmPremiumPerRecipeRows);
    console.table(ingredientCalculationRows);
    console.log("02A ingredientCostTotal 302/306 breakdown", {
      whatThisIs:
        "ingredientCostTotal now uses total RM cost/kg for each recipe row.",
      formula:
        rmPremiumPerRecipeRows
          .map((row) => row.lineCostCad)
          .join(" + ") + ` = ${n(ingredientCostTotal)}`,
      ingredientCostTotal: n(ingredientCostTotal),
      expectedIngredientCostTotal: 306,
      differenceFromExpected306: n(ingredientCostTotal - 306),
      masterCaseWeightKg: n(masterCaseWeightKg),
      ratioTotal: n(ratioTotal),
      note:
        "Check the table below: each line shows exactly what is being added into ingredientCostTotal.",
    });
    console.table(ingredientCost302Breakdown);
    console.log("02 ingredient totals", {
      ingredientCostTotal: {
        value: n(ingredientCostTotal),
        formula:
          "sum(qtyKg * (ing.unitCost + Rmdatabase.categoryPremiumKgCad + Rmdatabase.allergenPremiumKgCad))",
        expected: 306,
        differenceFromExpected306: n(ingredientCostTotal - 306),
        masterQuantityUsed: n(masterCaseWeightKg),
        recipeBuilderTotalWeight: n(totalWeight),
      },



      rmCategoryPremiumTotalCad: {
        value: n(rmCategoryPremiumTotalCad),
        formula: "sum(qtyKg * Rmdatabase.categoryPremiumKgCad)",
      },
      rmAllergenPremiumTotalCad: {
        value: n(rmAllergenPremiumTotalCad),
        formula: "sum(qtyKg * Rmdatabase.allergenPremiumKgCad)",
      },
      totalIngredientWithRmPremiums: n(
        ingredientCostTotal +
        rmCategoryPremiumTotalCad +
        rmAllergenPremiumTotalCad,
      ),
    });
    console.log("03 packaging / throughput / category premiums", {
      packagingCostNum: {
        value: n(packagingCostNum),
        source: "selected packagingCost state",
        addedPackaging,
      },
      throughputCostPerUnit: {
        value: n(throughputCostPerUnit),
        formula:
          "((overheadAllocationPercent * weeklyOverheadCostCad) + (labourAllocationPercent * weeklyLabourCostCad)) / throughputUnitsPerWeek",
        matchedThroughputRow,
        throughputUnitsPerDay: n(throughputUnitsPerDay),
        workingDaysPerWeek: n(workingDaysPerWeek),
        throughputUnitsPerWeek: n(throughputUnitsPerWeek),
        weeklyOverheadCostCad: n(weeklyOverheadCostCad),
        weeklyLabourCostCad: n(weeklyLabourCostCad),
      },
      signaturePremiumPerUnit: {
        value: n(signaturePremiumPerUnit),
        source: "CostingSettings.categoryRules.signaturePremiumPerUnit",
        selectedCategoryRule,
      },
      allergenPremiumPerUnit: {
        value: n(allergenPremiumPerUnit),
        source:
          "CostingSettings.categoryRules.allergenPremiumPerUnit when allergen applies",
      },
    });
    console.log("04 final COGS base", {
      formula:
        "ingredientCostTotal + packagingCostNum + throughputCostPerUnit + signaturePremiumPerUnit + allergenPremiumPerUnit",
      ingredientCostTotal: n(ingredientCostTotal),
      packagingCostNum: n(packagingCostNum),
      throughputCostPerUnit: n(throughputCostPerUnit),
      signaturePremiumPerUnit: n(signaturePremiumPerUnit),
      allergenPremiumPerUnit: n(allergenPremiumPerUnit),
      rmCategoryPremiumTotalCad: n(rmCategoryPremiumTotalCad),
      rmAllergenPremiumTotalCad: n(rmAllergenPremiumTotalCad),
      finalCogsBaseCad: n(finalCogsBaseCad),
    });
    console.log("05 multiplier and final COGS", {
      formula:
        "1 + lossBufferPercent/100 + earlyPaymentDeductionPercent/100 + cogsIncreaseBufferPercent/100",
      lossBufferPercent: n(globalAssumptions.lossBufferPercent),
      earlyPaymentDeductionPercent: n(
        globalAssumptions.earlyPaymentDeductionPercent,
      ),
      cogsIncreaseBufferPercent: n(globalAssumptions.cogsIncreaseBufferPercent),
      finalCogsMultiplier: n(finalCogsMultiplier),
      finalCogsPerCsCadFormula: "finalCogsBaseCad * finalCogsMultiplier",
      finalCogsPerCsCad: n(finalCogsPerCsCad),
      finalCogsPerCsCadDisplay: Number(Number(finalCogsPerCsCad || 0).toFixed(2)),
    });
    console.log("06 bulk / royalty / wholesale 3", {
      selectedBulkRule,
      grossMarginPercent: n(grossMarginPercent),
      grossMarginRate: n(grossMarginRate),
      paysRoyalty,
      royaltyPercent: n(royaltyPercent),
      royaltyRate: n(royaltyRate),
      wholesale3CadDenominator: n(wholesale3CadDenominator),
      wholesale3CadFormula: "finalCogsPerCsCad / denominator",
      wholesale3Cad: n(wholesale3Cad),
      wholesale3CadRounded: n(wholesale3CadRounded),
    });
    console.log("07 SRP / wholesale / distributor", {
      webPercent: n(webPercent),
      webRate: n(webRate),
      standardSrpCadRawFormula: "wholesale3Cad / (1 - webRate)",
      standardSrpCadRaw: n(standardSrpCadRaw),
      standardSrpCad: n(standardSrpCad),
      wholesale1Rate: n(wholesale1Rate),
      wholesale1Cad: n(wholesale1Cad),
      wholesale2Rate: n(wholesale2Rate),
      wholesale2Cad: n(wholesale2Cad),
      distributorRate: n(distributorRate),
      distributorCad: n(distributorCad),
    });
    console.log("08 FX / MSRP / export", {
      webRetailChannelRule,
      usdFxFromChannelRule: n(usdFxFromChannelRule),
      usdFxFromRates: n(usdFxFromRates),
      usdFxVsCad: n(usdFxVsCad),
      standardSrpUsd: n(standardSrpUsd),
      msrpCadPerKgRounded99: n(msrpCadPerKgRounded99),
      msrpCadMasterRounded99: n(msrpCadMasterRounded99),
      msrpUsdPerKgRounded99: n(msrpUsdPerKgRounded99),
      msrpUsdMasterRounded99: n(msrpUsdMasterRounded99),
      exportPercent: n(exportPercent),
      exportMenaPercent: n(exportMenaPercent),
      exportUsdRoyaltyRuleName,
      exportUsdRoyaltyRate: n(exportUsdRoyaltyRate),
      exportUsdBaseCad: n(exportUsdBaseCad),
      exportUsd: n(exportUsd),
      exportMenaRoyaltyRuleName,
      exportMenaRoyaltyRate: n(exportMenaRoyaltyRate),
      exportMenaBaseCad: n(exportMenaBaseCad),
      exportMena: n(exportMena),
      aedFxFromRates: n(aedFxFromRates),
    });
    console.log("09 cost per weight and margins", {
      summaryCostPerKg,
      summaryCostPerLbs,
      mfgCostPerKg,
      standardMarginPercent,
      wholesale1MarginPercent: formatMarginPercent(wholesale1MarginPercent),
      wholesale2MarginPercent: formatMarginPercent(wholesale2MarginPercent),
      wholesale3MarginPercent: formatMarginPercent(wholesale3MarginPercent),
      webRetailMarginPercent: formatMarginPercent(webRetailMarginPercent),
      distributorMarginPercent: formatMarginPercent(distributorMarginPercent),
      msrpCadMarginPercent: formatMarginPercent(msrpCadMarginPercent),
      msrpUsdMarginPercent: formatMarginPercent(msrpUsdMarginPercent),
      exportUsdMarginPercent: formatMarginPercent(exportUsdMarginPercent),
      exportMenaMarginPercent: formatMarginPercent(exportMenaMarginPercent),
    });
    console.groupEnd();
  }, [
    addedPackaging,
    aedFxFromRates,
    actualIngredientCount,
    allergenPremiumPerUnit,
    distributorCad,
    distributorMarginPercent,
    distributorRate,
    exportMena,
    exportMenaBaseCad,
    exportMenaMarginPercent,
    exportMenaPercent,
    exportMenaRoyaltyRate,
    exportMenaRoyaltyRuleName,
    exportPercent,
    exportUsd,
    exportUsdBaseCad,
    exportUsdMarginPercent,
    exportUsdRoyaltyRate,
    exportUsdRoyaltyRuleName,
    finalCogsBaseCad,
    finalCogsMultiplier,
    finalCogsPerCsCad,
    globalAssumptions,
    grossMarginPercent,
    grossMarginRate,
    hasAllergenPremium,
    ingredientCostTotal,
    ingredients,
    isBaseIngredient,
    masterCaseWeightKg,
    matchedThroughputRow,
    mfgCostPerKg,
    msrpCadMarginPercent,
    msrpCadMasterRounded99,
    msrpCadPerKgRounded99,
    msrpUsdMarginPercent,
    msrpUsdMasterRounded99,
    msrpUsdPerKgRounded99,
    packagingCostNum,
    paysRoyalty,
    product,
    rmAllergenPremiumTotalCad,
    rmCategoryPremiumTotalCad,
    rmDatabaseMap,
    royaltyPercent,
    royaltyRate,
    selectedBulkRule,
    selectedCategory,
    selectedCategoryRule,
    selectedPackagingName,
    signaturePremiumPerUnit,
    standardMarginPercent,
    standardSrpCad,
    standardSrpCadRaw,
    standardSrpUsd,
    summaryCostPerKg,
    summaryCostPerLbs,
    throughputCostPerUnit,
    throughputUnitsPerDay,
    throughputUnitsPerWeek,
    totalWeight,
    ratioTotal,
    usdFxFromChannelRule,
    usdFxFromRates,
    usdFxVsCad,
    webPercent,
    webRate,
    webRetailMarginPercent,
    webRetailChannelRule,
    weeklyLabourCostCad,
    weeklyOverheadCostCad,
    wholesale1Cad,
    wholesale1MarginPercent,
    wholesale1Rate,
    wholesale2Cad,
    wholesale2MarginPercent,
    wholesale2Rate,
    wholesale3Cad,
    wholesale3CadDenominator,
    wholesale3CadRounded,
    wholesale3MarginPercent,
    workingDaysPerWeek,
  ]);

  const baseWeightKgForMsrp =
    Number(product.netWeightGkKg) || Number(product.qtySku) || Number(product.netWeight1Kg) || Number(product.qtyUn) || 1;

  const msrpCadPerKg = isBaseIngredient
    ? (Number(product.msrpCad) / baseWeightKgForMsrp).toFixed(4)
    : masterCaseWeightKg > 0 && Number(product.msrpCad) > 0
    ? (Number(product.msrpCad) / masterCaseWeightKg).toFixed(4)
    : "0.000";

  const msrpUsdPerKg = isBaseIngredient
    ? (Number(product.msrpUsd) / baseWeightKgForMsrp).toFixed(3)
    : masterCaseWeightKg > 0 && Number(product.msrpUsd) > 0
    ? (Number(product.msrpUsd) / masterCaseWeightKg).toFixed(3)
    : "0.000";

  useEffect(() => {
    if (product.isVariant) return;
    const targetCad = isBaseIngredient
      ? standardSrpCad
      : msrpCadMasterRounded99;
    const nextMsrpCad = targetCad > 0 ? targetCad.toFixed(2) : "";
    const currentMsrpCad =
      product.msrpCad === null || product.msrpCad === undefined
        ? ""
        : String(product.msrpCad);
    if (currentMsrpCad === nextMsrpCad) return;
    setProduct((prev) => ({
      ...prev,
      msrpCad: nextMsrpCad,
    }));
  }, [
    product.isVariant,
    isBaseIngredient,
    standardSrpCad,
    msrpCadMasterRounded99,
    product.msrpCad,
    setProduct,
  ]);

  useEffect(() => {
    if (product.isVariant) return;
    const targetUsd = isBaseIngredient
      ? standardSrpUsd
      : msrpUsdMasterRounded99;
    const nextMsrpUsd = targetUsd > 0 ? targetUsd.toFixed(2) : "";
    const currentMsrpUsd =
      product.msrpUsd === null || product.msrpUsd === undefined
        ? ""
        : String(product.msrpUsd);
    if (currentMsrpUsd === nextMsrpUsd) return;
    setProduct((prev) => ({
      ...prev,
      msrpUsd: nextMsrpUsd,
    }));
  }, [
    product.isVariant,
    isBaseIngredient,
    standardSrpUsd,
    msrpUsdMasterRounded99,
    product.msrpUsd,
    setProduct,
  ]);

  /* -------------------- HANDLERS -------------------- */
  const [ingredientCount, setIngredientCount] = useState(1);
  const [debouncedSearchByIngredientId, setDebouncedSearchByIngredientId] =
    useState({});
  const [
    activeSuggestionIndexByIngredientId,
    setActiveSuggestionIndexByIngredientId,
  ] = useState({});
  const ingredientRowsWrapperRef = useRef(null);

  const addIngredient = () => {
    if (!ensureCostingWriteAccess("add ingredients")) return;
    const desiredTotal = Math.min(100, Math.max(1, ingredientCount));
    const currentTotal = ingredients.length;
    const ingredientsToAdd = Math.min(
      desiredTotal,
      Math.max(0, 50 - currentTotal),
    );

    if (ingredientsToAdd <= 0) return;

    const newIngredients = Array.from(
      { length: ingredientsToAdd },
      createEmptyIngredient,
    );

    setIngredients((prev) => [...prev, ...newIngredients]);
  };
  // const clearRecipeIngredients = () => {
  //   if (!ensureCostingWriteAccess("clear ingredients")) return;
  //   setIngredients([createEmptyIngredient(), createEmptyIngredient()]);
  //   setIngredientCount(1);
  //   setFocusedIngredientId(null);
  // };


  const clearRecipeIngredients = () => {
    if (!ensureCostingWriteAccess("clear ingredients")) return;
    setIngredients([createEmptyIngredient(), createEmptyIngredient()]);
    setIngredientCount(1);
    setFocusedIngredientId(null);
    setProduct((prev) => ({
      ...prev,
      baseQuantity: "",
    }));
  };


  const updateAllUnits = (unit) => {
    setGlobalUnit(unit);
    setIngredients((prev) =>
      prev.map((ing) => ({
        ...ing,
        unit,
      })),
    );
  };

  useEffect(() => {
    setIngredients((prev) => {
      let next = prev.map((ing) => ({
        ...ing,
        ratio: roundRatioValue(ing.ratio),
      }));
      next = capRatiosToMax(next);
      if (masterCaseWeightKg <= 0) return next;
      return next.map((ing) => {
        const ratio = toRatioNumber(ing.ratio);
        const newWeight = ratio > 0 ? (ratio / 100) * masterCaseWeightKg : 0;
        return {
          ...ing,
          weight: newWeight > 0 ? Number(newWeight.toFixed(4)) : "",
        };
      });
    });
  }, [ratioRoundingStep]);

  const removeIngredient = (id) => {
    if (!ensureCostingWriteAccess("remove ingredients")) return;
    if (ingredients.length > 2) {
      setIngredients(ingredients.filter((i) => i.id !== id));
    }
  };

  const normalizedIngredientsSource = useMemo(
    () =>
      ingredientsList.map((item) => ({
        ...item,
        _searchName: String(item.name || item.customFields?.nameEn || "")
          .trim()
          .toLowerCase(),
        _searchSku: String(item.sku || "")
          .trim()
          .toLowerCase(),
      })),
    [ingredientsList],
  );

  useEffect(() => {
    if (!focusedIngredientId) return;
    const focusedIngredient = ingredients.find(
      (ing) => ing.id === focusedIngredientId,
    );
    const liveQuery = String(focusedIngredient?.name || "")
      .trim()
      .toLowerCase();

    const timeoutId = setTimeout(() => {
      setDebouncedSearchByIngredientId((prev) => ({
        ...prev,
        [focusedIngredientId]: liveQuery,
      }));
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [focusedIngredientId, ingredients]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        ingredientRowsWrapperRef.current &&
        !ingredientRowsWrapperRef.current.contains(event.target)
      ) {
        setFocusedIngredientId(null);
        setActiveSuggestionIndexByIngredientId({});
      }
    };

    document.addEventListener("click", handleOutsideClick);


    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const getIngredientSuggestions = (ingredientId, fallbackQuery) => {
    const query = String(
      debouncedSearchByIngredientId[ingredientId] ?? fallbackQuery ?? "",
    )
      .trim()
      .toLowerCase();

    if (!query) return [];

    return normalizedIngredientsSource
      .filter(
        (item) =>
          item._searchName.includes(query) || item._searchSku.includes(query),
      )
      .slice(0, 30);
  };

  const resolveIngredientCostWithBomFallback = useCallback(
    async (ingredientId, sku, baseCost) => {
      const normalizedSku = String(sku || "").trim();
      if (!ingredientId || !normalizedSku || Number(baseCost) > 0) return;

      const nextRequestId =
        (bomCostRequestIdByIngredientRef.current[ingredientId] || 0) + 1;
      bomCostRequestIdByIngredientRef.current[ingredientId] = nextRequestId;

      const bomCost = await getBomEstimatedCost(normalizedSku, {
        sku: normalizedSku,
        onNotFound: null,
      });

      if (
        bomCostRequestIdByIngredientRef.current[ingredientId] !== nextRequestId ||
        Number(bomCost) <= 0
      ) {
        return;
      }

      setIngredients((prev) =>
        prev.map((ing) =>
          ing.id === ingredientId &&
            String(ing.sku || "").trim().toUpperCase() ===
            normalizedSku.toUpperCase()
            ? {
              ...ing,
              unitCost: Number(bomCost).toFixed(2),
            }
            : ing,
        ),
      );
    },
    [],
  );

  const applyIngredientSuggestion = (ingredientId, item) => {
    const rawUom =
      item.uom || item.customFields?.un || item.customFields?.gKg || "";
    const normalizedUom = rawUom.trim().toLowerCase();
    const validUnits = ["g", "kg", "ml", "l", "un."];
    const finalUnit = validUnits.includes(normalizedUom) ? normalizedUom : "kg";
    const rmRow = rmDatabaseMap.get(String(item.sku || "").trim());
    const baseCostFromIng = Number(item.unitCost) || 0;
    const baseCostFromRm = Number(rmRow?.baseCostKgCad) || Number(rmRow?.TotalRMCost) || 0;
    const resolvedCost = baseCostFromIng > 0 ? baseCostFromIng : baseCostFromRm;

    setIngredients((prev) =>
      prev.map((ing) =>
        ing.id === ingredientId
          ? {
            ...ing,
            name: item.name || item.customFields?.nameEn || "",
            nameFr: item.customFields?.nameFr || "",
            sku: item.sku || "",
            unitCost: Number(resolvedCost).toFixed(2),
            unit: finalUnit,
          }
          : ing,
      ),
    );

    setFocusedIngredientId(null);
    setActiveSuggestionIndexByIngredientId((prev) => ({
      ...prev,
      [ingredientId]: -1,
    }));

    resolveIngredientCostWithBomFallback(
      ingredientId,
      item.sku || "",
      resolvedCost,
    );
  };

  const handleIngredientNameKeyDown = (event, ingredientId, suggestions) => {
    if (!suggestions.length) return;
    const currentIndex =
      activeSuggestionIndexByIngredientId[ingredientId] ?? -1;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndexByIngredientId((prev) => ({
        ...prev,
        [ingredientId]: Math.min(currentIndex + 1, suggestions.length - 1),
      }));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndexByIngredientId((prev) => ({
        ...prev,
        [ingredientId]: Math.max(currentIndex - 1, 0),
      }));
      return;
    }

    if (
      event.key === "Enter" &&
      currentIndex >= 0 &&
      suggestions[currentIndex]
    ) {
      event.preventDefault();
      applyIngredientSuggestion(ingredientId, suggestions[currentIndex]);
      return;
    }

    if (event.key === "Escape") {
      setFocusedIngredientId(null);
      setActiveSuggestionIndexByIngredientId((prev) => ({
        ...prev,
        [ingredientId]: -1,
      }));
    }
  };

  const updateIngredient = (id, field, value) => {
    if (!canWriteCostingTool) return;
    const normalizedInputSku =
      field === "sku"
        ? String(value || "")
          .trim()
          .toUpperCase()
        : "";
    const matchedIngredientBySku =
      field === "sku" && normalizedInputSku
        ? ingredientsList.find(
          (item) =>
            String(item.sku || "").toUpperCase() === normalizedInputSku,
        )
        : null;
    const pendingBomCostLookup =
      matchedIngredientBySku &&
        (Number(matchedIngredientBySku.unitCost) || 0) === 0
        ? {
          ingredientId: id,
          sku: matchedIngredientBySku.sku || normalizedInputSku,
          baseCost: 0,
        }
        : null;
    setIngredients((prev) => {
      const currentIngredient = prev.find((ing) => ing.id === id);
      if (!currentIngredient) return prev;
      if (String(currentIngredient[field] ?? "") === String(value ?? "")) {
        return prev;
      }

      let updatedIngredients = prev.map((ing) =>
        ing.id === id ? { ...ing, [field]: value } : ing,
      );

      // Handle SKU field changes (existing logic - unchanged)
      if (field === "sku") {
        const target = updatedIngredients.find((ing) => ing.id === id);

        if (normalizedInputSku === "") {
          target.name = "";
          target.unitCost = "";
          target.unit = "kg";
        } else {
          const found = matchedIngredientBySku;

          if (found) {
            target.name = found.name || found.customFields?.nameEn || "";
            target.nameFr = found.customFields?.nameFr || "";

            target.sku = found.sku;
            const rmRow = rmDatabaseMap.get(String(found.sku || "").trim());
            const baseCostFromIng = Number(found.unitCost) || 0;
            const baseCostFromRm = Number(rmRow?.baseCostKgCad) || Number(rmRow?.TotalRMCost) || 0;
            const resolvedCost = baseCostFromIng > 0 ? baseCostFromIng : baseCostFromRm;
            target.unitCost = Number(resolvedCost).toFixed(2);

            const rawUom =
              found.uom ||
              found.customFields?.un ||
              found.customFields?.gKg ||
              "";
            const normalizedUom = rawUom.trim().toLowerCase();
            const validUnits = ["g", "kg", "ml", "l", "un."];
            target.unit = validUnits.includes(normalizedUom)
              ? normalizedUom
              : "kg";
          } else {
            const rmRow = rmDatabaseMap.get(normalizedInputSku);
            if (rmRow) {
              target.name = rmRow.RMName || "";
              const baseCostFromRm = Number(rmRow.baseCostKgCad) || Number(rmRow.TotalRMCost) || 0;
              target.unitCost = Number(baseCostFromRm).toFixed(2);
              target.unit = "kg";
            } else {
              target.name = "";
              target.unitCost = "";
              target.unit = "kg";
            }
          }
        }
      }

      // Handle manual name clear (existing logic - unchanged)
      if (field === "name") {
        const trimmedName = value.trim();
        if (trimmedName === "") {
          const target = updatedIngredients.find((ing) => ing.id === id);
          target.sku = "";
          target.unitCost = "";
          target.unit = "kg";
        }
      }

      // === RECALCULATE WEIGHTS & RATIOS (your existing logic - unchanged) ===
      const totalWeight = updatedIngredients.reduce(
        (sum, ing) => sum + convertToKg(ing.weight, ing.unit),
        0,
      );

      if (field === "weight") {
        updatedIngredients.forEach((ing) => {
          const w = convertToKg(ing.weight, ing.unit);
          ing.ratio =
            totalWeight > 0 ? roundRatioValue((w / totalWeight) * 100) : 0;
        });
        updatedIngredients = rebalanceRatiosToHundred(updatedIngredients, id);
        updatedIngredients = capRatiosToMax(updatedIngredients, id);
      } else if (field === "ratio") {
        const newRatio = roundRatioValue(value);
        const targetIng = updatedIngredients.find((ing) => ing.id === id);
        if (targetIng) {
          targetIng.ratio = newRatio;
        }

        const ratioSum = updatedIngredients.reduce(
          (sum, ing) => sum + (Number(ing.ratio) || 0),
          0,
        );
        if (ratioSum > 100) {
          toast.error("Total ratio exceeds 100%!");
        }

        if (masterCaseWeightKg > 0) {
          updatedIngredients.forEach((ing) => {
            const ratio = toRatioNumber(ing.ratio);
            const newWeight =
              ratio > 0 ? (ratio / 100) * masterCaseWeightKg : 0;
            ing.weight = newWeight > 0 ? Number(newWeight.toFixed(4)) : "";
          });
        } else if (targetIng) {
          const ratio = toRatioNumber(targetIng.ratio);
          const newWeight = ratio > 0 ? (ratio / 100) * totalWeight : 0;
          targetIng.weight = newWeight > 0 ? Number(newWeight.toFixed(4)) : "";
        }
      }

      return updatedIngredients;
    });

    if (pendingBomCostLookup) {
      resolveIngredientCostWithBomFallback(
        pendingBomCostLookup.ingredientId,
        pendingBomCostLookup.sku,
        pendingBomCostLookup.baseCost,
      );
    }
  };

  const clearIngredientNameTimer = (id) => {
    const timer = ingredientNameTimersRef.current[id];
    if (timer) {
      clearTimeout(timer);
      delete ingredientNameTimersRef.current[id];
    }
  };

  const clearIngredientNameDraft = (id) => {
    setIngredientNameDrafts((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const scheduleIngredientNameUpdate = (id, value) => {
    setIngredientNameDrafts((prev) => ({ ...prev, [id]: value }));
    clearIngredientNameTimer(id);
    ingredientNameTimersRef.current[id] = setTimeout(() => {
      updateIngredient(id, "name", value);
      clearIngredientNameDraft(id);
      clearIngredientNameTimer(id);
    }, 250);
  };

  const commitIngredientNameUpdate = (id, value) => {
    clearIngredientNameTimer(id);
    updateIngredient(id, "name", value);
    clearIngredientNameDraft(id);
  };

  const setIngredientRatioDraft = (id, value) => {
    setIngredientRatioDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const clearIngredientRatioDraft = (id) => {
    setIngredientRatioDrafts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleIngredientRatioChange = (id, value) => {
    setIngredientRatioDraft(id, value);
  };

  const commitIngredientRatioUpdate = (id, value) => {
    updateIngredient(id, "ratio", value);
    clearIngredientRatioDraft(id);
  };

  useEffect(() => {
    return () => {
      Object.values(ingredientNameTimersRef.current).forEach((timer) =>
        clearTimeout(timer),
      );
      ingredientNameTimersRef.current = {};
    };
  }, []);
  function convertToKg(value, unit) {
    const v = Number(value) || 0;
    switch (unit) {
      case "g":
        return v / 1000;
      case "kg":
        return v;
      case "ml":
        return v / 1000;
      case "l":
        return v;
      case "un.":
        return 0;
      default:
        return 0;
    }
  }

  const handlePaste = async (e, startIndex, field) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData("text");
    const rawValues = clipboardData.split(/[\n\r\t,\s]+/);
    const trimmedValues = rawValues.map((val) => val.trim()).filter(Boolean);
    let values = trimmedValues;

    if (field === "sku") {
      const { validSkus, invalidSkus } = parseSkuInput(clipboardData, {
        splitPattern: /[\n\r\t,\s]+/,
        dedupe: false,
      });
      values = validSkus;
      if (invalidSkus.length > 0) {
        toast.error(buildInvalidSkuMessage(invalidSkus.length));
      }
    }

    if (values.length === 0) return;

    let newIngredients = [...ingredients];

    for (let i = 0; i < values.length; i++) {
      const idx = startIndex + i;

      if (idx >= newIngredients.length) {
        newIngredients.push(createEmptyIngredient());
      }

      const val = values[i];
      const ing = { ...newIngredients[idx] };

      if (field === "sku") {
        ing.sku = val;

        const trimmedSku = val.trim().toUpperCase();

        const found = ingredientsList.find(
          (item) => String(item.sku || "").toUpperCase() === trimmedSku,
        );

        if (found) {
          ing.name = found.name || found.customFields?.nameEn || "";
          ing.nameFr = found.customFields?.nameFr || "";
          ing.sku = found.sku;

          const rmRow = rmDatabaseMap.get(String(found.sku || "").trim());
          const baseCostFromIng = Number(found.unitCost) || 0;
          const baseCostFromRm = Number(rmRow?.baseCostKgCad) || Number(rmRow?.TotalRMCost) || 0;
          const resolvedBase = baseCostFromIng > 0 ? baseCostFromIng : baseCostFromRm;

          const resolvedCost =
            resolvedBase > 0
              ? resolvedBase
              : await getBomEstimatedCost(found.sku || trimmedSku, {
                sku: found.sku || trimmedSku,
                onNotFound: (message) => toast.error(message),
              });
          ing.unitCost = Number(resolvedCost || 0).toFixed(2);

          const rawUom =
            found.uom ||
            found.customFields?.un ||
            found.customFields?.gKg ||
            "";

          const normalizedUom = rawUom.trim().toLowerCase();

          const validUnits = ["g", "kg", "ml", "l", "un."];

          ing.unit = validUnits.includes(normalizedUom) ? normalizedUom : "kg";
        } else {
          const rmRow = rmDatabaseMap.get(trimmedSku);
          if (rmRow) {
            ing.name = rmRow.RMName || "";
            const baseCostFromRm = Number(rmRow.baseCostKgCad) || Number(rmRow.TotalRMCost) || 0;
            ing.unitCost = Number(baseCostFromRm).toFixed(2);
            ing.unit = "kg";
          }
        }
      } else if (field === "weight") {
        ing.weight = val;
      } else if (field === "ratio") {
        ing.ratio = roundRatioValue(val);
      }

      newIngredients[idx] = ing;
    }

    // Recalculate ratios or weights depending on the pasted field
    const totalWeight = newIngredients.reduce(
      (sum, ing) => sum + convertToKg(ing.weight, ing.unit),
      0,
    );
    const lastTouchedIndex = Math.min(
      startIndex + values.length - 1,
      newIngredients.length - 1,
    );
    const lastTouchedId = newIngredients[lastTouchedIndex]?.id || null;

    if (field === "weight") {
      if (totalWeight > 0) {
        newIngredients.forEach((ing) => {
          const w = convertToKg(ing.weight, ing.unit);
          ing.ratio = roundRatioValue((w / totalWeight) * 100);
        });
        newIngredients = rebalanceRatiosToHundred(
          newIngredients,
          lastTouchedId,
        );
      } else {
        newIngredients.forEach((ing) => {
          ing.ratio = 0;
        });
      }
      newIngredients = capRatiosToMax(newIngredients, lastTouchedId);
    } else if (field === "ratio") {
      const baseTotal = masterCaseWeightKg;
      if (baseTotal > 0) {
        newIngredients.forEach((ing) => {
          const ratio = toRatioNumber(ing.ratio);
          if (ratio > 0) {
            const newWeight = (ratio / 100) * baseTotal;
            ing.weight = Number(newWeight.toFixed(4));
          } else {
            ing.weight = "";
          }
        });
      }
      const ratioSum = newIngredients.reduce(
        (sum, ing) => sum + (Number(ing.ratio) || 0),
        0,
      );
      if (ratioSum > 100) {
        toast.error("Total ratio exceeds 100%!");
      }
    }

    // Recalculate costs
    newIngredients.forEach((ing) => {
      const qtyKg = getIngredientQtyKg(ing);
      const {
        categoryPremiumKgCad,
        allergenPremiumKgCad,
        totalCostKgCad,
      } = getIngredientCostParts(ing);
      ing.categoryPremiumKgCad = categoryPremiumKgCad;
      ing.allergenPremiumKgCad = allergenPremiumKgCad;
      ing.totalCostKgCad = totalCostKgCad;
      ing.totalCost = qtyKg * totalCostKgCad;
    });

    if (field === "sku") {
      newIngredients = newIngredients.filter(
        (ing) => ing.sku && ing.sku.trim() !== "",
      );

      if (newIngredients.length === 0) {
        newIngredients = [createEmptyIngredient()];
      }
    }

    setIngredients(newIngredients);
  };

  const getRecipeColumnValues = (columnKey) => {
    return ingredients.map((ing) => {
      switch (columnKey) {
        case "name":
          return String(ing.name || "");
        case "sku":
          return String(ing.sku || "");
        case "weight":
          return String(ing.weight ?? "");
        case "unit":
          return String(ing.unit || "");
        case "ratio":
          return formatRatioDisplay(ing.ratio) || "";
        case "masterCaseWeightKg":
          return getRatioWeightKg(ing) > 0 ? getRatioWeightKg(ing).toFixed(4) : "";
        case "unitCost":
          return String(ing.unitCost ?? "");
        case "totalCost":
          return Number(ing.totalCost || 0).toFixed(4);
        default:
          return "";
      }
    });
  };

  const handleCopyRecipeColumn = async (columnKey) => {
    if (!canSeeDetailedCosts && (columnKey === "unitCost" || columnKey === "totalCost")) {
      toast.error("Cost details are confidential.");
      return;
    }
    const values = getRecipeColumnValues(columnKey);
    const dataToCopy = values.join("\r\n");

    if (!dataToCopy.trim()) {
      toast.error("Nothing to copy in this column.");
      return;
    }

    try {
      await navigator.clipboard.writeText(dataToCopy);
      setCopiedRecipeColumn(columnKey);
      toast.success("Column copied.");
    } catch (err) {
      toast.error("Failed to copy column.");
    }
  };

  useEffect(() => {
    if (!copiedRecipeColumn) return undefined;
    const timer = setTimeout(() => {
      setCopiedRecipeColumn(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copiedRecipeColumn]);

  const addToDatabase = async () => {
    if (!ensureCostingWriteAccess("save recipes")) return { success: false };
    try {
      let rowsForSave = barcodeSecondUomRows;
      if (!product.isVariant && rowsForSave.length === 0 && product.sku) {
        rowsForSave = await createBarcodeRowsForBase(product);
        setBarcodeSecondUomRows(rowsForSave);
      }

      const variantRows = Array.isArray(product.variantRowsForSave)
        ? product.variantRowsForSave
        : [];

      if (product.isVariant) {
        const validVariants = variantRows.filter(
          (row) => row?.sku && row?.baseSku && row?.nameEn,
        );
        if (!validVariants.length) {
          toast.error(
            "Generate at least one variant SKU before saving variant data",
          );
          return { success: false };
        }

        const variantPayload = {
          skuGenerated: true,
          variants: validVariants.map((row) => ({
            sku: String(row.sku || "").trim(),
            baseSku: String(row.baseSku || "").trim(),
            nameEn: String(row.nameEn || "")
              .trim()
              .toUpperCase(),
            nameFr: String(row.nameFr || "")
              .trim()
              .toUpperCase(),
            descriptionEn: String(row.descriptionEn || "").trim(),
            descriptionFr: String(row.descriptionFr || "").trim(),
            upc: String(row.upc || "").trim(),
            category: String(row.category || "").trim(),
            isMasterCase: Boolean(row.isMasterCase),
            actualWeight: Number(row.actualWeight) || 0,
            netWeightKg: Number(row.netWeightKg || row.actualWeight) || 0,
            countryOfOrigin: String(row.countryOfOrigin || "").trim(),
            harmonizedTariff: String(row.harmonizedTariff || "").trim(),
            fdaCode: String(row.fdaCode || "").trim(),
            expiryDays: String(row.expiryDays || "").trim(),
            manualDate: String(row.manualDate || "").trim(),
            mfgCostPerKg: Number(row.mfgCostPerKg) || 0,
            packaging: Array.isArray(row.packaging) ? row.packaging : [],
            customFields: {
              nameEn: String(row.nameEn || "")
                .trim()
                .toUpperCase(),
              nameFr: String(row.nameFr || "")
                .trim()
                .toUpperCase(),
              rawMaterialSku: String(row.baseSku || "").trim(),
              fdaCode: String(row.fdaCode || "").trim(),
              expiryDays: String(row.expiryDays || "").trim(),
              manualDate: String(row.manualDate || "").trim(),
              netWeightGKg: String(
                Number(row.actualWeight || row.netWeightKg) || "",
              ),
              gKg: "kg",
            },
          })),
        };

        const variantResponse = await saveRecipeToDatabase(variantPayload);
        if (variantResponse?.data?.success === false) {
          throw new Error(
            variantResponse.data.error ||
            variantResponse.data.message ||
            "Variant save failed",
          );
        }

        setProduct((prev) => ({ ...prev, isEdit: true }));
        toast.success("Variants saved to database successfully!");
        return { success: true };
      }

      const payload = {
        sku: product.sku?.trim() || "",
        skuGenerated: Boolean(product.skuGenerated),
        description: product.descriptionEn?.trim() || "",
        type: String(product.type || "regular"),
        category:
          product.itemCategory || product.categorie || "Spice Blends VRAC",
        customerName: String(product.customerName || "").trim(),
        trademark: String(product.trademark || "").trim(),
        customPrefix: String(product.customPrefix || "").trim(),
        uom: product.uom || "kg",
        countryOfOrigin: product.countryOfOrigin || "Canada",
        harmonizedTariff: product.harmonizedTariff || "",
        eanUpc: product.upc || "",
        backOrder: product.backOrder || "",
        msrpCad: Number(product.msrpCad) || 0,
        msrpUsd: Number(product.msrpUsd) || 0,
        sellingPrice: Number(product.defaultPrice) || 0,
        unitCost: isBaseIngredient
          ? baseIngredientCostPerKg
          : (Number(product.purchasePrice) || Number(acctCostPerKg) || 0),

        customFields: {
          nameEn: product.nameEn?.trim().toUpperCase() || "",
          nameFr: product.nameFr?.trim().toUpperCase() || "",
          nameEnCustom: product.nameEnCustom?.trim() || "",
          nameFrCustom: product.nameFrCustom?.trim() || "",
          fdaCode: product.fdaCode?.trim() || "",
          fr: (product.fr?.trim() || buildFrDescription(product) || "").trim(),
          ingredients: product.ingredientsEn?.trim() || "",
          ingrédients: product.ingredientsFr?.trim() || "",
          allergens: product.allergensEn?.trim() || "",
          allergènes: product.allergensFr?.trim() || "",
          rawMaterialSku: product.rawMaterialSku || product.sku || "",
          sku: "",
          qtyUn: product.qtySku || "",
          un: product.uom || "kg",
          netWeightGKg: product.netWeightGkKg || "",
          gKg: product.gkKgUnit || "kg",
          netWeightOzLbs: product.netWeightOzLbs || "",
          oldCode: "",
          dbmCode: "",
          catégorie: product.categorie || "",
          new: "",
          labelCategory: product.itemCategory || "",
          customerName: String(product.customerName || "").trim(),
          trademark: String(product.trademark || "").trim(),
          customPrefix: String(product.customPrefix || "").trim(),
        },

        active: true,
        itemType: "Product",
        hasSerialOrLot: false,
        inventoryQuantity: 0,
        suppliers: {
          supplier: String(product.supplier || "").trim(),
          purchasePrice: isBaseIngredient
            ? baseIngredientCostPerKg
            : (Number(product.purchasePrice) || 0),
          landingCost: Number(product.landingCost) || 0,
          purchaseFxPrice: 0,
          shippingDays: Number(product.shippingDays) || 0,
          minimumOrderQuantity: Number(product.minimumOrderQuantity) || 0,
          orderStepQuantity: Number(product.orderStepQuantity) || 0,
          currency: "USD",
          lastUpdated: new Date().toISOString(),
        },
        ingredients: ingredients.map((ing) => ({
          sku: ing.sku?.trim() || "",
          name: ing.name?.trim() || "",
          weight: Number(ing.weight) || 0,
          unit: ing.unit || "kg",
          ratio: Number(ing.ratio) || 0,
          unitCost: Number(ing.unitCost) || 0,
          totalCost: Number(ing.totalCost) || 0,
        })),

        costing: {
          ingredientCostTotal: Number(ingredientCostTotal) || 0,
          packagingCost: Number(packagingCost) || 0,
          acctCostPerKg: Number(acctCostPerKg) || 0,
          mfgCostPerKg: Number(mfgCostPerKg) || 0,
        },

        packaging: addedPackaging
          ? [
            {
              packagingVariantId: addedPackaging._id,
              name: addedPackaging.name,
              format: addedPackaging.format,
              size: addedPackaging.size,
              ti: addedPackaging.ti,
              hi: addedPackaging.hi,
              items:
                addedPackaging.items?.map((item) => ({
                  sku: item.sku,
                  name: item.name,
                  qty: Number(item.qty) || 1,
                  unitCost: Number(item.unitCost) || 0,
                })) || [],
            },
          ]
          : [],
        barcodeSecondUom:
          !product.isVariant && Array.isArray(rowsForSave)
            ? rowsForSave.map((row) => ({
              eanUpc: String(row.eanUpc || "").trim(),
              secondUom: String(row.secondUom || "").trim() || "kg",
              quantityDefault: Number(row.quantityDefault) || 0,
              description: String(row.description || "").trim(),
              type: String(row.type || "UPCA"),
              labelPrinting: String(row.labelPrinting || "Per quantity"),
            }))
            : [],

        priceManagementData: {
          finalCogsPerCsCad: Number(finalCogsPerCsCad) || 0,
          throughputCost: Number(throughputCostPerUnit) || 0,
          costPerKgCad: Number(summaryCostPerKg) || 0,
          costPerLbsCad: Number(summaryCostPerLbs) || 0,
          pricingDetails: [
            {
              metric: "Web Retail CAD",
              masterPrice: Number(standardSrpCad) || 0,
              pricePerKg: Number(pricePerKg(standardSrpCad)) || 0,
              pricePerLbs: Number(pricePerLb(standardSrpCad)) || 0,
              marginPercentage: Number(webRetailMarginPercent) || 0
            },
            {
              metric: "Wholesale 1 CAD",
              masterPrice: Number(wholesale1Cad) || 0,
              pricePerKg: Number(pricePerKg(wholesale1Cad)) || 0,
              pricePerLbs: Number(pricePerLb(wholesale1Cad)) || 0,
              marginPercentage: Number(wholesale1MarginPercent) || 0
            },
            {
              metric: "Wholesale 2 CAD",
              masterPrice: Number(wholesale2Cad) || 0,
              pricePerKg: Number(pricePerKg(wholesale2Cad)) || 0,
              pricePerLbs: Number(pricePerLb(wholesale2Cad)) || 0,
              marginPercentage: Number(wholesale2MarginPercent) || 0
            },
            {
              metric: "Wholesale 3 CAD",
              masterPrice: Number(wholesale3CadRounded) || 0,
              pricePerKg: Number(pricePerKg(wholesale3CadRounded)) || 0,
              pricePerLbs: Number(pricePerLb(wholesale3CadRounded)) || 0,
              marginPercentage: Number(wholesale3MarginPercent) || 0
            },
            {
              metric: "Standard SRP",
              masterPrice: Number(standardSrpCad) || 0,
              pricePerKg: Number(pricePerKg(standardSrpCad)) || 0,
              pricePerLbs: Number(pricePerLb(standardSrpCad)) || 0,
              marginPercentage: Number(standardMarginPercent) || 0
            },
            {
              metric: "Distributor CAD",
              masterPrice: Number(distributorCad) || 0,
              pricePerKg: Number(pricePerKg(distributorCad)) || 0,
              pricePerLbs: Number(pricePerLb(distributorCad)) || 0,
              marginPercentage: Number(distributorMarginPercent) || 0
            },
            {
              metric: "Export USD",
              masterPrice: Number(exportUsd) || 0,
              pricePerKg: Number(pricePerKg(exportUsd)) || 0,
              pricePerLbs: Number(pricePerLb(exportUsd)) || 0,
              marginPercentage: Number(exportUsdMarginPercent) || 0
            },
            {
              metric: "Export MENA",
              masterPrice: Number(exportMena) || 0,
              pricePerKg: Number(pricePerKg(exportMena)) || 0,
              pricePerLbs: Number(pricePerLb(exportMena)) || 0,
              marginPercentage: Number(exportMenaMarginPercent) || 0
            }
          ]
        }
      };

      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === undefined ||
          payload[key] === null ||
          (typeof payload[key] === "string" && !payload[key].trim())
        ) {
          delete payload[key];
        }
      });

      // For nested objects too
      if (Object.keys(payload.customFields || {}).length === 0) {
        delete payload.customFields;
      }

      payload.rawSnapshot = buildCurrentSnapshot();
      payload.isEdit = Boolean(
        product.isEdit ||
        product.inErpag ||
        searchParams.get("edit_sku")
      );

      const response = await saveRecipeToDatabase(payload);

      if (response?.data?.success === false) {
        throw new Error(
          response.data.error || response.data.message || "Save failed",
        );
      }

      setProduct((prev) => ({
        ...prev,
        isEdit: true,
      }));

      if (!product.isVariant) {
        setHasGeneratedButUnsavedBase(false);
      }

      void clearSavedDraft();
      toast.success("Recipe saved to database successfully!");
      return { success: true };
    } catch (err) {
      console.error("Error saving recipe:", err);
      toast.error(err.message || "Failed to save recipe to database");
      return { success: false };
    }
    
  };

  const handleSyncToErpag = async () => {
    if (!ensureCostingWriteAccess("sync recipe to ERPAG")) return;

    if (!product.sku?.trim()) {
      toast.error("Please generate or select a SKU before syncing to ERPAG");
      return;
    }

    if (!product.nameEn?.trim()) {
      toast.error("Product English Name (Name EN) is required for ERPAG sync");
      return;
    }

    setIsSyncingErpag(true);
    const loadingToast = toast.loading(`Syncing SKU ${product.sku.trim()} to ERPAG...`);

    try {
      // 1. Ensure latest recipe state is saved in the database
      const saveRes = await addToDatabase();
      if (!saveRes?.success) {
        toast.dismiss(loadingToast);
        setIsSyncingErpag(false);
        toast.error("Could not save recipe before syncing to ERPAG.");
        return;
      }

      setProduct((prev) => ({ ...prev, isEdit: true, inErpag: true }));

      const currentFr = (product.fr?.trim() || buildFrDescription(product) || "").trim();
      const currentCategory = product.itemCategory || product.categorie || product.category || "Spice Blends VRAC";
      const currentMsrpCad = Number(product.msrpCad) || Number(standardSrpCad) || 0;
      const currentMsrpUsd = Number(product.msrpUsd) || Number(exportUsd) || 0;
      const currentCountry = product.countryOfOrigin || "Canada";
      const currentHarmonizedTariff = product.harmonizedTariff || product.fdaCode || "";

      const result = await syncRecipeToErpag({
        sku: product.sku.trim(),
        extraFields: {
          harmonizedTariff: currentHarmonizedTariff,
          countryOfOrigin: currentCountry,
          category: currentCategory,
          msrpCad: currentMsrpCad,
          msrpUsd: currentMsrpUsd,
          fr: currentFr,
          nameEn: product.nameEn?.trim().toUpperCase() || "",
          nameFr: product.nameFr?.trim().toUpperCase() || "",
          trademark: product.trademark?.trim() || "A SPICE AFFAIR",
          fdaCode: product.fdaCode?.trim() || "",
          lengthInch: product.lengthInch || "",
          widthInch: product.widthInch || "",
          heightInch: product.heightInch || "",
          lengthMm: product.lengthMm || "",
          widthMm: product.widthMm || "",
          heightMm: product.heightMm || "",
          ti: product.ti || addedPackaging?.ti || "",
          hi: product.hi || addedPackaging?.hi || "",
          ranking: product.ranking || product.customFields?.ranking || "",
        },
      });
      toast.dismiss(loadingToast);

      if (result?.success) {
        setProduct((prev) => ({ ...prev, isEdit: true, inErpag: true }));
        toast.success(`🎉 SKU ${product.sku.trim()} successfully synced to ERPAG!`, { duration: 6000 });
        if (ingredients && ingredients.length > 0) {
          toast("Reminder: ERPAG BOM recipe import should be done via Excel file.", {
            icon: "ℹ️",
            duration: 6000,
          });
        }
      } else if (result?.rateLimited) {
        toast.error(
          result.message || "ERPAG rate limit reached. Please wait 1 minute before retrying. You can use Save & Export (Excel) as fallback.",
          { duration: 7000 }
        );
      } else {
        toast.error(
          result?.message || "Failed to sync to ERPAG. You can download Excel files as fallback.",
          { duration: 7000 }
        );
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      console.error("ERPAG sync error:", err);
      toast.error(
        err?.message || "Failed to push to ERPAG. You can download Excel files as fallback.",
        { duration: 7000 }
      );
    } finally {
      setIsSyncingErpag(false);
    }
  };

  const handleDownloadExcel = () => {
    const variantRows = Array.isArray(product.variantRowsForSave)
      ? product.variantRowsForSave
      : [];
    const isVariantExport = Boolean(product.isVariant && variantRows.length);
    const isIngredientType =
      String(product.type || "")
        .trim()
        .toLowerCase() === "ingredient";

    if (!isVariantExport && !product.nameEn.trim()) {
      toast.error("Product name is required to export");
      return;
    }

    if (
      !isVariantExport &&
      !isIngredientType &&
      (ingredients.length === 0 ||
        ingredients.every((i) => !i.name?.trim() && !i.sku?.trim()))
    ) {
      toast.error("Add at least one ingredient to export");
      return;
    }
    const exportMasterCaseWeightKg =
      Number(product.baseQuantity) || Number(product.masterWeightKg) || 0;
    const additionalExportColumns = {};
    const currenciesToExport = ["CAD", ...exportCurrencies.filter((c) => c !== "CAD")];
    currenciesToExport.forEach((currency) => {
      const fxRate = getFxRate(currency);
      const cogs = finalCogsPerCsCad * fxRate;
      const costPerKg = summaryCostPerKg * fxRate;
      const costPerLbs = summaryCostPerLbs * fxRate;
      additionalExportColumns[`Final COGS / CS (${currency})`] = cogs > 0 ? cogs.toFixed(3) : "";
      additionalExportColumns[`Cost per kg (${currency})`] = costPerKg > 0 ? costPerKg.toFixed(3) : "";
      additionalExportColumns[`Cost per lbs (${currency})`] = costPerLbs > 0 ? costPerLbs.toFixed(3) : "";
    });

    exportRecipeToExcel({
      product,
      ingredients,
      totalWeight,
      ingredientCostTotal,
      packagingCostNum,
      totalBatchCost,
      acctCostPerKg,
      addedPackaging,
      masterCase,
      variantRows,
      masterCaseWeightKg: exportMasterCaseWeightKg,
      barcodeSecondUomRows,
      wholesale3Cad: wholesale3CadRounded,
      additionalExportColumns,
    });
  };

  const buildCurrentExportSnapshot = () => ({
    product: { ...product, exportCurrencies },
    ingredients: JSON.parse(JSON.stringify(ingredients)),
    totalWeight,
    ingredientCostTotal,
    packagingCostNum,
    totalBatchCost,
    acctCostPerKg,
    addedPackaging: JSON.parse(JSON.stringify(addedPackaging)),
    masterCase: JSON.parse(JSON.stringify(masterCase)),
    variantRows: Array.isArray(product.variantRowsForSave)
      ? JSON.parse(JSON.stringify(product.variantRowsForSave))
      : [],
    masterCaseWeightKg:
      Number(product.baseQuantity) || Number(product.masterWeightKg) || 0,
    barcodeSecondUomRows: JSON.parse(JSON.stringify(barcodeSecondUomRows)),
    wholesale3Cad: wholesale3CadRounded,
    additionalExportColumns: (() => {
      const cols = {};
      const currenciesToExport = ["CAD", ...exportCurrencies.filter((c) => c !== "CAD")];
      currenciesToExport.forEach((currency) => {
        const fxRate = getFxRate(currency);
        const cogs = finalCogsPerCsCad * fxRate;
        const costPerKg = summaryCostPerKg * fxRate;
        const costPerLbs = summaryCostPerLbs * fxRate;
        cols[`Final COGS / CS (${currency})`] = cogs > 0 ? cogs.toFixed(3) : "";
        cols[`Cost per kg (${currency})`] = costPerKg > 0 ? costPerKg.toFixed(3) : "";
        cols[`Cost per lbs (${currency})`] = costPerLbs > 0 ? costPerLbs.toFixed(3) : "";
      });
      return cols;
    })(),
  });

  const handleSaveToBatch = async () => {
    if (!ensureCostingWriteAccess("save recipes to batch")) return;

    const result = await addToDatabase();
    if (!result?.success) return;

    const snapshot = buildCurrentExportSnapshot();
    const skuKey = String(snapshot.product?.sku || "").trim();

    if (!skuKey) {
      toast.error("SKU is required to save to batch.");
      return;
    }

    try {
      await upsertCostingBatchItem({ skuKey, payload: snapshot });
    } catch (err) {
      toast.error(err?.message || "Failed to save to export batch.");
      return;
    }

    setSavedExportBatch((prev) => {
      const idx = prev.findIndex(
        (entry) => String(entry?.product?.sku || "").trim() === skuKey,
      );
      if (idx === -1) return [...prev, snapshot];
      const next = [...prev];
      next[idx] = snapshot;
      return next;
    });

    toast.success(`Saved ${skuKey || "recipe"} to export batch.`);
    baselineRef.current = buildUnsavedSignature({
      product,
      ingredients,
      bulkFormats,
      addedPackaging,
      barcodeSecondUomRows,
    });
    setHasUnsavedChanges(false);
    void clearSavedDraft();
    resetEntireForm({ showToast: false });
  };

  // const handleExportSavedBatch = () => {
  //   if (savedExportBatch.length === 0) {
  //     toast.error("No saved recipes in export batch.");
  //     return;
  //   }

  //   exportRecipeBatchToExcel(savedExportBatch);
  //   toast.success(
  //     `Batch export complete for ${savedExportBatch.length} product${savedExportBatch.length !== 1 ? "s" : ""}.`,
  //     { duration: 5000 },
  //   );
  // };
  const handleExportSavedBatch = async () => {
    if (savedExportBatch.length === 0) {
      toast.error("No saved recipes in export batch.");
      return;
    }

    const count = savedExportBatch.length;
    exportRecipeBatchToExcel(savedExportBatch);

    // Auto-clear batch after export
    setSavedExportBatch([]);
    try {
      await clearCostingBatch();
    } catch (err) {
      console.warn("Failed to clear costing batch on server after export", err);
    }

    toast.success(
      `Batch export complete for ${count} product${count !== 1 ? "s" : ""}. Batch cleared.`,
      { duration: 5000 },
    );
  };

  const handleClearSavedBatch = async () => {
    setSavedExportBatch([]);
    try {
      await clearCostingBatch();
    } catch (err) {
      console.warn("Failed to clear costing batch", err);
      toast.error(err?.message || "Failed to clear batch on server.");
    }
  };

  const handleSaveAndExport = async (afterSuccess) => {
    if (!ensureCostingWriteAccess("save and export recipes")) return;
    try {
      const result = await addToDatabase();
      if (!result?.success) return;

      handleDownloadExcel();
      baselineRef.current = buildUnsavedSignature({
        product,
        ingredients,
        bulkFormats,
        addedPackaging,
        barcodeSecondUomRows,
      });
      setHasUnsavedChanges(false);
      void clearSavedDraft();
      resetEntireForm();
      toast.success("Excel file generated and downloaded", { duration: 5000 });
      if (afterSuccess) afterSuccess();
    } catch (err) {
      console.error("Failed during save & export process", err);
    }
  };

  const hasBaseProduct = product.isVariant && product.baseProductSku !== "";

  const isIngredientTouched = (ing) => ing.name?.trim() || ing.sku?.trim();

  const hasZeroCostAfterSelect = (ing) =>
    isIngredientTouched(ing) && Number(ing.unitCost) === 0;

  const buildUnsavedSignature = useCallback(
    ({
      product: currentProduct,
      ingredients: currentIngredients,
      bulkFormats: currentBulkFormats,
      addedPackaging: currentAddedPackaging,
      barcodeSecondUomRows: currentBarcodeSecondUomRows,
    }) =>
      JSON.stringify({
        product: currentProduct,
        ingredients: currentIngredients,
        bulkFormats: currentBulkFormats,
        addedPackaging: currentAddedPackaging,
        barcodeSecondUomRows: currentBarcodeSecondUomRows,
      }),
    [],
  );

  const buildCurrentSnapshot = useCallback(
    () => ({
      product,
      ingredients,
      bulkFormats,
      addedPackaging,
      barcodeSecondUomRows,
      masterCase,
      packagingCost,
      ingredientCount,
      activeTab,
      ratioRoundingStep,
      globalUnit,
    }),
    [
      product,
      ingredients,
      bulkFormats,
      addedPackaging,
      barcodeSecondUomRows,
      masterCase,
      packagingCost,
      ingredientCount,
      activeTab,
      ratioRoundingStep,
      globalUnit,
    ],
  );

  const hasMeaningfulWork = useCallback((snapshot) => {
    const data = snapshot || {};
    const productData = data.product || {};
    const ingredientsData = Array.isArray(data.ingredients)
      ? data.ingredients
      : [];
    const bulkFormatsData = Array.isArray(data.bulkFormats)
      ? data.bulkFormats
      : [];
    const barcodeRowsData = Array.isArray(data.barcodeSecondUomRows)
      ? data.barcodeSecondUomRows
      : [];

    const productFieldsToCheck = [
      "nameEn",
      "nameFr",
      "sku",
      "upc",
      "scc14",
      "descriptionEn",
      "descriptionFr",
      "supplier",
      "supplierSku",
      "defaultPrice",
      "purchasePrice",
      "landingCost",
      "shippingDays",
      "minimumOrderQuantity",
      "orderStepQuantity",
      "baseQuantity",
      "harmonizedTariff",
      "fdaCode",
      "ingredientsEn",
      "ingredientsFr",
      "allergensEn",
      "allergensFr",
      "rawMaterialSku",
      "baseProductSku",
      "customFieldsSku",
      "generatedForNameEn",
    ];
    const hasProductValues = productFieldsToCheck.some(
      (field) => String(productData?.[field] || "").trim() !== "",
    );

    const hasIngredientValues = ingredientsData.some((ing) => {
      const name = String(ing?.name || "").trim();
      const sku = String(ing?.sku || "").trim();
      const weight = Number(ing?.weight) || 0;
      const ratio = Number(ing?.ratio) || 0;
      const unitCost = Number(ing?.unitCost) || 0;
      return Boolean(name || sku || weight > 0 || ratio > 0 || unitCost > 0);
    });

    const hasBulkFormatValues = bulkFormatsData.some((format) =>
      Object.entries(format || {}).some(([key, value]) => {
        if (["id", "generatedSku", "displayWeight"].includes(key)) return false;
        if (value === null || value === undefined) return false;
        if (typeof value === "number") return value > 0;
        return String(value).trim() !== "";
      }),
    );

    const hasBarcodeRows =
      barcodeRowsData.length > 0 &&
      barcodeRowsData.some((row) =>
        ["eanUpc", "secondUom", "description"].some(
          (key) => String(row?.[key] || "").trim() !== "",
        ),
      );

    return Boolean(
      hasProductValues ||
      hasIngredientValues ||
      hasBulkFormatValues ||
      data.addedPackaging ||
      hasBarcodeRows,
    );
  }, []);

  const applyDraftSnapshot = useCallback(
    (snapshot) => {
      const parsed = snapshot || {};
      setProduct(parsed.product || getInitialProduct());
      setIngredients(
        Array.isArray(parsed.ingredients) && parsed.ingredients.length > 0
          ? parsed.ingredients
          : [createEmptyIngredient(), createEmptyIngredient()],
      );
      setBulkFormats(
        Array.isArray(parsed.bulkFormats) ? parsed.bulkFormats : [],
      );
      setAddedPackaging(parsed.addedPackaging || null);
      setBarcodeSecondUomRows(
        Array.isArray(parsed.barcodeSecondUomRows)
          ? parsed.barcodeSecondUomRows
          : [],
      );
      setMasterCase(
        parsed.masterCase || {
          type: "Standard Master case",
          weight: 25,
        },
      );
      setPackagingCost(parsed.packagingCost || "");
      setIngredientCount(Math.max(1, Number(parsed.ingredientCount) || 1));
      setActiveTab(parsed.activeTab === "formats" ? "formats" : "recipe");
      setRatioRoundingStep(
        parsed.ratioRoundingStep === "0.05" ? "0.05" : "0.1",
      );
      setGlobalUnit(parsed.globalUnit || "kg");

      baselineRef.current = buildUnsavedSignature({
        product: parsed.product || getInitialProduct(),
        ingredients:
          Array.isArray(parsed.ingredients) && parsed.ingredients.length > 0
            ? parsed.ingredients
            : [createEmptyIngredient(), createEmptyIngredient()],
        bulkFormats: Array.isArray(parsed.bulkFormats)
          ? parsed.bulkFormats
          : [],
        addedPackaging: parsed.addedPackaging || null,
        barcodeSecondUomRows: Array.isArray(parsed.barcodeSecondUomRows)
          ? parsed.barcodeSecondUomRows
          : [],
      });
      setHasUnsavedChanges(false);
    },
    [buildUnsavedSignature],
  );

  const saveDraftToServer = useCallback(
    async (snapshot) => {
      if (!ENABLE_SERVER_DRAFTS || !canWriteCostingTool) {
        return { success: false, skipped: true };
      }
      if (!hasMeaningfulWork(snapshot)) {
        try {
          await deleteCostingDraft();
        } catch (err) {
          console.warn("Failed to delete empty costing draft", err);
        }
        return { success: true, deleted: true };
      }
      await saveCostingDraft({ payload: snapshot });
      return { success: true };
    },
    [ENABLE_SERVER_DRAFTS, canWriteCostingTool, hasMeaningfulWork],
  );

  const clearSavedDraft = useCallback(async () => {
    if (!ENABLE_SERVER_DRAFTS || !canWriteCostingTool) return;
    try {
      await deleteCostingDraft();
    } catch (err) {
      console.warn("Failed to clear costing draft", err);
    }
  }, [ENABLE_SERVER_DRAFTS, canWriteCostingTool]);

  useEffect(() => {
    const snapshot = buildCurrentSnapshot();
    const signature = buildUnsavedSignature(snapshot);
    const started = hasMeaningfulWork(snapshot);

    if (baselineRef.current === null) {
      baselineRef.current = signature;
      setHasUnsavedChanges(false);
      return;
    }

    setHasUnsavedChanges(started && signature !== baselineRef.current);
  }, [buildCurrentSnapshot, buildUnsavedSignature, hasMeaningfulWork]);

  const handleLoadEditRecipe = useCallback(
    async (sku, recipeMeta = null) => {
      if (!sku) return;
      const loadingToast = toast.loading(`Loading recipe ${sku} for full editing...`);
      try {
        const response = await getSkuRawSnapshot(sku);
        toast.dismiss(loadingToast);

        if (response?.data) {
          const snapshot = response.data;
          applyDraftSnapshot(snapshot);

          const upcToPreserve = String(
            snapshot.product?.upc ||
            recipeMeta?.upc ||
            snapshot.product?.eanUpc ||
            "",
          ).trim();

          const loadedProduct = {
            ...(snapshot.product || {}),
            isEdit: true,
            skuGenerated: true,
            preservedUpc: upcToPreserve,
            upc: upcToPreserve || snapshot.product?.upc || "",
          };

          setProduct(loadedProduct);
          setShowDraftResumeModal(false);
          setActiveTab("recipe");

          baselineRef.current = buildUnsavedSignature({
            product: loadedProduct,
            ingredients: snapshot.ingredients || [],
            bulkFormats: snapshot.bulkFormats || [],
            addedPackaging: snapshot.addedPackaging || null,
            barcodeSecondUomRows: snapshot.barcodeSecondUomRows || [],
          });
          setHasUnsavedChanges(false);

          toast.success(
            `Loaded recipe ${sku} for full editing. Reserved UPC ${upcToPreserve || "N/A"} preserved.`,
            { duration: 5000 },
          );
        } else {
          toast.error("No recipe data found for this SKU.");
        }
      } catch (err) {
        toast.dismiss(loadingToast);
        console.error("Failed to load edit SKU", err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load SKU for editing",
        );
      }
    },
    [applyDraftSnapshot, buildUnsavedSignature],
  );

  useEffect(() => {
    const editSku = searchParams.get("edit_sku");
    if (editSku) {
      handleLoadEditRecipe(editSku);
    }
  }, [searchParams, handleLoadEditRecipe]);

  useEffect(() => {
    if (
      !ENABLE_SERVER_DRAFTS ||
      hasHydratedDraftRef.current ||
      !canWriteCostingTool
    )
      return;
    hasHydratedDraftRef.current = true;
    const loadDraft = async () => {
      try {
        const response = await getCostingDraft();
        const payload = response?.data?.payload;
        if (!payload || typeof payload !== "object") return;
        if (!hasMeaningfulWork(payload)) return;
        setPendingDraftSnapshot(payload);
        setShowDraftResumeModal(true);
      } catch (err) {
        console.warn("Failed to load costing draft", err);
      }
    };
    loadDraft();
  }, [
    ENABLE_SERVER_DRAFTS,
    canWriteCostingTool,
    hasMeaningfulWork,
    applyDraftSnapshot,
  ]);

  useEffect(() => {
    if (hasHydratedBatchRef.current || !canWriteCostingTool) return;
    hasHydratedBatchRef.current = true;

    const loadBatch = async () => {
      try {
        const response = await getCostingBatch();
        const items = Array.isArray(response?.data) ? response.data : [];
        const snapshots = items
          .map((item) => item?.payload)
          .filter((payload) => payload && typeof payload === "object");
        setSavedExportBatch(snapshots);
      } catch (err) {
        console.warn("Failed to load costing batch", err);
      }
    };

    loadBatch();
  }, [canWriteCostingTool]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasUnsavedChanges || !canWriteCostingTool) return;
      e.preventDefault();
      e.returnValue = product.isVariant
        ? "You have unsaved variant changes, including packaging assignments. Save recipe and export?"
        : "You have unsaved changes to the product, including auto-generated barcodes and UOM.";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, product.isVariant, canWriteCostingTool]);

  useEffect(() => {
    const handleDocumentNavigation = (e) => {
      if (!hasUnsavedChanges) return;
      if (!canWriteCostingTool) return;
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      if (
        anchor.target === "_blank" ||
        anchor.hasAttribute("download") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return;
      }

      const current = new URL(window.location.href);
      const next = new URL(anchor.href, window.location.origin);
      if (current.origin !== next.origin) return;
      if (
        current.pathname === next.pathname &&
        current.search === next.search &&
        current.hash === next.hash
      ) {
        return;
      }

      e.preventDefault();
      pendingInternalActionRef.current = null;
      pendingNavigationRef.current = `${next.pathname}${next.search}${next.hash}`;
      setShowUnsavedPromptModal(true);
    };

    document.addEventListener("click", handleDocumentNavigation, true);
    return () => {
      document.removeEventListener("click", handleDocumentNavigation, true);
    };
  }, [hasUnsavedChanges, canWriteCostingTool]);

  const markCurrentAsSaved = () => {
    baselineRef.current = buildUnsavedSignature({
      product,
      ingredients,
      bulkFormats,
      addedPackaging,
      barcodeSecondUomRows,
    });
    setHasUnsavedChanges(false);
  };

  const confirmOrRunInternalAction = (action) => {
    if (!hasUnsavedChanges || !canWriteCostingTool) {
      action();
      return;
    }
    pendingNavigationRef.current = null;
    pendingInternalActionRef.current = action;
    setShowUnsavedPromptModal(true);
  };


  const effectiveCaseWeightKg = isBaseIngredient
    ? Number(product.netWeight1Kg) || Number(product.qtyUn) || 1
    : masterCaseWeightKg > 0
      ? masterCaseWeightKg
      : 1;

  const pricePerKg = (price) =>
    effectiveCaseWeightKg > 0 && Number(price) > 0
      ? (Number(price) / effectiveCaseWeightKg).toFixed(2)
      : "0.00";

  const pricePerLb = (price) =>
    effectiveCaseWeightKg > 0 && Number(price) > 0
      ? (Number(price) / (effectiveCaseWeightKg * 2.20462)).toFixed(2)
      : "0.00";

  const availableCurrencies = useMemo(() => {
    return [
      "CAD",
      ...(Array.isArray(fxRates) ? fxRates : []).map(row => String(row.currency || "").trim().toUpperCase())
    ].filter((v, i, a) => a.indexOf(v) === i && v !== "");
  }, [fxRates]);

  const getFxRate = (curr) => {
    if (!curr || curr.toUpperCase() === "CAD") return 1;
    const found = (Array.isArray(fxRates) ? fxRates : []).find(
      (row) => String(row?.currency || "").trim().toUpperCase() === curr.toUpperCase()
    );
    return found ? (Number(found.bufferedFxVsCad) || 0) : 0;
  };

  const getCurrencySymbol = (curr) => {
    const map = {
      USD: "$",
      CAD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "$",
      AED: "AED ",
    };
    return map[curr.toUpperCase()] || `${curr} `;
  };

  const convertCurrencyDisplay = (value, fromCurrency) => {
    const numericValue = Number(value) || 0;
    if (numericValue <= 0) return 0;
    const toCurrency = selectedCurrency;
    if (fromCurrency === toCurrency) return numericValue;

    let valueInCad = numericValue;
    if (fromCurrency !== "CAD") {
      const fromRate = getFxRate(fromCurrency);
      if (fromRate > 0) {
        valueInCad = numericValue / fromRate;
      }
    }

    const targetRate = getFxRate(toCurrency);
    return valueInCad * targetRate;
  };

  const renderCostSummaryRow = (label, value, sourceCurrency, marginPercent) => {
    const displayVal = convertCurrencyDisplay(value || 0, sourceCurrency);
    return (
      <tr className="border-t border-gray-700">
        <td className="px-4 py-3 text-gray-200">{label}</td>
        <td className="px-4 py-3 text-right font-semibold">
          {getCurrencySymbol(selectedCurrency)}
          {displayVal.toFixed(2)}
        </td>
        <td className="px-4 py-3 text-right font-semibold">
          {getCurrencySymbol(selectedCurrency)}
          {pricePerKg(displayVal)}
        </td>
        <td className="px-4 py-3 text-right font-semibold">
          {getCurrencySymbol(selectedCurrency)}
          {pricePerLb(displayVal)}
        </td>
        <td className="px-4 py-3 text-right font-semibold">
          {formatMarginPercent(marginPercent)}
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full max-w-full min-w-0 overflow-x-hidden">
      <Toaster position="top-right" reverseOrder={false} />

      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="mx-auto px-4 sm:px-6 py-3 sm:py-5 flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 truncate pr-12 sm:pr-0">Costing Tool</h1>
        </div>
      </div>

      <div className="mx-auto p-2 sm:p-4 md:p-6 space-y-4 sm:space-y-6 md:space-y-8 w-full max-w-full min-w-0 overflow-x-hidden">
        {/* Tabs */}
        <div className="flex gap-2 sm:gap-6 md:gap-8 border-b border-gray-300 overflow-x-auto no-scrollbar whitespace-nowrap -mx-3 px-3 sm:mx-0 sm:px-0">
          {[
            { key: "recipe", label: "Recipe & Bulk" },
            { key: "formats", label: "Retail Formats" },
            { key: "private", label: "Private Label" },
            ...(canSeeRecipeHistory
              ? [{ key: "history", label: "New SKU History" }]
              : []),
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                if (activeTab === tab.key) return;
                confirmOrRunInternalAction(() => setActiveTab(tab.key));
              }}
              className={`shrink-0 pb-3 sm:pb-4 px-2 sm:px-3 font-semibold text-sm sm:text-base md:text-lg transition-colors ${activeTab === tab.key
                ? "text-black border-b-4 border-black"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB: Recipe & Bulk */}
        {activeTab === "recipe" && (
          <div className="space-y-6 sm:space-y-8 min-w-0 w-full max-w-full">
            {!canWriteCostingTool && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Read-only mode: you can view costing data, but
                create/edit/delete and save actions are disabled.
              </div>
            )}
            <fieldset disabled={!canWriteCostingTool} className="space-y-6 sm:space-y-8 min-w-0 w-full max-w-full border-0 p-0 m-0">
              {/* EDIT MODE BANNER */}
              {product.isEdit && (
                <div className="p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm animate-in fade-in">
                  <div className="flex items-start md:items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center font-bold shadow-xs shrink-0">
                      <Edit className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-200 text-amber-900 rounded-md">
                          EDITING RECIPE
                        </span>
                        <span className="font-mono font-bold text-gray-900 text-xs sm:text-sm">
                          SKU: {product.sku}
                        </span>
                        {product.upc && (
                          <span className="font-mono text-[11px] sm:text-xs font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-md inline-flex items-center gap-1 break-all">
                            <Barcode className="w-3.5 h-3.5 shrink-0" />
                            Reserved UPC: {product.upc} (Preserved)
                          </span>
                        )}
                      </div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-800 mt-1 break-words">
                        {product.nameEn || product.descriptionEn || "Recipe Listing"}
                      </div>
                      <p className="text-[11px] sm:text-xs text-amber-800 mt-0.5">
                        You are editing this listing. You can modify ingredients, weights, allergens, or descriptions. Saving updates all tables (BOM, Price Management, Detailed SKU, RM DB) while strictly preserving the reserved UPC.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                    <button
                      type="button"
                      onClick={() => {
                        if (hasUnsavedChanges) {
                          if (!window.confirm("You have unsaved changes. Exit Edit Mode and start a new recipe?")) return;
                        }
                        resetEntireForm();
                        toast("Exited Edit Mode. Ready for new recipe.", { icon: "ℹ️" });
                      }}
                      className="w-full md:w-auto px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-xl text-xs font-bold transition shadow-xs flex items-center justify-center gap-1.5"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Exit Edit Mode (New Recipe)
                    </button>
                  </div>
                </div>
              )}

              {/* Product Mode Selector */}
              <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-5 mb-4 sm:mb-8 min-w-0 w-full max-w-full shadow-xs">
                <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                  Select Product Type
                </h2>

                <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2.5">
                  <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm font-medium shrink-0 whitespace-nowrap">
                    <input
                      type="radio"
                      name="productMode"
                      value="base"
                      checked={
                        !product.isVariant && product.type !== "ingredient"
                      }
                      onChange={() => {
                        confirmOrRunInternalAction(() => {
                          setProduct((prev) => ({
                            ...prev,
                            isVariant: false,
                            type: "regular",
                            backOrder: "Bill of Materials",
                          }));
                          setHasGeneratedButUnsavedBase(false);
                        });
                      }}
                    />
                    <span className="font-medium">Base Blends</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm font-medium shrink-0 whitespace-nowrap">
                    <input
                      type="radio"
                      name="productMode"
                      value="ingredient"
                      checked={
                        !product.isVariant && product.type === "ingredient"
                      }
                      onChange={() => {
                        confirmOrRunInternalAction(() => {
                          setProduct((prev) => ({
                            ...prev,
                            isVariant: false,
                            type: "ingredient",
                            backOrder: "Supplier",
                            msrpCad:
                              prev.msrpCad === "" ||
                                prev.msrpCad === null ||
                                prev.msrpCad === undefined
                                ? "0"
                                : prev.msrpCad,
                            msrpUsd:
                              prev.msrpUsd === "" ||
                                prev.msrpUsd === null ||
                                prev.msrpUsd === undefined
                                ? "0"
                                : prev.msrpUsd,
                          }));
                          setHasGeneratedButUnsavedBase(false);
                        });
                      }}
                    />
                    <span className="font-medium"> Base Ingredient</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm font-medium shrink-0 whitespace-nowrap">
                    <input
                      type="radio"
                      name="productMode"
                      value="variant"
                      checked={product.isVariant}
                      onChange={() => {
                        confirmOrRunInternalAction(() => {
                          if (
                            !product.isVariant &&
                            hasGeneratedButUnsavedBase
                          ) {
                            setShowSaveBeforeVariantModal(true);
                          } else {
                            setProduct((prev) => ({
                              ...prev,
                              isVariant: true,
                              baseProductSku: "",
                            }));
                          }
                        });
                      }}
                    />
                    <span className="font-medium">Variants</span>
                  </label>
                </div>
              </div>

              {/* Base Product Section */}
              {product.isVariant && (
                <BaseProductNameSection
                  product={product}
                  setProduct={setProduct}
                />
              )}
              {!product.isVariant && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6 md:p-8 min-w-0 w-full max-w-full overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-4">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">
                      BASIC INFO
                    </h2>
                  </div>

                  {/* Responsive Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-6 min-w-0">
                    <div className="sm:col-span-1 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Name (EN)
                      </label>
                      <input
                        placeholder="Enter the Product name En"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.nameEn}
                        onChange={(e) =>
                          setProduct({
                            ...product,
                            nameEn: e.target.value.toUpperCase(),
                          })
                        }
                      />
                    </div>

                    <div className="sm:col-span-1 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Name (FR)
                      </label>
                      <input
                        placeholder="Enter the Product name Fr"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.nameFr}
                        onChange={(e) =>
                          setProduct({
                            ...product,
                            nameFr: e.target.value.toUpperCase(),
                          })
                        }
                      />
                    </div>
                    {isBaseIngredient && (
                      <>
                        <div className="sm:col-span-1 lg:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ingredient Name (EN)
                          </label>
                          <input
                            placeholder="Enter the Ingredient name En"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            value={product.ingredientsEn || ""}
                            onChange={(e) =>
                              setProduct({
                                ...product,
                                ingredientsEn: e.target.value
                              })
                            }
                          />
                        </div>

                        <div className="sm:col-span-1 lg:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ingredient Name (FR)
                          </label>
                          <input
                            placeholder="Enter the Ingredient name Fr"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            value={product.ingredientsFr || ""}
                            onChange={(e) =>
                              setProduct({
                                ...product,
                                ingredientsFr: e.target.value
                              })
                            }
                          />
                        </div>
                      </>
                    )}



                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description (EN)
                      </label>
                      <input
                        type="text"
                        placeholder={!isBaseIngredient
                          ? "Auto-generated from Product Name (EN) e.g. ADOBO SEASONING NUTRIVILLA VRAC"
                          : "Auto-generated from Product Name (EN) e.g. ADOBO SEASONING VRAC"
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.descriptionEn}
                        readOnly
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description (FR)
                      </label>
                      <input
                        type="text"
                        placeholder={!isBaseIngredient
                          ? "Auto-generated from Product Name (FR) eg ASSAISONNEMENT ADOBO NUTRIVILLA VRAC" : "Auto-generated from Product Name (EN) e.g. ASSAISONNEMENT ADOBO VRAC"}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.descriptionFr}
                        readOnly
                      />
                    </div>

                    <div className="sm:col-span-1 lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SKU Keyword
                      </label>
                      <input
                        placeholder="Keyword (e.g. YEAST)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.skuKeyword || ""}
                        onChange={(e) =>
                          setProduct((prev) => ({
                            ...prev,
                            skuKeyword: String(e.target.value || "").toUpperCase(),
                          }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-1 lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UOM
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.uom}
                        onChange={(e) =>
                          setProduct({ ...product, uom: e.target.value })
                        }
                      >
                        <option value="kg">kg</option>
                        <option value="CS">CS</option>
                        <option value="L">L</option>
                      </select>
                    </div>

                    <div className="sm:col-span-1 lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={
                          product.type === "ingredient"
                            ? "regular"
                            : product.type
                        }
                        disabled={
                          product.type === "ingredient" || product.skuGenerated
                        }
                        onChange={(e) =>
                          setProduct({ ...product, type: e.target.value })
                        }
                      >
                        <option value="regular">Regular</option>
                        <option value="custom">Custom Mix</option>
                      </select>
                    </div>

                    <div className="sm:col-span-1 lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contains Allergen
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        value={product.containsAllergen}
                        disabled={ingredients.some((ing) => ing.name?.trim() || ing.sku?.trim())}
                        onChange={(e) =>
                          setProduct({
                            ...product,
                            containsAllergen: e.target.value,
                          })
                        }
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>

                    {!isBaseIngredient && (
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Signature Premium
                        </label>
                        <select
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          value={product.isSignaturePremium ? "yes" : "no"}
                          onChange={(e) => {
                            const value = e.target.value === "yes";
                            setProduct({
                              ...product,
                              isSignaturePremium: value,
                            });
                          }}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    )}

                    {product.type === "ingredient" ? (
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contains Ingredients
                        </label>
                        <select
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          value={product.containsIngredients || "no"}
                          onChange={(e) =>
                            setProduct({
                              ...product,
                              containsIngredients: e.target.value,
                            })
                          }
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    ) : (
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ingredient Count
                        </label>
                        <input
                          type="number"
                          readOnly
                          value={actualIngredientCount}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg font-semibold text-center bg-gray-50 cursor-not-allowed"
                        />
                      </div>
                    )}

                    <div className="sm:col-span-1 lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                        <span>{product.isEdit ? "SKU (Locked)" : "New SKU"}</span>
                        {product.isEdit && (
                          <span className="text-[10px] uppercase font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-300">
                            Preserved
                          </span>
                        )}
                      </label>
                      <input
                        className={`w-full px-4 py-3 border rounded-lg font-medium ${
                          product.isEdit
                            ? "bg-amber-50/70 border-amber-300 text-amber-950 font-bold"
                            : "bg-gray-100"
                        }`}
                        value={product.sku || "Not generated yet"}
                        readOnly
                      />
                    </div>

                    {/* UPC field */}
                    <div className="sm:col-span-1 lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                        <span>UPC</span>
                        {product.isEdit && (
                          <span className="text-[10px] uppercase font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300">
                            Preserved & Protected
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        placeholder="Auto-generated UPC"
                        className={`w-full px-4 py-3 border rounded-lg font-medium ${
                          product.isEdit
                            ? "bg-emerald-50/70 border-emerald-300 text-emerald-950 font-bold"
                            : "bg-gray-100"
                        }`}
                        value={product.upc || "Not generated yet"}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              )}
              {!product.isVariant && (
                <FulfillmentSection
                  product={product}
                  setProduct={setProduct}
                  suppliers={suppliers}
                  onCreateSupplier={handleCreateSupplier}
                />
              )}

              {/* Recipe Builder */}
              {!product.isVariant && product.type !== "ingredient" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6 md:p-8 min-w-0 w-full max-w-full overflow-hidden">
                  <>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">
                        RECIPE BUILDER
                      </h2>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
                        <input
                          type="number"
                          min="2"
                          max="50"
                          className="w-20 sm:w-[80px] px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-sm"
                          value={ingredientCount}
                          onChange={(e) =>
                            setIngredientCount(
                              Math.min(
                                100,
                                Math.max(1, Number(e.target.value) || 1),
                              ),
                            )
                          }
                        />
                        <select
                          className="flex-1 sm:flex-none sm:w-[140px] px-2 py-2 sm:py-3 border border-gray-300 rounded-lg bg-white text-sm"
                          value={ratioRoundingStep}
                          onChange={(e) => setRatioRoundingStep(e.target.value)}
                          title="Ratio rounding step"
                        >
                          <option value="0.1">Round 0.10%</option>
                          <option value="0.05">Round 0.05%</option>
                        </select>
                        <button
                          onClick={clearRecipeIngredients}
                          className="px-3 sm:px-5 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm whitespace-nowrap"
                        >
                          Clear All
                        </button>
                        <button
                          onClick={addIngredient}
                          className="flex items-center justify-center gap-1.5 px-3 sm:px-5 py-2 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-800 text-sm whitespace-nowrap"
                        >
                          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                          Add Ingredient
                        </button>
                      </div>
                    </div>

                    {/* Recipe Builder Table Container (with horizontal scroll on tablet/desktop) */}
                    <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
                      <div className="min-w-0 md:min-w-[850px] xl:min-w-0">
                    {/* Desktop Column Headings */}
                    {hasBaseProduct ? (
                      <div
                        className="hidden md:grid gap-4 mb-1 px-1 text-sm font-medium text-gray-600"
                        style={{
                          gridTemplateColumns: "0.5fr 3fr 2fr 1fr 1fr 1fr 1fr",
                        }}
                      >
                        <div>Seq</div>
                        <div>Description</div>
                        <div>SKU Key</div>
                        <div>Qty</div>
                        <div>UOM</div>
                        <div>Cost</div>
                        <div>Amount</div>
                      </div>
                    ) : (
                      <div
                        className="hidden md:grid gap-4 mb-1 px-1 text-sm font-medium text-gray-600"
                        style={{
                          gridTemplateColumns:
                            "2fr 2fr 1fr 1fr 1fr 1fr 1fr 1.4fr 1fr",
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span>Ingredient</span>
                          <button
                            type="button"
                            onClick={() => handleCopyRecipeColumn("name")}
                            className="text-gray-500 hover:text-black"
                            aria-label="Copy Ingredient column"
                            title="Copy Ingredient column"
                          >
                            {copiedRecipeColumn === "name" ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>SKU</span>
                          <button
                            type="button"
                            onClick={() => handleCopyRecipeColumn("sku")}
                            className="text-gray-500 hover:text-black"
                            aria-label="Copy SKU column"
                            title="Copy SKU column"
                          >
                            {copiedRecipeColumn === "sku" ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>Weight</span>
                          <button
                            type="button"
                            onClick={() => handleCopyRecipeColumn("weight")}
                            className="text-gray-500 hover:text-black"
                            aria-label="Copy Weight column"
                            title="Copy Weight column"
                          >
                            {copiedRecipeColumn === "weight" ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span>Unit</span>
                            <button
                              type="button"
                              onClick={() => handleCopyRecipeColumn("unit")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy Unit column"
                              title="Copy Unit column"
                            >
                              {copiedRecipeColumn === "unit" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          <select
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
                            value={globalUnit}
                            onChange={(e) => updateAllUnits(e.target.value)}
                          >
                            <option value="kg">kg</option>
                            <option value="un.">un.</option>
                            <option value="g">g</option>
                            <option value="ml">ml</option>
                            <option value="l">l</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>Ratio %</span>
                          <button
                            type="button"
                            onClick={() => handleCopyRecipeColumn("ratio")}
                            className="text-gray-500 hover:text-black"
                            aria-label="Copy Ratio column"
                            title="Copy Ratio column"
                          >
                            {copiedRecipeColumn === "ratio" ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>Master Case Wt (kg)</span>
                          <button
                            type="button"
                            onClick={() =>
                              handleCopyRecipeColumn("masterCaseWeightKg")
                            }
                            className="text-gray-500 hover:text-black"
                            aria-label="Copy Master Case Weight column"
                            title="Copy Master Case Weight column"
                          >
                            {copiedRecipeColumn === "masterCaseWeightKg" ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>{canSeeDetailedCosts ? "Raw Cost ($)/Kg" : "Raw Cost (Confidential)"}</span>
                          {canSeeDetailedCosts && (
                            <button
                              type="button"
                              onClick={() => handleCopyRecipeColumn("unitCost")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy Raw Cost column"
                              title="Copy Raw Cost column"
                            >
                              {copiedRecipeColumn === "unitCost" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span>{canSeeDetailedCosts ? "Cost ($) as per weight used" : "Cost (Confidential)"}</span>
                          {canSeeDetailedCosts && (
                            <button
                              type="button"
                              onClick={() => handleCopyRecipeColumn("totalCost")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy Total Cost column"
                              title="Copy Total Cost column"
                            >
                              {copiedRecipeColumn === "totalCost" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="text-center">Action</div>
                      </div>
                    )}

                    {hasBaseProduct ? (
                      <BaseProductRecipeRow product={product} />
                    ) : (
                      <div className="space-y-6" ref={ingredientRowsWrapperRef}>
                        {ingredients.map((ing, index) => {
                          const filteredSuggestions = getIngredientSuggestions(
                            ing.id,
                            ing.name,
                          );
                          const activeSuggestionIndex =
                            activeSuggestionIndexByIngredientId[ing.id] ?? -1;

                          return (
                            <div
                              key={ing.id}
                              className="border border-gray-200 rounded-xl overflow-hidden md:overflow-visible bg-gray-50 md:bg-transparent md:border-0"
                            >
                              {/* Mobile Layout (Card Style) */}
                              <div className="md:hidden p-5 space-y-4">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-semibold text-gray-700">
                                    Ingredient #{index + 1}
                                  </span>
                                  <button
                                    onClick={() => removeIngredient(ing.id)}
                                    disabled={ingredients.length === 1}
                                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>

                                {/* Ingredient Name + Search Dropdown */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Ingredient Name
                                  </label>
                                  <div className="relative">
                                    <input
                                      placeholder="Type to search ingredient..."
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                                      value={
                                        ingredientNameDrafts[ing.id] ?? ing.name
                                      }
                                      onChange={(e) =>
                                        scheduleIngredientNameUpdate(
                                          ing.id,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={(e) =>
                                        commitIngredientNameUpdate(
                                          ing.id,
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() =>
                                        setFocusedIngredientId(ing.id)
                                      }
                                    />
                                    {focusedIngredientId === ing.id &&
                                      ing.name && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-30 overflow-hidden">
                                          <div className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                                            {filteredSuggestions.map(
                                              (item, idx) => (
                                                <button
                                                  key={item.sku}
                                                  type="button"
                                                  onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    applyIngredientSuggestion(
                                                      ing.id,
                                                      item,
                                                    );
                                                    clearIngredientNameDraft(
                                                      ing.id,
                                                    );
                                                    clearIngredientNameTimer(
                                                      ing.id,
                                                    );

                                                    setFocusedIngredientId(
                                                      null,
                                                    );
                                                  }}
                                                  className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 text-xs transition-colors ${idx % 2 === 0
                                                    ? "bg-gray-50/70"
                                                    : "bg-white"
                                                    } hover:bg-blue-50 hover:text-blue-800`}
                                                >
                                                  <div className="min-w-0 flex-1 flex items-center gap-2">
                                                    <span className="font-medium text-gray-900 truncate text-xs" title={item.name}>
                                                      {item.name}
                                                    </span>
                                                    {(() => {
                                                      const rmRow = rmDatabaseMap.get(
                                                        String(item.sku || "").trim(),
                                                      );
                                                      const costFromIng = Number(item.unitCost) || 0;
                                                      const costFromRm = Number(rmRow?.baseCostKgCad) || Number(rmRow?.TotalRMCost) || 0;
                                                      const displayCost = costFromIng > 0 ? costFromIng : costFromRm;
                                                      return displayCost > 0 && canSeeDetailedCosts ? (
                                                        <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                                          (${displayCost.toFixed(2)})
                                                        </span>
                                                      ) : null;
                                                    })()}
                                                  </div>
                                                  <span className="font-mono text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200 whitespace-nowrap shrink-0">
                                                    {item.sku}
                                                  </span>
                                                </button>
                                              ),
                                            )}
                                            {filteredSuggestions.length ===
                                              0 && (
                                                <div className="px-4 py-6 text-center text-gray-500 text-xs">
                                                  No ingredients found
                                                </div>
                                              )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>

                                {/* Grid for other fields */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      SKU
                                    </label>
                                    <input
                                      placeholder="Paste or type SKU"
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm"
                                      value={ing.sku}
                                      onChange={(e) =>
                                        updateIngredient(
                                          ing.id,
                                          "sku",
                                          e.target.value,
                                        )
                                      }
                                      onPaste={(e) =>
                                        handlePaste(e, index, "sku")
                                      }
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Weight
                                    </label>
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                                      value={ing.weight}
                                      onChange={(e) =>
                                        updateIngredient(
                                          ing.id,
                                          "weight",
                                          e.target.value,
                                        )
                                      }
                                      onInput={(e) =>
                                        updateIngredient(
                                          ing.id,
                                          "weight",
                                          e.currentTarget.value,
                                        )
                                      }
                                      onPaste={(e) =>
                                        handlePaste(e, index, "weight")
                                      }
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Unit
                                    </label>
                                    <select
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                                      value={ing.unit}
                                      onChange={(e) =>
                                        updateIngredient(
                                          ing.id,
                                          "unit",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <option value="kg">kg</option>
                                      <option value="un.">un.</option>
                                      <option value="g">g</option>
                                      <option value="ml">ml</option>
                                      <option value="l">l</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Ratio %
                                    </label>
                                    <input
                                      type="number"
                                      step={ratioRoundingStep}
                                      min="0"
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-sm"
                                      value={
                                        ingredientRatioDrafts[ing.id] ??
                                        formatRatioDisplay(ing.ratio)
                                      }
                                      onChange={(e) =>
                                        handleIngredientRatioChange(
                                          ing.id,
                                          e.target.value,
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.currentTarget.blur();
                                        }
                                      }}
                                      onBlur={(e) =>
                                        commitIngredientRatioUpdate(
                                          ing.id,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Master Case Wt (kg)
                                    </label>
                                    <input
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-sm font-mono"
                                      value={
                                        getRatioWeightKg(ing) > 0
                                          ? getRatioWeightKg(ing).toFixed(4)
                                          : ""
                                      }
                                      readOnly
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Raw Cost ($/kg)
                                    </label>
                                    <input
                                      className={`w-full px-4 py-3 rounded-lg font-mono text-sm bg-gray-100 border-2 ${hasZeroCostAfterSelect(ing)
                                        ? "border-red-500 text-red-700"
                                        : "border-gray-300"
                                        }`}
                                      value={canSeeDetailedCosts ? ing.unitCost : "••••••"}
                                      readOnly
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Total Cost ($)
                                    </label>
                                    <input
                                      className={`w-full px-4 py-3 rounded-lg font-mono text-sm bg-gray-200 border-2 ${hasZeroCostAfterSelect(ing)
                                        ? "border-red-500 text-red-700"
                                        : "border-gray-300"
                                        }`}
                                      value={canSeeDetailedCosts ? ing.totalCost.toFixed(4) : "••••••"}
                                      readOnly
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Desktop Grid Layout (Unchanged) */}
                              <div
                                className="hidden md:grid gap-4 items-center relative"
                                style={{
                                  gridTemplateColumns:
                                    "2fr 2fr 1fr 1fr 1fr 1fr 1fr 1.4fr 1fr",
                                }}
                              >
                                  <div className="relative">
                                    <input
                                      placeholder="Type to search ingredient..."
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                                      value={
                                        ingredientNameDrafts[ing.id] ?? ing.name
                                      }
                                      onChange={(e) =>
                                        scheduleIngredientNameUpdate(
                                          ing.id,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={(e) =>
                                        commitIngredientNameUpdate(
                                          ing.id,
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() => {
                                        setFocusedIngredientId(ing.id);
                                        setActiveSuggestionIndexByIngredientId(
                                          (prev) => ({
                                            ...prev,
                                            [ing.id]: -1,
                                          }),
                                        );
                                      }}
                                      onKeyDown={(e) =>
                                        handleIngredientNameKeyDown(
                                          e,
                                          ing.id,
                                          filteredSuggestions,
                                        )
                                      }
                                    />
                                    {focusedIngredientId === ing.id &&
                                      ing.name && (
                                        <div className="absolute top-full left-0 w-[420px] max-w-[90vw] mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden">
                                          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                                            {filteredSuggestions.map(
                                              (item, idx) => (
                                                <button
                                                  key={item.sku}
                                                  type="button"
                                                  onMouseEnter={() =>
                                                    setActiveSuggestionIndexByIngredientId(
                                                      (prev) => ({
                                                        ...prev,
                                                        [ing.id]: idx,
                                                      }),
                                                    )
                                                  }
                                                  onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    applyIngredientSuggestion(
                                                      ing.id,
                                                      item,
                                                    );
                                                    clearIngredientNameDraft(
                                                      ing.id,
                                                    );
                                                    clearIngredientNameTimer(
                                                      ing.id,
                                                    );

                                                    setFocusedIngredientId(null);
                                                  }}
                                                  className={`
                            w-full text-left px-3 py-2 flex items-center justify-between gap-2.5 text-xs transition-colors
                            ${activeSuggestionIndex === idx
                                                      ? "bg-blue-50 text-blue-700"
                                                      : idx % 2 === 0
                                                        ? "bg-gray-50/70"
                                                        : "bg-white"
                                                    }
                            hover:bg-blue-50 hover:text-blue-700
                          `}
                                                >
                                                  <div className="min-w-0 flex-1 flex items-center gap-2">
                                                    <span className="font-medium text-gray-900 truncate text-xs" title={item.name}>
                                                      {item.name}
                                                    </span>
                                                    {item.unitCost && canSeeDetailedCosts && (
                                                      <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                                        (${Number(item.unitCost).toFixed(2)})
                                                      </span>
                                                    )}
                                                  </div>
                                                  <span className="font-mono text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200 whitespace-nowrap shrink-0">
                                                    {item.sku}
                                                  </span>
                                                </button>
                                              ),
                                            )}

                                            {filteredSuggestions.length === 0 && (
                                              <div className="px-4 py-6 text-center text-gray-500">
                                                <div className="text-sm font-medium">
                                                  No ingredients found
                                                </div>
                                                <div className="text-xs text-gray-400 mt-0.5">
                                                  Try a different search term
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          {filteredSuggestions.length > 0 && (
                                            <div className="border-t border-gray-100 px-3 py-1.5 bg-gray-50 flex items-center justify-between text-[11px] text-gray-500">
                                              <span>{filteredSuggestions.length} matches</span>
                                              <span>Use ↑↓ and Enter</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                  </div>

                                <input
                                  placeholder="Paste or type SKU"
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono"
                                  value={ing.sku}
                                  onChange={(e) =>
                                    updateIngredient(
                                      ing.id,
                                      "sku",
                                      e.target.value,
                                    )
                                  }
                                  onFocus={() => setFocusedIngredientId(ing.id)}
                                  onPaste={(e) => handlePaste(e, index, "sku")}
                                />

                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  placeholder="Weight"
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                                  value={ing.weight}
                                  onChange={(e) =>
                                    updateIngredient(
                                      ing.id,
                                      "weight",
                                      e.target.value,
                                    )
                                  }
                                  onInput={(e) =>
                                    updateIngredient(
                                      ing.id,
                                      "weight",
                                      e.currentTarget.value,
                                    )
                                  }
                                  onPaste={(e) =>
                                    handlePaste(e, index, "weight")
                                  }
                                />

                                <select
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                                  value={ing.unit}
                                  onChange={(e) =>
                                    updateIngredient(
                                      ing.id,
                                      "unit",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="kg">kg</option>
                                  <option value="un.">un.</option>
                                  <option value="g">g</option>
                                  <option value="ml">ml</option>
                                  <option value="l">l</option>
                                </select>

                                <input
                                  type="number"
                                  step={ratioRoundingStep}
                                  min="0"
                                  placeholder="Ratio"
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                                  value={
                                    ingredientRatioDrafts[ing.id] ??
                                    formatRatioDisplay(ing.ratio)
                                  }
                                  onChange={(e) =>
                                    handleIngredientRatioChange(
                                      ing.id,
                                      e.target.value,
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  onBlur={(e) =>
                                    commitIngredientRatioUpdate(
                                      ing.id,
                                      e.target.value,
                                    )
                                  }
                                  onPaste={(e) =>
                                    handlePaste(e, index, "ratio")
                                  }
                                />

                                <input
                                  placeholder="Master case weight"
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 font-mono"
                                  value={
                                    getRatioWeightKg(ing) > 0
                                      ? getRatioWeightKg(ing).toFixed(4)
                                      : ""
                                  }
                                  readOnly
                                />

                                <input
                                  type="text"
                                  placeholder={canSeeDetailedCosts ? "Raw Cost " : "Confidential"}
                                  className={`w-full px-4 py-3 rounded-lg font-mono bg-gray-50 border-2
                ${hasZeroCostAfterSelect(ing)
                                      ? "border-red-500 bg-red-50 text-red-700"
                                      : "border-gray-300"
                                    }`}
                                  value={canSeeDetailedCosts ? ing.unitCost : "••••••"}
                                  readOnly
                                />

                                <input
                                  type="text"
                                  placeholder={canSeeDetailedCosts ? "Total Cost" : "Confidential"}
                                  className={`w-full px-4 py-3 rounded-lg font-mono bg-gray-100 border-2
                ${hasZeroCostAfterSelect(ing)
                                      ? "border-red-500 bg-red-50 text-red-700"
                                      : "border-gray-300"
                                    }`}
                                  value={canSeeDetailedCosts ? ing.totalCost.toFixed(4) : "••••••"}
                                  readOnly
                                />

                                <div className="text-center">
                                  <button
                                    onClick={() => removeIngredient(ing.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    disabled={ingredients.length === 1}
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Totals */}
                    {!hasBaseProduct && (
                      <div className="mt-8 pt-6 border-t border-gray-300">
                        {/* Mobile Totals */}
                        <div className="grid grid-cols-2 gap-4 md:hidden text-center">
                          <div>
                            <div className="text-sm text-gray-600">
                              Total Weight
                            </div>
                            <div className="text-xl font-bold text-blue-600">
                              {Number(totalWeight || 0).toFixed(4)}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">
                              Ratio Total
                            </div>
                            <div
                              className={`text-xl font-bold ${isRatioTotalValid
                                ? "text-green-600"
                                : "text-red-600"
                                }`}
                            >
                              {ratioTotalDisplay}%
                            </div>
                          </div>
                        </div>

                        {/* Desktop Totals */}
                        <div
                          className="hidden md:grid gap-4 items-center mt-2"
                          style={{
                            gridTemplateColumns:
                              "2fr 2fr 1fr 1fr 1fr 1fr 1fr 1.4fr 1fr",
                          }}
                        >
                          <div />
                          <div />
                          <div>
                            <input
                              readOnly
                              value={Number(totalWeight || 0).toFixed(4)}
                              className="w-full px-3 py-2 text-sm rounded-md text-center font-semibold border border-blue-500 text-blue-600 bg-blue-50"
                            />
                          </div>
                          <div />
                          <div>
                            <input
                              readOnly
                              value={ratioTotalDisplay + "%"}
                              className={`w-full px-3 py-2 text-sm rounded-md text-center font-semibold border
              ${isRatioTotalValid
                                  ? "border-green-500 text-green-600 bg-green-50"
                                  : "border-red-500 text-red-600 bg-red-50"
                                }`}
                            />
                          </div>
                          <div />
                          <div />
                          <div />
                          <div />
                        </div>
                      </div>
                    )}
                      </div>
                    </div>
                  </>

                  {/* Master Case & Packaging */}

                  {!product.isVariant && (
                    <>
                      {/* Master Case & Packaging trigger */}
                      <div className="hidden md:flex items-end justify-between gap-4 mt-8">
                        <div></div>
                        <button
                          onClick={handleOpenPackagingModal}
                          className="flex items-center justify-center gap-2 px-5 py-3 bg-black text-white rounded-lg hover:bg-gray-800 h-[52px]"
                        >
                          + Add Packaging
                        </button>
                      </div>

                      {/* Mobile Packaging Button */}
                      <div className="md:hidden mt-6">
                        <button
                          onClick={handleOpenPackagingModal}
                          className="w-full flex items-center justify-center gap-2 px-5 py-4 bg-black text-white rounded-lg hover:bg-gray-800"
                        >
                          + Add Packaging
                        </button>
                      </div>
                    </>
                  )}

                  {/* Packaging Modal */}
                  {showPackagingModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                          <h2 className="text-xl font-semibold text-gray-900">
                            Select Packaging Size
                          </h2>
                        </div>

                        {/* Search and Filter */}
                        <div className="px-6 pt-5 pb-4 border-b bg-gray-50">
                          <input
                            type="text"
                            value={packagingSearchTerm}
                            onChange={(e) =>
                              setPackagingSearchTerm(e.target.value)
                            }
                            placeholder="Search packaging..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all"
                          />
                        </div>

                        {/* Error Message */}
                        {!!packagingVariantsError && (
                          <div className="mx-6 mt-4 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
                            <div className="flex items-center justify-between gap-3">
                              <span>{packagingVariantsError}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  fetchPackagingVariants({
                                    force: true,
                                    showLoader: true,
                                  })
                                }
                                className="shrink-0 rounded-xl border border-amber-300 px-4 py-1.5 text-xs font-medium hover:bg-amber-100 transition"
                              >
                                Retry
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Packaging List - Scrollable Area */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-3">
                          {loadingPackaging ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black mb-4"></div>
                              <p className="text-gray-600 font-medium">
                                Loading packaging variants...
                              </p>
                            </div>
                          ) : (
                            <>
                              {filteredPackagingVariants.map((variant) => (
                                <label
                                  key={variant._id}
                                  className={`group flex items-center gap-4 p-5 border-2 rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-sm ${selectedVariant?._id === variant._id
                                    ? "border-black bg-gray-50 shadow-sm"
                                    : "border-gray-200 hover:border-gray-300"
                                    }`}
                                >
                                  <input
                                    type="radio"
                                    name="packaging"
                                    checked={
                                      selectedVariant?._id === variant._id
                                    }
                                    onChange={() => setSelectedVariant(variant)}
                                    className="w-5 h-5 accent-black mt-0.5 cursor-pointer"
                                  />
                                  <div className="flex-1">
                                    <p className="font-semibold text-gray-900 text-[15px]">
                                      {variant.name}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {variant.format} • {variant.size}
                                    </p>
                                  </div>
                                </label>
                              ))}

                              {!filteredPackagingVariants.length && (
                                <div className="rounded-2xl border border-dashed border-gray-300 py-16 text-center">
                                  <p className="text-gray-500">
                                    No packaging variants match your search or
                                    filter.
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="border-t bg-white p-6 space-y-4">

                          {/* Action Buttons */}
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                setShowPackagingModal(false);
                                setSelectedVariant(null);
                                setPackagingModalForFormatIndex(null);
                              }}
                              className="flex-1 py-3.5 border border-gray-300 rounded-2xl text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-all"
                            >
                              Cancel
                            </button>

                            <button
                              disabled={!selectedVariant}
                              onClick={() => {
                                if (!selectedVariant) return;

                                logPackagingCostComparison(
                                  "selected packaging cost comparison",
                                  selectedVariant,
                                );

                                const totalPkgCost =
                                  selectedVariant.items.reduce(
                                    (sum, i) =>
                                      sum +
                                      Number(i.unitCost || 0) *
                                      Number(i.qty || 1),
                                    0,
                                  );

                                if (packagingModalForFormatIndex !== null) {
                                  const updated = [...bulkFormats];
                                  updated[packagingModalForFormatIndex] = {
                                    ...updated[packagingModalForFormatIndex],
                                    selectedPackaging: selectedVariant,
                                    formatPackagingCost:
                                      totalPkgCost.toFixed(4),
                                  };
                                  setBulkFormats(updated);
                                } else {
                                  setBulkFormats((prev) => [
                                    ...prev,
                                    {
                                      netWeightKg: selectedVariant.size
                                        ? parseFloat(selectedVariant.size)
                                        : parseFloat(
                                          selectedVariant.weightKg || 0,
                                        ),
                                      selectedPackaging: selectedVariant,
                                      formatPackagingCost:
                                        totalPkgCost.toFixed(4),
                                    },
                                  ]);

                                  setAddedPackaging(selectedVariant);
                                  setPackagingCost(totalPkgCost.toFixed(4));
                                }

                                setProduct((prev) => ({
                                  ...prev,
                                  lengthMm:
                                    selectedVariant?.lengthMm ??
                                    prev.lengthMm ??
                                    "",
                                  widthMm:
                                    selectedVariant?.widthMm ??
                                    prev.widthMm ??
                                    "",
                                  heightMm:
                                    selectedVariant?.heightMm ??
                                    prev.heightMm ??
                                    "",
                                  lengthInch:
                                    selectedVariant?.lengthIn ??
                                    prev.lengthInch ??
                                    "",
                                  widthInch:
                                    selectedVariant?.widthIn ??
                                    prev.widthInch ??
                                    "",
                                  heightInch:
                                    selectedVariant?.heightIn ??
                                    prev.heightInch ??
                                    "",
                                  ti:
                                    selectedVariant?.ti ??
                                    prev.ti ??
                                    "",
                                  hi:
                                    selectedVariant?.hi ??
                                    prev.hi ??
                                    "",
                                }));

                                setShowPackagingModal(false);
                                setSelectedVariant(null);
                                setPackagingModalForFormatIndex(null);
                              }}
                              className="flex-1 py-3.5 bg-black hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-2xl transition-all"
                            >
                              {packagingModalForFormatIndex !== null
                                ? "Apply to Format"
                                : "Add Packaging"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Save Before Variant Modal */}
                  <SaveBeforeVariantModal
                    isOpen={showSaveBeforeVariantModal}
                    onClose={() => setShowSaveBeforeVariantModal(false)}
                    onSaveAndContinue={async () => {
                      setShowSaveBeforeVariantModal(false);
                      const { success } = await addToDatabase();
                      if (success) {
                        setHasGeneratedButUnsavedBase(false);
                        toast.success(
                          "Base product saved. Now switching to variant mode.",
                        );
                        setProduct((prev) => ({
                          ...prev,
                          isVariant: true,
                          baseProductSku: "",
                        }));
                      } else {
                        toast.error("Save failed — stayed in base mode.");
                      }
                    }}
                    onDiscardAndSwitch={() => {
                      setShowSaveBeforeVariantModal(false);
                      setHasGeneratedButUnsavedBase(false);
                      setProduct((prev) => ({
                        ...prev,
                        isVariant: true,
                        baseProductSku: "",
                      }));
                      toast.success("Changes discarded.");
                    }}
                  />

                  {/* Added Packaging Display */}
                  {!product.isVariant && addedPackaging && (
                    <div className="mt-8 bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {addedPackaging.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {addedPackaging.format} • {addedPackaging.size}
                          </p>
                        </div>
                        <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full">
                          Packaging
                        </span>
                      </div>

                      {/* Desktop Column Headings */}
                      <div
                        className="hidden md:grid gap-4 mb-1 px-1 text-sm font-medium text-gray-600"
                        style={{ gridTemplateColumns: "2fr 2fr 1fr 1fr 2fr" }}
                      >
                        <div>Packaging Item</div>
                        <div>SKU</div>
                        <div>Qty</div>
                        <div>Unit</div>
                        <div>Cost ($)</div>
                      </div>

                      <div className="space-y-6">
                        {addedPackaging.items.map((item, index) => (
                          <div
                            key={item._id}
                            className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 md:bg-transparent md:border-0"
                          >
                            {/* Mobile Card Layout */}
                            <div className="md:hidden p-5 space-y-4">
                              <div className="text-sm font-semibold text-gray-700">
                                Item #{index + 1}
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Packaging Item
                                  </label>
                                  <input
                                    value={item.name}
                                    readOnly
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-sm"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    SKU
                                  </label>
                                  <input
                                    value={item.sku}
                                    readOnly
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm bg-white"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Quantity
                                  </label>
                                  <input
                                    value={item.qty}
                                    readOnly
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-sm bg-white"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Unit
                                  </label>
                                  <input
                                    value="un."
                                    readOnly
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-sm bg-white"
                                  />
                                </div>

                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Cost ($)
                                  </label>
                                  <input
                                    value={canSeeDetailedCosts ? (
                                      item.unitCost * Number(item.qty)
                                    ).toFixed(4) : "••••••"}
                                    readOnly
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm bg-gray-100"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Desktop Grid Row (Unchanged) */}
                            <div
                              className="hidden md:grid gap-4 items-center"
                              style={{
                                gridTemplateColumns: "2fr 2fr 1fr 1fr 2fr",
                              }}
                            >
                              <input
                                value={item.name}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                              />
                              <input
                                value={item.sku}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono bg-gray-50"
                              />
                              <input
                                value={item.qty}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center bg-gray-50"
                              />
                              <input
                                value="un."
                                readOnly
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center bg-gray-50"
                              />
                              <input
                                value={canSeeDetailedCosts ? (
                                  item.unitCost * Number(item.qty)
                                ).toFixed(4) : "••••••"}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono bg-gray-100"
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Totals */}
                      {/* Desktop Total Row */}
                      <div
                        className="hidden md:grid gap-4 items-center mt-6"
                        style={{ gridTemplateColumns: "2fr 2fr 1fr 1fr 2fr" }}
                      >
                        <div />
                        <div />
                        <div />
                        <div className="text-center text-sm font-semibold">
                          Total
                        </div>
                        <input
                          readOnly
                          value={canSeeDetailedCosts ? `$${addedPackaging.items
                            .reduce(
                              (sum, i) =>
                                sum + Number(i.unitCost) * Number(i.qty),
                              0,
                            )
                            .toFixed(4)}` : "••••••"}
                          className="w-full px-4 py-3 text-sm rounded-lg text-left font-bold border-2 border-blue-500 text-blue-600 bg-blue-50"
                        />
                      </div>

                      {/* Mobile Total */}
                      <div className="md:hidden mt-6 pt-6 border-t-2 border-gray-300">
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-sm text-gray-600">
                              Total Packaging Cost
                            </p>
                          </div>
                          <p className="text-2xl font-bold text-blue-600">
                            {canSeeDetailedCosts ? `$${addedPackaging.items
                              .reduce(
                                (sum, i) =>
                                  sum + Number(i.unitCost) * Number(i.qty),
                                0,
                              )
                              .toFixed(4)}` : "••••••"}
                          </p>
                        </div>
                      </div>

                      {/* Remove Button */}
                      <div className="flex justify-end items-end mt-8">
                        <button
                          onClick={() => {
                            setAddedPackaging(null);
                            setPackagingCost("");

                            setProduct((prev) => ({
                              ...prev,
                              lengthMm: "",
                              widthMm: "",
                              heightMm: "",
                              lengthInch: "",
                              widthInch: "",
                              heightInch: "",
                            }));
                          }}
                          className="w-full md:w-auto px-6 py-3 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 font-medium transition"
                        >
                          Remove Packaging
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!product.isVariant && (
                <>
                  <ItemCategorySection
                    product={product}
                    setProduct={setProduct}
                  />
                  <AdditionalInfoSection
                    product={product}
                    setProduct={setProduct}
                  />
                  <CustomFieldsSection
                    product={product}
                    setProduct={setProduct}
                    mandatoryFields={mandatoryFields}
                    setMandatoryFields={setMandatoryFields}
                    msrpCadPerKg={msrpCadPerKg}
                    msrpUsdPerKg={msrpUsdPerKg}
                  />
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={submitRecipeSku}
                      disabled={
                        !product.nameEn.trim() ||
                        (!product.baseProductSku && ingredients.length === 0)
                      }
                      className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                        product.isEdit
                          ? "bg-amber-600 text-white hover:bg-amber-700 shadow-sm"
                          : "bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      }`}
                    >
                      {product.isEdit ? (
                        <>
                          <Check className="w-4 h-4" />
                          Refresh Descriptions (SKU & UPC Locked)
                        </>
                      ) : (
                        "Generate SKU"
                      )}
                    </button>
                  </div>

                  <BarcodeSecondUomSection
                    rows={barcodeSecondUomRows}
                    setRows={setBarcodeSecondUomRows}
                    isVariant={product.isVariant}
                    lockGenerated={Boolean(product.skuGenerated)}
                  />
                  <SellingPriceSection
                    product={product}
                    setProduct={setProduct}
                    wholesale3Cad={wholesale3CadRounded}
                    masterCaseWeightKg={masterCaseWeightKg}
                    totalWeight={totalWeight}
                  />
                </>
              )}

              {/* Cost Summary */}
              {/* {!product.isVariant && (
                <div className="bg-gradient-to-r from-black to-gray-800 text-white rounded-2xl p-8">
                  <h2 className="text-2xl font-semibold mb-8">Cost Summary</h2>

                  <div className="space-y-10">
                    <div className="space-y-6">
                      <div
                        className={`grid grid-cols-1 ${isBaseIngredient ? "md:grid-cols-2" : "md:grid-cols-3"
                          } gap-8`}
                      >
                        {!isBaseIngredient && (
                          <div>
                            <p className="text-gray-300 text-sm">Final COGS / CS CAD</p>
                            <p className="text-2xl font-bold">
                              ${Number(finalCogsPerCsCad || 0).toFixed(3)}
                            </p>
                            <p className="text-sm text-gray-300 mt-2">
                              Throughput: ${throughputCostPerUnit.toFixed(3)}
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="text-gray-300 text-sm">Cost per kg</p>
                          <p className="text-2xl font-bold">${summaryCostPerKg}</p>
                        </div>

                        <div>
                          <p className="text-gray-300 text-sm">Cost per lbs</p>
                          <p className="text-2xl font-bold">${summaryCostPerLbs}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t-2 sm:border-t-4 border-gray-500 pt-6 sm:pt-8">
                      <div className="overflow-x-auto rounded-xl border border-gray-600 -mx-1 sm:mx-0">
                        <table className="min-w-full text-xs sm:text-sm whitespace-nowrap">
                          <thead className="bg-white/10 text-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold">
                                Metric
                              </th>
                              <th className="px-4 py-3 text-right font-semibold">
                                Value
                              </th>
                              <th className="px-4 py-3 text-right font-semibold">
                                Margin Percentage
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {!isBaseIngredient && (
                              <tr className="border-t border-gray-700">
                                <td className="px-4 py-3 text-gray-200">
                                  Web Retail CAD
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  ${Number(standardSrpCad || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(webRetailMarginPercent)}
                                </td>
                              </tr>
                            )}
                            {!isBaseIngredient && (
                              <tr className="border-t border-gray-700">
                                <td className="px-4 py-3 text-gray-200">
                                  Wholesale 1 CAD
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  ${Number(wholesale1Cad || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(wholesale1MarginPercent)}
                                </td>
                              </tr>
                            )}
                            {!isBaseIngredient && (
                              <tr className="border-t border-gray-700">
                                <td className="px-4 py-3 text-gray-200">
                                  Wholesale 2 CAD
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  ${Number(wholesale2Cad || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(wholesale2MarginPercent)}
                                </td>
                              </tr>
                            )}
                            <tr className="border-t border-gray-700">
                              <td className="px-4 py-3 text-gray-200">
                                Wholesale 3 CAD
                              </td>
                              <td className="px-4 py-3 text-right font-semibold">
                                ${Number(wholesale3CadRounded || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold">
                                {formatMarginPercent(wholesale3MarginPercent)}
                              </td>
                            </tr>
                           
                             <tr className="border-t border-gray-700">
                              <td className="px-4 py-3 text-gray-200">
                                Standard SRP
                              </td>
                              <td className="px-4 py-3 text-right font-semibold">
                                ${Number(standardSrpCad || 0).toFixed(2)}
                              </td>
                             
                              <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(standardMarginPercent)}
</td>
                            </tr>
                            {!isBaseIngredient && (
                              <tr className="border-t border-gray-700">
                                <td className="px-4 py-3 text-gray-200">
                                  Distributor CAD
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  ${Number(distributorCad || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(distributorMarginPercent)}
                                </td>
                              </tr>
                            )}
                            {!isBaseIngredient && (
                              <tr className="border-t border-gray-700">
                                <td className="px-4 py-3 text-gray-200">
                                  Export USD
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  ${Number(exportUsd || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(exportUsdMarginPercent)}
                                </td>
                              </tr>
                            )}
                            {!isBaseIngredient && (
                              <tr className="border-t border-gray-700">
                                <td className="px-4 py-3 text-gray-200">
                                  Export MENA
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  ${Number(exportMena || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">
                                  {formatMarginPercent(exportMenaMarginPercent)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )} */}

              {!product.isVariant && (
                <div className="bg-gradient-to-r from-black to-gray-800 text-white rounded-2xl p-4 sm:p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
                    <h2 className="text-xl sm:text-2xl font-semibold">Cost Summary</h2>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-white/10 px-3 sm:px-4 py-2 rounded-lg border border-white/20">
                        <span className="text-xs sm:text-sm text-gray-300">Export Currencies:</span>
                        <div className="flex items-center gap-3">
                          {availableCurrencies.filter(curr => curr !== "CAD").map((curr) => (
                            <label key={`exp-${curr}`} className="flex items-center gap-1.5 cursor-pointer">
                              <div
                                className={`w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all ${exportCurrencies.includes(curr)
                                  ? "border-[2px] border-indigo-500"
                                  : "border border-gray-400 bg-white"
                                  }`}
                              >
                                {exportCurrencies.includes(curr) && (
                                  <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full"></div>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                value={curr}
                                checked={exportCurrencies.includes(curr)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setExportCurrencies((prev) => [...prev, curr]);
                                  } else {
                                    setExportCurrencies((prev) => prev.filter((c) => c !== curr));
                                  }
                                }}
                                className="hidden"
                              />
                              <span className="text-xs sm:text-sm font-semibold text-white">{curr}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg border border-white/20">
                        <span className="text-xs sm:text-sm text-gray-300">Display:</span>
                        <select
                          value={selectedCurrency}
                          onChange={(e) => setSelectedCurrency(e.target.value)}
                          className="bg-transparent text-white border-0 text-xs sm:text-sm font-semibold focus:outline-none focus:ring-0 cursor-pointer"
                        >
                          {availableCurrencies.map((curr) => (
                            <option key={curr} value={curr} className="bg-gray-800 text-white">
                              {curr}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6 sm:space-y-10">
                    <div className="space-y-4 sm:space-y-6">
                      <div
                        className={`grid grid-cols-1 ${isBaseIngredient ? "md:grid-cols-2" : "sm:grid-cols-2 md:grid-cols-3"
                          } gap-4 sm:gap-8`}
                      >
                        {!isBaseIngredient && (
                          <div>
                            <p className="text-gray-300 text-sm">
                              {canSeeDetailedCosts ? `Final COGS / CS ${selectedCurrency}` : `Confidential Cost / CS ${selectedCurrency}`}
                            </p>
                            <p className="text-2xl font-bold">
                              {getCurrencySymbol(selectedCurrency)}
                              {convertCurrencyDisplay(displayFinalCogsPerCsCad || 0, "CAD").toFixed(3)}
                            </p>
                            <p className="text-sm text-gray-300 mt-2">
                              Throughput: {getCurrencySymbol(selectedCurrency)}
                              {convertCurrencyDisplay(throughputCostPerUnit || 0, "CAD").toFixed(3)}
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="text-gray-300 text-sm">Cost per kg ({selectedCurrency})</p>
                          <p className="text-2xl font-bold">
                            {getCurrencySymbol(selectedCurrency)}
                            {convertCurrencyDisplay(summaryCostPerKg || 0, "CAD").toFixed(3)}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-300 text-sm">Cost per lbs ({selectedCurrency})</p>
                          <p className="text-2xl font-bold">
                            {getCurrencySymbol(selectedCurrency)}
                            {convertCurrencyDisplay(summaryCostPerLbs || 0, "CAD").toFixed(3)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t-4 border-gray-500 pt-6 sm:pt-8 min-w-0 w-full max-w-full">
                      <div className="overflow-x-auto rounded-xl border border-gray-600 -mx-1 sm:mx-0 w-full max-w-full">
                        <table className="min-w-full text-xs sm:text-sm whitespace-nowrap">
                          <thead className="bg-white/10 text-gray-100">
                            <tr className="">
                              <th className="px-4 py-3 text-left font-semibold">Metric</th>
                              <th className="px-4 py-3 text-right font-semibold">
                                Master Price ({selectedCurrency})
                              </th>
                              <th className="px-4 py-3 text-right font-semibold">Price/KG ({selectedCurrency})</th>
                              <th className="px-4 py-3 text-right font-semibold">Price/Lbs ({selectedCurrency})</th>
                              <th className="px-4 py-3 text-right font-semibold">
                                Margin Percentage
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {!isBaseIngredient && renderCostSummaryRow("Web Retail CAD", standardSrpCad, "CAD", webRetailMarginPercent)}
                            {!isBaseIngredient && renderCostSummaryRow("Wholesale 1 CAD", wholesale1Cad, "CAD", wholesale1MarginPercent)}
                            {!isBaseIngredient && renderCostSummaryRow("Wholesale 2 CAD", wholesale2Cad, "CAD", wholesale2MarginPercent)}
                            {renderCostSummaryRow("Wholesale 3 CAD", wholesale3CadRounded, "CAD", wholesale3MarginPercent)}
                            {renderCostSummaryRow("Standard SRP", standardSrpCad, "CAD", standardMarginPercent)}
                            {!isBaseIngredient && renderCostSummaryRow("Distributor CAD", distributorCad, "CAD", distributorMarginPercent)}
                            {!isBaseIngredient && renderCostSummaryRow("Export USD", exportUsd, "USD", exportUsdMarginPercent)}
                            {!isBaseIngredient && renderCostSummaryRow("Export MENA", exportMena, "CAD", exportMenaMarginPercent)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Final Actions */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap justify-end items-stretch sm:items-center gap-2.5 sm:gap-3 mt-8 sm:mt-10 mb-6">
                <div className="text-xs sm:text-sm text-gray-600 sm:mr-auto text-center sm:text-left mb-2 sm:mb-0">
                  Saved for batch export:{" "}
                  <span className="font-semibold">{savedExportBatch.length}</span>
                </div>
                {product.isEdit && (
                  <button
                    type="button"
                    onClick={async () => {
                      const res = await addToDatabase();
                      if (res?.success) {
                        toast.success(
                          `Recipe ${product.sku} updated successfully across all databases!`,
                          { duration: 6000 },
                        );
                        baselineRef.current = buildUnsavedSignature({
                          product,
                          ingredients,
                          bulkFormats,
                          addedPackaging,
                          barcodeSecondUomRows,
                        });
                        setHasUnsavedChanges(false);
                      }
                    }}
                    disabled={
                      product.isVariant
                        ? !Array.isArray(product.variantRowsForSave) ||
                          !product.variantRowsForSave.some((row) => row?.sku)
                        : ingredients.length === 0 || !product.nameEn.trim()
                    }
                    className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-emerald-600 hover:bg-emerald-700 
                 text-white rounded-lg font-bold text-sm sm:text-base transition disabled:opacity-50 
                 disabled:cursor-not-allowed shadow-md w-full sm:w-auto"
                  >
                    <Check size={18} />
                    Update Recipe in Database
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSyncToErpag}
                  disabled={
                    isSyncingErpag ||
                    (product.isVariant
                      ? !Array.isArray(product.variantRowsForSave) ||
                        !product.variantRowsForSave.some((row) => row?.sku)
                      : ingredients.length === 0 || !product.nameEn.trim())
                  }
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#1e40af] hover:bg-[#1d4ed8] text-white rounded-lg font-bold text-sm sm:text-base transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg cursor-pointer w-full sm:w-auto"
                  title="Push product data directly to ERPAG"
                >
                  {isSyncingErpag ? (
                    <>
                      <RefreshCw size={18} className="animate-spin text-white" />
                      Syncing to ERPAG...
                    </>
                  ) : (
                    <>
                      <Zap size={18} className="text-yellow-400 fill-yellow-400" />
                      Sync to ERPAG
                    </>
                  )}
                </button>
                <button
                  onClick={handleSaveToBatch}
                  disabled={
                    product.isVariant
                      ? !Array.isArray(product.variantRowsForSave) ||
                      !product.variantRowsForSave.some((row) => row?.sku)
                      : ingredients.length === 0 || !product.nameEn.trim()
                  }
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white border border-gray-300 hover:bg-gray-100 
               text-gray-900 rounded-lg font-medium text-sm sm:text-base transition disabled:opacity-50 
               disabled:cursor-not-allowed shadow-sm w-full sm:w-auto"
                >
                  Save to Batch
                </button>
                <button
                  onClick={handleExportSavedBatch}
                  disabled={savedExportBatch.length === 0}
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#365daf] hover:bg-blue-800 
               text-white rounded-lg font-medium text-sm sm:text-base transition disabled:opacity-50 
               disabled:cursor-not-allowed shadow-md w-full sm:w-auto"
                >
                  <Download size={18} />
                  Export Saved Batch (3 files)
                </button>
                <button
                  onClick={handleClearSavedBatch}
                  disabled={savedExportBatch.length === 0}
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 hover:bg-gray-300 
               text-gray-900 rounded-lg font-medium text-sm sm:text-base transition disabled:opacity-50 
               disabled:cursor-not-allowed shadow-sm w-full sm:w-auto"
                >
                  Clear Batch
                </button>
                <button
                  onClick={() => handleSaveAndExport()}
                  disabled={
                    product.isVariant
                      ? !Array.isArray(product.variantRowsForSave) ||
                      !product.variantRowsForSave.some((row) => row?.sku)
                      : ingredients.length === 0 || !product.nameEn.trim()
                  }
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-black hover:bg-gray-800 
               text-white rounded-lg font-medium text-sm sm:text-base transition disabled:opacity-50 
               disabled:cursor-not-allowed shadow-md w-full sm:w-auto"
                >
                  <Download size={18} />
                  Save & Export (Excel)
                </button>
              </div>
              <div className="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                <p className="font-semibold mb-2">How it works:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Create your first SKU and click <strong>“Save to Batch.”</strong></li>
                  <li>Repeat the process for each additional SKU.</li>
                  <li>Once all SKUs are added, click <strong>“Export Saved Batch”</strong> to download all files at once.</li>
                </ol>
              </div>
            </fieldset>
          </div>
        )}

        {/* TAB: Retail Formats */}
        {activeTab === "formats" && <FormatBuilder />}

        {/* TAB: Private Label */}
        {activeTab === "private" && <PrivateLabel />}

        {/* TAB: 90-Days Recipe History & Remodify */}
        {activeTab === "history" && canSeeRecipeHistory && (
          <RecipeHistoryTab
            onSelectRecipe={(recipe) => {
              handleLoadEditRecipe(recipe.sku, recipe);
            }}
          />
        )}
      </div>

      {showUnsavedPromptModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Unsaved Changes
            </h3>

            <p className="text-sm text-gray-700 leading-6">
              You have unsaved changes. You can save your progress or leave
              without saving and lose your latest edits. Note: Draft changes are
              saved for up to 3 hours.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
              {/* Cancel Button */}
              <button
                type="button"
                disabled={isSavingFromUnsavedPrompt}
                onClick={() => {
                  pendingNavigationRef.current = null;
                  pendingInternalActionRef.current = null;
                  setShowUnsavedPromptModal(false);
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              {/* Leave Without Saving */}
              <button
                type="button"
                disabled={isSavingFromUnsavedPrompt}
                onClick={() => {
                  const to = pendingNavigationRef.current;
                  const internalAction = pendingInternalActionRef.current;
                  pendingNavigationRef.current = null;
                  pendingInternalActionRef.current = null;
                  setShowUnsavedPromptModal(false);

                  markCurrentAsSaved();
                  void clearSavedDraft();

                  if (to) {
                    navigate(to);
                    return;
                  }
                  if (internalAction) {
                    resetEntireForm({ showToast: false });
                    internalAction();
                  }
                }}
                className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Leave Without Saving
              </button>

              <button
                type="button"
                disabled={isSavingFromUnsavedPrompt}
                onClick={async () => {
                  setIsSavingFromUnsavedPrompt(true);
                  const to = pendingNavigationRef.current;
                  const internalAction = pendingInternalActionRef.current;
                  try {
                    const snapshot = buildCurrentSnapshot();
                    const result = await saveDraftToServer(snapshot);
                    if (!result?.success) {
                      setIsSavingFromUnsavedPrompt(false);
                      return;
                    }

                    markCurrentAsSaved();
                    pendingNavigationRef.current = null;
                    pendingInternalActionRef.current = null;
                    setShowUnsavedPromptModal(false);
                    setIsSavingFromUnsavedPrompt(false);
                    toast.success("Draft saved for 3 hours.");

                    if (to) {
                      navigate(to);
                      return;
                    }
                    if (internalAction) {
                      internalAction();
                    }
                  } catch (err) {
                    toast.error(
                      err?.message || "Failed to save draft. Please try again.",
                    );
                    setIsSavingFromUnsavedPrompt(false);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {isSavingFromUnsavedPrompt ? "Saving..." : "Save & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDraftResumeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Pre-Saved Work Found
            </h3>

            <p className="text-sm text-gray-700 leading-6">
              We found pre-saved work from earlier. Do you want to continue from
              where you left off, or start new?
            </p>

            <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                disabled={isApplyingDraftChoice}
                onClick={async () => {
                  setIsApplyingDraftChoice(true);
                  try {
                    if (pendingDraftSnapshot) {
                      applyDraftSnapshot(pendingDraftSnapshot);
                      toast.success("Draft restored successfully.");
                    }
                    setShowDraftResumeModal(false);
                    setPendingDraftSnapshot(null);
                  } finally {
                    setIsApplyingDraftChoice(false);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
              >
                Continue
              </button>

              <button
                type="button"
                disabled={isApplyingDraftChoice}
                onClick={async () => {
                  setIsApplyingDraftChoice(true);
                  try {
                    await clearSavedDraft();
                    setShowDraftResumeModal(false);
                    setPendingDraftSnapshot(null);
                    resetEntireForm({ showToast: false });
                    toast.success("Starting fresh. Saved draft was deleted.");
                  } finally {
                    setIsApplyingDraftChoice(false);
                  }
                }}
                className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Start New
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BaseProductRecipeRow({ product }) {
  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden bg-blue-50 mt-4">
      {/* Mobile Card */}
      <div className="md:hidden p-4 space-y-2 bg-white text-sm">
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Seq: 01</span>
          <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{product.baseProductSku}</span>
        </div>
        <div className="font-semibold text-gray-900">{product.baseDescriptionEn || "BASE PRODUCT"}</div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span>Qty: <strong>1</strong></span>
          <span>UOM: <strong>{product.uom || "kg"}</strong></span>
        </div>
      </div>
      {/* Desktop Grid Row */}
      <div
        className="hidden md:grid gap-4 px-4 py-4 items-center bg-white"
        style={{
          gridTemplateColumns: "0.5fr 3fr 2fr 1fr 1fr 1fr 1fr",
        }}
      >
        <div className="font-mono text-sm">01</div>
        <div className="font-semibold">
          {product.baseDescriptionEn || "BASE PRODUCT"}
        </div>
        <div className="font-mono text-sm">{product.baseProductSku}</div>
        <div>1</div>
        <div>{product.uom || "kg"}</div>
        <div>—</div>
        <div>—</div>
      </div>
    </div>
  );
}


